import { trapFocus, removeTrapFocus } from '@theme/focus';
import * as petPerf from '@theme/osf-pet-perf';
import * as petEvents from '@theme/osf-pet-events';

// Pet portrait widget: sign-in token, photo resize and crop, pet count, preview jobs with polling, history,
// preview stage, cart guard.
const MAX_PX = 1536;
const APRON_THEME_PATTERNS = { christmas: "Christmas Classic", valentine: "Kiss the Cook" };
const POLL_MS = 3000;
const SLOW_MS = 150000;
const TOKEN_MARGIN_S = 60;
const HISTORY_SHOWN = 12; // arts per history page; "Show more" loads the next page
const HISTORY_CACHE_KEY = "osf-pet-history"; // per customer and product kind: light rows shown before the network answers
const HISTORY_CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const HISTORY_CACHE_ROWS = 100;
const FULL_KEYS = ["verified_preview_pair", "puzzle_previews"]; // with every *_data_url: bytes only GET /pet-preview/<id> returns
const hasBytes = (key) => key.endsWith("_data_url") || FULL_KEYS.includes(key);
// window.__petPerf: timings in ms since navigation start plus server header text, no ids or customer data.
const perf = (key, value) => {
  (window.__petPerf ||= {})[key] = value;
  console.debug("osf-pet", key, value);
};
// Where the first tiles came from: "metafield" (inlined by the theme), "cache" (this browser) or "processor".
const historySource = (source) => !window.__petPerf?.history_source && perf("history_source", source);
const scrollerOf = (el) => {
  for (let p = el.parentElement; p; p = p.parentElement)
    if (/auto|scroll/.test(getComputedStyle(p).overflowY) && p.scrollHeight > p.clientHeight) return p;
  return document.scrollingElement;
};
const afterPaint = () => new Promise((resolve) => requestAnimationFrame(() => setTimeout(resolve, 0)));
// For speculative fetches: an image requested before window load holds the load event, and WebKit's document.fonts.ready.
const afterLoad = (fn) => (document.readyState === "complete" ? fn() : addEventListener("load", () => fn(), { once: true }));
const PENDING_KEY = "osf-pet-pending";
const PENDING_TTL_MS = 30 * 60 * 1000; // a generating puzzle is dropped from the page after this
const EMAIL_KEY = "osf-pet-email-me";
const POLL_RETRY_MS = [2000, 4000, 8000, 15000, 15000]; // a failed poll is retried; the 5th straight failure gives up
const POLL_GIVE_UP_MS = 120000;
const POLL_TIMEOUT_MS = 20000; // a poll that hangs (lost socket, suspended tab) counts as a failed poll
// Resolves after `ms`, or sooner when the tab returns to view or the network comes back.
const pollPause = (ms) => new Promise((resolve) => {
  const wake = () => document.visibilityState === "visible" && done();
  const done = () => {
    clearTimeout(timer);
    document.removeEventListener("visibilitychange", wake);
    removeEventListener("online", done);
    resolve();
  };
  const timer = setTimeout(done, ms);
  document.addEventListener("visibilitychange", wake);
  addEventListener("online", done);
});
const SAVED_PHOTOS = 20; // the first screenful of saved photos; a "+N more" tile shows the rest
const SAVED_CACHE_KEY = "osf-pet-saved"; // saved photo list + small thumbnails, so a return visit shows them at once
const SAVED_THUMB_PX = 160;
const SESSION_KEY = "osf-pet-render";
// Count and photos last for the tab, so switching to a product of another kind (a page load) keeps them.
const COUNT_KEY = "osf-pet-count";
const TURNSTILE_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js";
const GUEST_KEY = "osf-pet-guest"; // localStorage: the 30-day email guest key (processor/pet_guest.py)
const LOGIN_KEY = "osf-pet-login"; // sessionStorage: the draft id set on the sign-in click; only that tab's return restores it
const PHOTOS_KEY = "osf-pet-photos";
const DRAFT_TTL_MS = 24 * 60 * 60 * 1000;
// Signed-out photos and picks wait for sign-in in IndexedDB: sessionStorage is too small for photos and stays in one tab.
const DRAFT_WAIT_MS = 3000;
const timeLimit = (promise, ms) => Promise.race([promise, new Promise((_, reject) =>
  setTimeout(() => reject(new DOMException("Timed out", "TimeoutError")), ms))]);
function draftStore(mode, act) {
  return timeLimit(new Promise((resolve, reject) => {
    const open = indexedDB.open("osf-pet", 1);
    open.onupgradeneeded = () => open.result.createObjectStore("draft");
    open.onerror = () => reject(open.error);
    open.onsuccess = () => {
      const db = open.result, tx = db.transaction("draft", mode), req = act(tx.objectStore("draft"));
      tx.oncomplete = () => { db.close(); resolve(req.result); };
      tx.onerror = tx.onabort = () => { db.close(); reject(tx.error); };
    };
  }), DRAFT_WAIT_MS); // an IndexedDB open that never settles must not hold sign-in or history
}
const saveDraft = (draft) => draftStore("readwrite", (store) => store.put(draft, "pet"));
const loadDraft = () => draftStore("readonly", (store) => store.get("pet"));
const clearDraft = () => draftStore("readwrite", (store) => store.delete("pet")).catch(() => {});
// Bytes, not File objects: Safari private windows refuse Blobs in IndexedDB. The resized JPEG drops EXIF/GPS and size;
// the original stays only when it cannot be resized (HEIC on Chrome).
const packFile = async (file) => {
  const small = await resize(file).catch(() => null);
  return small ? { name: "pet.jpg", type: small.type, data: await small.arrayBuffer() }
    : { name: file.name, type: file.type, data: await file.arrayBuffer() };
};
const unpackFile = ({ name, type, data }) => new File([data], name, { type });
const MAX_PETS = 10; // pet_portrait.MAX_PETS
const BIG_GROUP = 4; // above this, warn that a retry may be needed
const CROP_MIN_PX = 96; // pet_portrait.CROP_MIN_PX, after the upload resize
const NAMES = {
  design: "Your design",
  photo: "Full photo", petonly: "Pet only", background: "Pet + a little background", // last two: cutouts saved before the full photo
  kungfu: "Kung Fu Master", pokernight: "Poker Night", sushichef: "Sushi Chef", chef: "Head Chef", baker: "Baker", pitmaster: "BBQ Pitmaster", general: "Military General",
  ceo: "Pet CEO", heaven: "Pet Heaven", aristocats: "Victorian Aristocrat", partyhat: "Party Hat",
  cake: "Birthday Cake", confetti: "Confetti Pop", candles: "Blowing Candles", giftbox: "Gift Box", pinata: "Piñata",
  bdaycrown: "Birthday Crown", partyanimal: "Party Animal", icecream: "Ice Cream Cone", bigballoon: "Big Balloon",
  americangothic: "American Gothic", monalisa: "Mona Lisa", pearlearring: "Pearl Earring", scream: "The Scream",
  starrynight: "Starry Night", washington: "Crossing the Delaware", whistler: "Whistler's Mother",
  greatwave: "The Great Wave", grandejatte: "Grande Jatte", vangogh: "Van Gogh", venus: "Birth of Venus",
  thinker: "The Thinker", cupid: "Cupid Wings", heartballoon: "Heart Balloon", loveletter: "Love Letter",
  chocolates: "Box of Chocolates", heartshades: "Heart Sunglasses", candlelit: "Candlelit Dinner",
  xoxopillow: "Heart Pillow", bouquet: "Be Mine Bouquet", heartcloud: "Heart Cloud", kissingbooth: "Kissing Booth",
  sweater: "Sweetheart Sweater", heartthrone: "Heart Throne", trickortreat: "Trick or Treat",
  hauntedhouse: "Haunted House", frankenpet: "Franken-Pet", candycorn: "Candy Corn", santahat: "Santa Hat",
  uglysweater: "Ugly Sweater", giftwrap: "Gift Wrap", naughtylist: "Naughty List", custom: "Your idea",
  readingnook: "Overdue Library Shift", gardenadventure: "Garden Adventure", madscientist: "Mad Scientist Lab", spaceexplorer: "Space Explorer",
  beachday: "Beach Day", holidayworkshop: "Holiday Workshop", renaissancegarden: "Renaissance Garden", busytown: "Busy Town",
  office: "Office Hours", office_distracted: "Distracted at Work", birthdaybash: "Birthday Bash",
  rockband: "Rock Band", bikergang: "Biker Gang", gentlemensclub: "Gentlemen's Club",
}; // pet_portrait.STYLE_NAMES and pet_puzzle.STYLES
const GROUP_NAMES = {
  royalty: "Royalty", superhero: "Superheroes", kungfu: "Kung Fu Masters", pokernight: "Poker Night", heaven: "Pet Heaven",
};

// Ornament canvases draw 560 units at twice the device resolution, so the zoom dialog's copy stays sharp.
const sharpSize = () => Math.min(1680, Math.round(1120 * Math.min(2, globalThis.devicePixelRatio || 1)));

// All-over-print goods on a patterned product photo: print area px, its left/top/width on the photo
// (scripts/pet_goods_previews.py) and the pet box inside it (print_files.APRON_BOX, STOCKING_LEG_BOX).
const GOODS = {
  stocking: { area: [2813, 3225], rect: [0.197, 0.1259, 0.6824], art: [0.05, 0.07, 0.64, 0.57] },
  apron: { area: [4350, 4783], rect: [0.1979, 0.1596, 0.5951], art: [0.30, 0.17, 0.70, 0.60] },
};

// Front-photo fractions -> lifestyle-scene fractions, canvas setTransform order (pet_goods_previews.py --lifestyle).
const SCENES = {
  apron: [1.20880, 0.00000, 0.00000, 1.20513, -0.09597, -0.04236],
  stocking: [0.64504, -0.32072, 0.33820, 0.65947, -0.05848, 0.37670],
};

// Puzzle art -> scene maps in photo fractions, canvas setTransform order, per size (scripts/pet_puzzle_scenes.py):
// the assembled face (clipped to its corners) and the loose pieces.
const PUZZLE_SCENES = {"252":{"assembled":{"face":[0.7834,-0.13809,0.10869,0.61625,0.05436,0.26147],"pieces":[0.155,0,0,0.12167,0.1225,0.79583],"clip":[[0.05443,0.26148],[0.8377,0.12337],[0.94652,0.73964],[0.16299,0.87771]]}},"520":{"assembled":{"face":[0.75965,-0.13391,0.10869,0.61625,0.06624,0.25938],"pieces":[0.155,0,0,0.12548,0.1225,0.79393],"clip":[[0.0663,0.25939],[0.82582,0.12546],[0.93464,0.74173],[0.17487,0.87562]]}}};
const PUZZLE_AREAS = { 252: [4395, 3450], 520: [6300, 5100] };

// Catalog front pixels by garment size; DTG fronts use 300 PPI.
const APPAREL_AREAS = {
  shirt: {
    S: [3319, 3761],
    M: [3909, 4431],
    L: [4500, 5100],
    XL: [4500, 5100],
    "2XL": [4500, 5100],
    "3XL": [4500, 5100],
  },
  sweatshirt: {
    S: [3319, 3761],
    M: [3909, 4431],
    L: [4500, 5100],
    XL: [4500, 5100],
    "2XL": [4500, 5100],
    "3XL": [4500, 5100],
  },
  hoodie: {
    S: [3709, 2472],
    M: [4110, 2740],
    L: [4500, 3000],
    XL: [4500, 3000],
    "2XL": [4500, 3000],
    "3XL": [4500, 3000],
  },
};
const HOODIE_BACK_AREAS = {
  S: [3461, 3955], M: [3839, 4387], L: [4200, 4800],
  XL: [4200, 4800], "2XL": [4200, 4800], "3XL": [4200, 4800],
};
const PRINT_SIZE_LABELS = {
  "Small pocket/left chest": "small",
  Standard: "standard",
  "Large full front": "large",
};

function apparelBox(kind, area, printSize) {
  const [w, h] = area;
  if (printSize === "small")
    return [
      Math.min(1050, 0.3 * w),
      Math.min(1050, 0.3 * h),
      0.75 * w,
      (kind === "hoodie" ? 0.3 : 0.22) * h,
    ];
  const fill = printSize === "standard" ? 0.65 : kind === "hoodie" ? 0.98 : 0.9;
  return [fill * w, fill * h, 0.5 * w, 0.5 * h];
}

// Printify color swatches per blueprint (scripts/printify_color_hex.py 12 49 77); unknown names keep the white blank.
const FLEECE_COLORS = { "light blue": "#d6e6f7", "light pink": "#fee0eb", sand: "#dcd2be", "sport grey": "#cacaca" };
const GARMENT_COLORS = {
  tee: { natural: "#fff8ea", "light blue": "#bcc5cf", "true royal": "#2b4da4", "soft cream": "#f1dab7", gold: "#ffb532",
    silver: "#d6d5d1", pink: "#ffcdce", aqua: "#348fb8" },
  sweatshirt: { ...FLEECE_COLORS, ash: "#f6f6f6" },
  hoodie: { ...FLEECE_COLORS, ash: "#e9e9e9" },
};

// Multiplies the blank's fabric by color hex inside its soft alpha mask (scripts/garment_alpha.py).
function tintGarment(canvas, img, hex) {
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0);
  ctx.globalCompositeOperation = "multiply";
  ctx.fillStyle = hex;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.globalCompositeOperation = "destination-in";
  ctx.drawImage(img, 0, 0);
}

// Real Printify mockups decoded by scripts/pet_knit_mockups.py: white/black print looks, packed print-file
// coordinates (12-bit u, v) and print coverage. A printed color c shows as black + c^tone * (white - black);
// tone is 1.46 unless the mockup data names its own (the sweater's is 1).
function composeKnit(ctx, knit, maps) {
  const tone = maps.tone ?? 1.46;
  const KNIT_TONE = Array.from({ length: 256 }, (_, c) => 1 - (c / 255) ** tone);
  const side = ctx.canvas.width = ctx.canvas.height = maps.side;
  const kw = knit.width, kh = knit.height;
  const art = knit.getContext('2d').getImageData(0, 0, kw, kh).data;
  const out = ctx.createImageData(side, side);
  const o = out.data, white = maps.white, black = maps.black, uv = maps.uv, mask = maps.mask;
  for (let i = 0; i < side * side; i++) {
    const p = i * 4, m = mask[p] / 255;
    let r = white[p], g = white[p + 1], b = white[p + 2];
    if (m > 0) {
      const u = ((uv[p] << 4) | (uv[p + 2] >> 4)) / 4095 * (kw - 1);
      const v = ((uv[p + 1] << 4) | (uv[p + 2] & 15)) / 4095 * (kh - 1);
      const x0 = u | 0, y0 = v | 0, x1 = Math.min(x0 + 1, kw - 1), y1 = Math.min(y0 + 1, kh - 1);
      const fx = u - x0, fy = v - y0;
      const a = (y0 * kw + x0) * 4, bq = (y0 * kw + x1) * 4, c = (y1 * kw + x0) * 4, d = (y1 * kw + x1) * 4;
      for (let k = 0; k < 3; k++) {
        const color = (art[a + k] * (1 - fx) + art[bq + k] * fx) * (1 - fy) + (art[c + k] * (1 - fx) + art[d + k] * fx) * fy;
        const w = white[p + k];
        const val = w - m * KNIT_TONE[Math.round(color)] * (w - black[p + k]);
        if (k === 0) r = val; else if (k === 1) g = val; else b = val;
      }
    }
    o[p] = r; o[p + 1] = g; o[p + 2] = b; o[p + 3] = 255;
  }
  ctx.putImageData(out, 0, 0);
}

function pixels(img) {
  const c = document.createElement('canvas');
  c.width = img.width; c.height = img.height;
  const x = c.getContext('2d', { willReadFrequently: true });
  x.drawImage(img, 0, 0);
  return x.getImageData(0, 0, c.width, c.height).data;
}

// Display geometry is generated from print_files and pet_knit; canvas pixels never enter fulfillment.
// config.body [x, y, w, h] places the portrait in that part of the area (the sweater's body safe area) on the plain knit.
// config.plans["WxH"][layout] is pet_knit.row_layout for that print area: tile bands [tile y, panel y, height] and pet centres.
// `key` names the tile in config.tileSizes: the layout, or "layout-pick" for a sock Design or Palette tile.
export function renderKnit(ctx, cutout, tile, config, fullArea, layout, base = tile, key = layout) {
  const body = layout === 'portrait' ? config.body : undefined;
  const [aw, ah] = fullArea;
  // Render at device resolution so CSS/DPR upscaling adds no grain beyond the stitch grid;
  // the pet stitch below scales with ratio, so the visible cell size is unchanged.
  const ratio = Math.min(560 / aw, 900 / ah) * Math.min(2, globalThis.devicePixelRatio || 1);
  const canvas = ctx.canvas;
  canvas.width = Math.round(aw * ratio);
  canvas.height = Math.round(ah * ratio);
  const plan = layout !== 'portrait' && config.plans?.[fullArea.join('x')]?.[layout];
  const [tw, th] = config.tileSizes[key];
  if (plan) {
    for (const [src, dst, n] of plan.bands) {
      for (let x = 0; x < aw; x += tw) ctx.drawImage(tile, 0, src, tw, n, x * ratio, dst * ratio, tw * ratio, n * ratio);
    }
  } else {
    const [bw, bh] = body ? config.tileSizes.portrait : [tw, th];
    for (let y = 0; y < ah; y += bh) {
      for (let x = 0; x < aw; x += bw) ctx.drawImage(body ? base : tile, x * ratio, y * ratio, bw * ratio, bh * ratio);
    }
  }
  const [sx, sy, w, h] = cutout.bounds;
  function pet(cx, cy, scale, pad) {
    // Knit-integrated: pixel-quantize to a stitch grid, no cream halo ring.
    const dw = Math.max(1, Math.round(w * scale * ratio)), dh = Math.max(1, Math.round(h * scale * ratio));
    const stitch = Math.max(2, Math.round(config.cell * ratio / 3));
    const small = document.createElement('canvas');
    small.width = Math.max(1, Math.round(dw / stitch)); small.height = Math.max(1, Math.round(dh / stitch));
    small.getContext('2d').drawImage(cutout.canvas, sx, sy, w, h, 0, 0, small.width, small.height);
    ctx.save();
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(small, 0, 0, small.width, small.height, (cx - w * scale / 2) * ratio,
      (cy - h * scale / 2) * ratio, dw, dh);
    ctx.restore();
  }
  if (layout !== 'portrait') {
    const box = config.petBox;
    const pad = Math.max(2, Math.floor(config.cell / 4));
    const scale = (box - 2 * pad) / Math.max(w, h);
    if (plan) {
      for (const [cx, cy] of plan.pets) pet(cx, cy, scale, pad);
      return;
    }
    // The legacy 104469 sock: pets in every pet row of the repeat, from the left edge.
    const count = Math.ceil(aw / config.pitch);
    for (let y = config.rowTop[layout]; y < ah; y += th) {
      for (let i = 0; i < count; i++) pet(i * config.pitch + box / 2, y + box / 2, scale, pad);
    }
  } else {
    const [bx, by, bw, bh] = body || [0, 0, aw, ah];
    const ys = config.centers;
    const room = Math.min(config.room ?? 1, ys.length > 1 ? ys[1] - ys[0] : 1, 2 * Math.min(...ys), 2 * (1 - Math.max(...ys)));
    const pad = Math.max(8, Math.floor(bw / 60));
    const scale = Math.min(bw * config.fill / w, (bh * room * config.fill - 2 * pad) / h);
    for (const y of ys) pet(bx + bw / 2, by + bh * y, scale, pad);
  }
}

const resized = new WeakMap();
function resize(file) {
  if (!resized.has(file)) resized.set(file, shrink(file).catch((error) => {
    resized.delete(file);
    throw error;
  }));
  return resized.get(file);
}

async function shrink(file) {
  let bitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    const url = URL.createObjectURL(file);
    bitmap = new Image();
    bitmap.src = url;
    try { await bitmap.decode(); } finally { URL.revokeObjectURL(url); }
  }
  const scale = Math.min(1, MAX_PX / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  canvas.getContext("2d").drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close?.();
  return new Promise((resolve, reject) => canvas.toBlob((blob) => {
    canvas.width = canvas.height = 0;
    if (blob) resolve(blob);
    else reject(new Error("We could not prepare this photo. Choose another JPEG or PNG."));
  }, "image/jpeg", 0.9));
}

// "Ninja (Realistic)", "Poker Night (Cartoon, 2 pets)": cart Style property and captions.
// Matches pet_portrait.style_label.
const HEART_OUTLINE = [[0.5101,0.0554],[0.4842,0.056],[0.462,0.0532],[0.4439,0.0478],[0.4264,0.0415],[0.4091,0.0347],[0.3919,0.0278],[0.3745,0.0212],[0.357,0.015],[0.3393,0.0095],[0.3211,0.0051],[0.3024,0.002],[0.2832,0.0005],[0.2639,0.0005],[0.2447,0.0019],[0.2259,0.0048],[0.2077,0.0091],[0.19,0.0149],[0.1728,0.022],[0.1562,0.0304],[0.14,0.04],[0.1243,0.051],[0.1091,0.0633],[0.0945,0.0769],[0.0806,0.0914],[0.0677,0.1069],[0.056,0.1231],[0.0456,0.14],[0.0363,0.1574],[0.0282,0.1753],[0.0211,0.1937],[0.0152,0.2126],[0.0104,0.232],[0.0064,0.2518],[0.0033,0.272],[0.0011,0.2924],[0.0,0.3132],[0.0,0.3343],[0.0008,0.3553],[0.0023,0.3762],[0.0045,0.3967],[0.0075,0.4165],[0.0114,0.4357],[0.0162,0.4541],[0.0218,0.4721],[0.028,0.4899],[0.0349,0.5078],[0.0424,0.5257],[0.0507,0.5436],[0.0595,0.5612],[0.0689,0.5786],[0.0786,0.5957],[0.0888,0.6127],[0.0995,0.6295],[0.1107,0.646],[0.1223,0.6624],[0.1342,0.6786],[0.1466,0.6946],[0.1593,0.7105],[0.1726,0.7261],[0.1861,0.7416],[0.1999,0.7568],[0.214,0.7719],[0.2282,0.7866],[0.2425,0.8011],[0.257,0.8153],[0.2716,0.8291],[0.2863,0.8427],[0.3011,0.8561],[0.3159,0.8693],[0.3308,0.8823],[0.3458,0.8952],[0.3608,0.9078],[0.3759,0.9202],[0.3909,0.9324],[0.4058,0.9446],[0.4209,0.9567],[0.4361,0.9687],[0.4518,0.9802],[0.4681,0.9906],[0.4853,0.9978],[0.5029,1.0],[0.5205,0.9958],[0.5373,0.9872],[0.5534,0.9762],[0.569,0.9646],[0.5842,0.9527],[0.5993,0.9408],[0.6144,0.9288],[0.6293,0.9163],[0.6442,0.9036],[0.6592,0.8908],[0.6742,0.878],[0.6892,0.8652],[0.7041,0.8521],[0.7188,0.8386],[0.7334,0.8248],[0.7478,0.8106],[0.7622,0.7962],[0.7765,0.7816],[0.7907,0.7668],[0.8048,0.7517],[0.8185,0.7364],[0.832,0.7209],[0.845,0.7052],[0.8577,0.6894],[0.87,0.6733],[0.8818,0.657],[0.8932,0.6406],[0.9042,0.6239],[0.9147,0.6071],[0.9247,0.59],[0.9342,0.5727],[0.9433,0.5553],[0.952,0.5376],[0.9603,0.5198],[0.9678,0.5019],[0.9747,0.4841],[0.9803,0.4661],[0.9854,0.4479],[0.9897,0.4293],[0.9934,0.41],[0.9963,0.3899],[0.9983,0.3693],[0.9995,0.3484],[1.0,0.3275],[0.9995,0.3065],[0.9981,0.2857],[0.9958,0.2652],[0.9926,0.2451],[0.9884,0.2254],[0.9831,0.2062],[0.9769,0.1874],[0.9694,0.1692],[0.9608,0.1515],[0.9511,0.1343],[0.9402,0.1176],[0.9282,0.1015],[0.9149,0.0863],[0.9007,0.0721],[0.8858,0.0591],[0.8705,0.0473],[0.8546,0.0368],[0.8383,0.0275],[0.8215,0.0195],[0.8042,0.0128],[0.7863,0.0074],[0.768,0.0035],[0.7491,0.001],[0.7297,0.0],[0.7103,0.0005],[0.6912,0.0026],[0.6727,0.0062],[0.6547,0.0112],[0.6371,0.0171],[0.6197,0.0236],[0.6024,0.0302],[0.5851,0.037],[0.5641,0.0442],[0.5392,0.0507]];
const styleName = (p) => NAMES[p.style] || `${p.style[0].toUpperCase()}${p.style.slice(1)}`;
const label = (p) => {
  if (p.style === "design") return NAMES.design;
  const look = p.look || "cartoon";
  const name = styleName(p);
  const pets = p.pets > 1 ? `, ${p.pets} pets` : "";
  return `${name} (${look[0].toUpperCase()}${look.slice(1)}${pets})`;
};

// Clarity custom events and tags, so session recordings show each step of the pet flow.
// Each event also goes to the processor's pet_events log in batches (osf-pet-events.js). Timing events
// (page_perf, pet_stall) go to Cloud Logging only, through /pet-log once the sign-in token answers.
petEvents.watchConsent();
let sendLog = null;
const earlyLogs = [];
// Chrome rejects keepalive requests past 64 KB in flight per page, a budget Shopify's own telemetry can fill; resend without keepalive.
const postLog = (url, body, headers) => {
  const send = (keepalive) => fetch(url, { method: "POST", keepalive, credentials: "same-origin", body, headers });
  return send(true).catch(() => send(false)).catch(() => {});
};
const post = (body) => {
  if (sendLog) sendLog(body);
  else if (earlyLogs.length < 20) earlyLogs.push(body);
};
// The guest's "news and deals" box: Shopify's own newsletter form, posted in the background.
const joinNewsletter = (email) => {
  const body = new URLSearchParams({ form_type: "customer", utf8: "✓", "contact[email]": email, "contact[tags]": "newsletter,pet-preview" });
  fetch(`${window.Shopify?.routes?.root || "/"}contact`, { method: "POST", body, credentials: "same-origin" }).catch(() => {});
};

const track = (event, tags = {}) => {
  window.clarity?.("event", `pet_${event}`);
  for (const [k, v] of Object.entries(tags)) window.clarity?.("set", `pet_${k}`, String(v));
  console.info("[osf-pet]", event, tags);
  const { render, ...props } = tags;
  petEvents.queue(event, props, typeof render === "string" ? render : undefined);
};
// Timing events carry nested data, so they bypass track() (no Clarity tags) and add the page and visit id here.
petPerf.start((body) => post({ ...body, page: location.pathname, sid: petEvents.sessionId }));
// Pet-page errors keep file and line; third-party script errors are only counted into page_perf.
const trackError = (details) => {
  const error = petPerf.classifyError(details);
  if (error) track("js_error", error);
};
addEventListener("error", (e) => trackError({ filename: e.filename, line: e.lineno, col: e.colno, message: e.message, stack: e.error?.stack }));
addEventListener("unhandledrejection", (e) => trackError({ message: e.reason?.message || e.reason, stack: e.reason?.stack }));

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Shows the spinner only after 300 ms of loading, then for at least 400 ms, so it never flashes.
function slowSpinner(stage) {
  let shownAt = 0;
  const timer = setTimeout(() => {
    shownAt = performance.now();
    stage.classList.add("is-switching");
  }, 300);
  return async () => {
    clearTimeout(timer);
    if (shownAt) await sleep(shownAt + 400 - performance.now());
    stage.classList.remove("is-switching");
  };
}

// The square a crop string ("x,y,size" fractions of the short side) selects, as an object URL.
const dataUrl = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => reject(reader.error);
  reader.readAsDataURL(blob);
});

let photoUid = 0; // stable ids for the left-to-right pet order

// "Max & Luna" -> ["Max", "Luna"]; pet_portrait.pet_order splits the same way.
const splitNames = (name) => name.split(/\s*(?:,|&|\+|\/|\band\b)\s*/i).filter(Boolean);
const joinNames = (names) => names.length < 3 ? names.join(" & ") : `${names.slice(0, -1).join(", ")} & ${names.at(-1)}`;

function fileFromDataUrl(url) {
  const [head, body] = url.split(",");
  const bytes = Uint8Array.from(atob(body), (c) => c.charCodeAt(0));
  return new File([bytes], "pet.jpg", { type: head.slice(5, head.indexOf(";")) });
}

async function cropUrl(url, crop) {
  const img = new Image();
  img.src = url;
  await img.decode();
  const [x, y, size] = crop.split(",").map(Number);
  const side = Math.round(size * Math.min(img.naturalWidth, img.naturalHeight));
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = Math.min(side, 600);
  canvas.getContext("2d").drawImage(img, x * img.naturalWidth, y * img.naturalHeight, side, side, 0, 0, canvas.width, canvas.width);
  return URL.createObjectURL(await new Promise((r) => canvas.toBlob(r, "image/jpeg", 0.9)));
}

// Display-only cutout for garment mockups; never use this mask for print files. The art's alpha (example
// assets) marks the background and only the neutral white ring band next to it is peeled, so white fur, capes and
// jackets stay. Customer previews send `mask`, the print's peeled cut, used as is. Old previews use the edge flood.
const RING_BAND = 0.03; // print_files.PET_BORDER_WIDTH
export function mockupCutout(image, mask, keepRing = false) {
  const side = 560;
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = side;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) return null;
  let pixels, background;
  try {
    if (mask) {
      ctx.drawImage(mask, 0, 0, side, side);
      const m = ctx.getImageData(0, 0, side, side).data;
      background = new Uint8Array(side * side);
      for (let p = 0; p < background.length; p++) background[p] = m[p * 4] < 128;
      ctx.clearRect(0, 0, side, side);
    }
    ctx.drawImage(image, 0, 0, side, side);
    pixels = ctx.getImageData(0, 0, side, side);
  } catch (error) {
    if (error.name !== "SecurityError") throw error;
    return null;
  }
  const data = pixels.data;
  if (!background) {
    for (let p = 0; p < side * side && !background; p++)
      if (data[p * 4 + 3] < 128) background = true;
    if (background) {
      background = new Uint8Array(side * side);
      for (let p = 0; p < background.length; p++) background[p] = data[p * 4 + 3] < 128;
    }
  }
  const band = keepRing ? -1 : mask ? 0 : background ? Math.ceil(side * RING_BAND) : Infinity;
  const seen = new Uint8Array(side * side);
  const depth = new Uint16Array(side * side);
  const queue = new Uint32Array(side * side);
  let start = 0, end = 0;
  const enqueue = (p, d) => {
    if (seen[p] || d > band) return;
    const i = p * 4;
    const low = Math.min(data[i], data[i + 1], data[i + 2]);
    const high = Math.max(data[i], data[i + 1], data[i + 2]);
    // Include the grey background watermark; enclosed whites and retained RGB stay untouched.
    if (!(background && background[p]) && data[i + 3] > 0 &&
        (background ? low < 200 || high - low > 16 : low < 170 || high - low > 30)) return;
    seen[p] = 1; depth[p] = d;
    queue[end++] = p;
  };
  if (background) {
    for (let p = 0; p < side * side; p++) if (background[p]) enqueue(p, 0);
  } else {
    for (let i = 0; i < side; i++) {
      enqueue(i, 0); enqueue((side - 1) * side + i, 0);
      enqueue(i * side, 0); enqueue(i * side + side - 1, 0);
    }
  }
  while (start < end) {
    const p = queue[start++], x = p % side, d = depth[p] + 1;
    if (x) enqueue(p - 1, d);
    if (x < side - 1) enqueue(p + 1, d);
    if (p >= side) enqueue(p - side, d);
    if (p < side * (side - 1)) enqueue(p + side, d);
  }
  let left = side, top = side, right = -1, bottom = -1, count = 0;
  for (let p = 0; p < seen.length; p++) {
    if (seen[p]) data[p * 4 + 3] = 0;
    if (data[p * 4 + 3] < 128) continue;
    const x = p % side, y = Math.floor(p / side);
    left = Math.min(left, x); top = Math.min(top, y);
    right = Math.max(right, x); bottom = Math.max(bottom, y); count++;
  }
  if (count < side * side * .05 || count > side * side * .95) return null;
  ctx.putImageData(pixels, 0, 0);
  return { canvas, bounds: [left, top, right - left + 1, bottom - top + 1] };
}

// Original photo look: the cut is the whole picture, fitted into the square, so light fur or sheets at its edge stay.
export function photoCutout(image) {
  const side = 560, canvas = document.createElement("canvas");
  canvas.width = canvas.height = side;
  const ctx = canvas.getContext("2d");
  if (!ctx || !image.naturalWidth || !image.naturalHeight) return null;
  const s = side / Math.max(image.naturalWidth, image.naturalHeight);
  const w = Math.round(image.naturalWidth * s), h = Math.round(image.naturalHeight * s);
  const x = (side - w) >> 1, y = (side - h) >> 1;
  ctx.drawImage(image, x, y, w, h);
  return { canvas, bounds: [x, y, w, h] };
}

// Instant, approximate sticker ring in another border, drawn in the browser; the server's exact art replaces it.
// `mask` (customer previews) or the art's alpha is the outline; the ring is the outer band of it.
const BORDER_LOOKS = { // print: pet_border.BORDERS
  white: ["ffffff"], black: ["151515"], coral: ["c9584c"], sunny: ["f2b84b"], teal: ["2f7f7a"],
  confetti: ["ffffff", "confetti"], stars: ["1f2a4a", "stars"],
  glitter: ["c9a24a", "glitter", "8f6b22", "fbe7a1"], silver: ["b8bcc4", "glitter", "5d626b", "f4f6fa"],
};
const rgbOf = (hex) => [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16));
function starPath(ctx, cx, cy, r, turn, points = 10, step = 1) {
  ctx.beginPath();
  for (let i = 0; i < points; i += step) {
    const rr = i % 2 ? r * .45 : r, a = turn + i * Math.PI / 5;
    ctx[i ? "lineTo" : "moveTo"](cx + rr * Math.cos(a), cy + rr * Math.sin(a));
  }
  ctx.closePath();
}
function borderPattern(border, side) {
  const [base, kind, dark, light] = BORDER_LOOKS[border] || BORDER_LOOKS.white;
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = side;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = `#${base}`;
  ctx.fillRect(0, 0, side, side);
  let seed = [...border].reduce((n, c) => n * 31 + c.charCodeAt(0) >>> 0, 7);
  const rand = () => ((seed = seed + 0x6d2b79f5 >>> 0), (((seed ^ seed >>> 15) * (1 | seed)) >>> 0) / 4294967296);
  const scale = (side / 1024) ** 2;
  if (kind === "confetti") {
    const colors = ["c9584c", "f2b84b", "2f7f7a", "5b8def", "e87ab0"];
    for (let i = 0; i < 2500 * scale; i++) {
      const x = rand() * side, y = rand() * side, r = side * (.004 + rand() * .003);
      ctx.fillStyle = `#${colors[Math.floor(rand() * 5)]}`;
      if (rand() < .5) { ctx.beginPath(); ctx.arc(x, y, r, 0, 7); ctx.fill(); }
      else { starPath(ctx, x, y, r * 1.3, rand() * Math.PI, 10, 2); ctx.fill(); }
    }
  } else if (kind === "stars") {
    ctx.fillStyle = "#f5d76e";
    for (let i = 0; i < 1400 * scale; i++) {
      starPath(ctx, rand() * side, rand() * side, side * (.005 + rand() * .004), rand() * Math.PI);
      ctx.fill();
    }
  } else if (kind === "glitter") {
    const grid = Math.ceil(side / 3), lo = rgbOf(dark), hi = rgbOf(light);
    const img = ctx.createImageData(side, side);
    const grains = Float32Array.from({ length: grid * grid }, rand);
    for (let y = 0; y < side; y++) for (let x = 0; x < side; x++) {
      const g = grains[Math.floor(y / 3) * grid + Math.floor(x / 3)], k = g ** 1.6, i = (y * side + x) * 4;
      for (let c = 0; c < 3; c++) img.data[i + c] = g > .985 ? 255 : lo[c] + (hi[c] - lo[c]) * k;
      img.data[i + 3] = 255;
    }
    ctx.putImageData(img, 0, 0);
  }
  return ctx.getImageData(0, 0, side, side).data;
}
export function recolorRing(image, mask, border) {
  const side = Math.min(image.naturalWidth, 768);
  if (!side || !BORDER_LOOKS[border]) return null;
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = side;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) return null;
  let pixels, outline;
  try {
    if (mask) {
      ctx.drawImage(mask, 0, 0, side, side);
      const m = ctx.getImageData(0, 0, side, side).data;
      outline = new Uint8Array(side * side);
      for (let p = 0; p < outline.length; p++) outline[p] = m[p * 4] >= 128;
      ctx.clearRect(0, 0, side, side);
    }
    ctx.drawImage(image, 0, 0, side, side);
    pixels = ctx.getImageData(0, 0, side, side);
  } catch (error) {
    if (error.name !== "SecurityError") throw error;
    return null;
  }
  const data = pixels.data, n = side * side;
  if (!outline) {
    outline = new Uint8Array(n);
    for (let p = 0; p < n; p++) outline[p] = data[p * 4 + 3] >= 128;
  }
  // Chamfer distance (3 per step, 4 per diagonal) from outside the outline, inward.
  const dist = new Uint16Array(n);
  for (let p = 0; p < n; p++) dist[p] = outline[p] ? 65535 : 0;
  const relax = (p, q, w) => { if (dist[q] + w < dist[p]) dist[p] = dist[q] + w; };
  for (let y = 0; y < side; y++) for (let x = 0; x < side; x++) {
    const p = y * side + x;
    if (!dist[p]) continue;
    if (!x || !y || x === side - 1 || y === side - 1) { dist[p] = 3; continue; }
    relax(p, p - 1, 3); relax(p, p - side, 3); relax(p, p - side - 1, 4); relax(p, p - side + 1, 4);
  }
  for (let y = side - 2; y > 0; y--) for (let x = side - 2; x > 0; x--) {
    const p = y * side + x;
    relax(p, p + 1, 3); relax(p, p + side, 3); relax(p, p + side + 1, 4); relax(p, p + side - 1, 4);
  }
  const limit = 3 * (Math.round(side * RING_BAND) + 1);
  const fill = borderPattern(border, side);
  for (let p = 0; p < n; p++) {
    const i = p * 4;
    if (!outline[p]) { data[i + 3] = 0; continue; }
    if (mask) data[i + 3] = 255;
    if (dist[p] > limit) continue;
    data[i] = fill[i]; data[i + 1] = fill[i + 1]; data[i + 2] = fill[i + 2];
  }
  ctx.putImageData(pixels, 0, 0);
  return canvas;
}
const loadImage = (src) => new Promise((resolve, reject) => {
  const img = new Image();
  img.crossOrigin = "anonymous";
  img.onload = () => resolve(img);
  img.onerror = reject;
  img.src = src;
});

class OsfPetPortrait extends HTMLElement {
  // Flat print layout per Size option (print_files.PET_PRINT_AREAS, PET_LAYOUTS); test_pet_format_stage.py checks parity.
  static FORMAT_PRINTS = {
    framed: { fill: 0.8, centres: [0.5], base: "#fff", areas: { '11" × 14"': [3300, 4200], '16" × 20"': [4800, 6000] } },
    canvas: { fill: 0.8, centres: [0.5], base: "#fff", areas: { '12" × 12"': [3600, 3600], '16" × 16"': [4800, 4800] } },
    mug: { fill: 0.85, centres: [0.25, 0.75], base: "#fff", areas: { "11 oz": [2475, 1155], "15 oz": [2475, 1275] } },
    blanket: { fill: 0.8, centres: [0.5], base: "#fff", areas: { '30" × 40"': [8400, 6400] } },
    tote: { fill: 0.8, centres: [0.5], base: "#f1e8d6", areas: { Natural: [3000, 3600] } },
    pillow: { fill: 0.8, centres: [0.25, 0.75], base: "#fff", areas: { '16" × 16"': [5250, 2625], '18" × 18"': [5850, 2925] } },
    calendar: { fill: 0.8, centres: [0.5], base: "#fff", areas: { '10.8" × 8.4"': [3454, 2725] } },
    bandana: { base: "#fff", areas: { S: [2175, 1237], M: [2775, 1650], XL: [4500, 2325] } },
  };

  connectedCallback() {
    this.previewLifecycle = {};
    this.outputKind = this.dataset.outputKind || "portrait";
    this.knitData = JSON.parse(this.querySelector('[data-pet-knit-data]').textContent);
    this.apparelVariants = JSON.parse(this.querySelector("[data-pet-apparel-variants]").textContent);
    this.knitTiles = new Map();
    this.stageData = JSON.parse(this.querySelector("[data-pet-stage-data]")?.textContent || "{}");
    const stageArt = this.querySelector("[data-pet-stage-art]");
    stageArt.addEventListener("load", () => { petPerf.markAfterPaint("stage_painted"); this.renderMockup(); });
    stageArt.addEventListener("error", () => this.renderMockup());
    const garment = this.querySelector("[data-pet-garment]");
    garment.addEventListener("load", () => { this.syncGarmentColor(); this.renderMockup(); });
    garment.addEventListener("error", () => this.renderMockup());
    this.setupZoom();
    this.renderMockup();
    this.puzzleSize = this.querySelector("[data-pet-puzzle-size]");
    this.puzzleSize?.addEventListener("change", () => { this.syncPuzzleVariant(); this.showStage(); filterLifestyle(this); });
    this.renderInput = this.querySelector("[data-pet-render-id]");
    this.styleInput = this.querySelector("[data-pet-style-prop]");
    this.previewUrlInput = this.querySelector("[data-pet-preview-url]");
    this.status = this.querySelector("[data-pet-status]");
    this.emailMe = this.querySelector("[data-pet-email-me]");
    this.emailMe.addEventListener("change", () => this.saveEmailChoice());
    // The checkbox sits on the stage, where a click opens the zoom.
    this.emailMe.closest("label").addEventListener("click", (event) => event.stopPropagation());
    this.button = this.querySelector("[data-pet-generate]");
    this.photo = this.querySelector("[data-pet-photo]");
    // Ad visitors already chose the product in the ad, so their first open step is the photo.
    this.fromAd = /[?&](utm_source|fbclid|gclid)=/.test(location.search);
    this.form = document.getElementById(this.dataset.formId);
    // Buy-now buttons skip the submit guard; the block CSS hides them while this is set.
    this.syncPending();
    if ("puzzleBlocked" in this.dataset)
      this.form?.querySelectorAll('button[type="submit"], input[type="submit"]').forEach((button) => (button.disabled = true));
    // The theme handles submit in document capture; window capture runs first.
    this.onSubmit = (e) => this.guard(e);
    window.addEventListener("submit", this.onSubmit, true);
    this.cards = [...this.querySelectorAll("[data-pet-card]")];
    // The size select is the sole puzzle selector; CSS hides the theme's variant picker, which re-renders.
    if (this.puzzleSize) this.dataset.ownSizes = "";
    this.wrapBuyBox();
    this.splitColumns();
    this.setupStepToggles();
    this.printSize = "large";
    this.hoodieView = "back";
    this.chestPrint = false;
    this.querySelectorAll("[data-pet-hoodie-view]").forEach((button) => button.addEventListener("click", () => {
      this.hoodieView = button.dataset.petHoodieView;
      lifestyleReset(this);
      this.syncApparel();
    }));
    this.syncApparel();
    this.addEventListener("variant:selected", (event) => {
      if (!["hoodie", "puzzle", "scene_print"].includes(this.dataset.kind) || !this.cards[3].contains(event.target) || this.formatRequest) return;
      this.variantPending = true;
      this.syncPending();
    });
    this.addEventListener("variant:update", (event) => {
      if (!this.cards[3].contains(event.target) || this.formatRequest) return;
      const id = String(event.detail.resource?.id || "");
      // A late response for an earlier choice must not undo the instant price already shown.
      if (!["puzzle", "scene_print"].includes(this.dataset.kind) && (this.variantPending || this.instantVariant) && id !== this.dataset.selectedVariant) {
        requestAnimationFrame(() => this.showVariantPrice());
        return;
      }
      this.instantVariant = false;
      this.variantPending = false;
      this.dataset.selectedVariant = id;
      if (this.puzzleSize) {
        const option = [...this.puzzleSize.options].find((option) => option.dataset.shopifyVariant === id && !option.disabled);
        this.puzzleSize.value = option?.value || '';
        this.syncPuzzleVariant();
        this.showStage();
      }
      this.syncApparel();
      this.syncPending();
    });
    this.updateSteps();
    this.setupFormatNavigation();
    this.setupOrnaments();
    this.drawCalendar(); // tile images and the style heading show before sign-in and history load
    // A buyer's own change before history revalidation must survive it; restoreSettings' clicks are untrusted.
    this.addEventListener("input", (e) => { if (e.isTrusted) this.cachedPick = null; });
    this.addEventListener("click", (e) => {
      const page = e.target.closest("[data-pet-calendar-page]");
      if (page) this.pickCalendarPage(page.dataset.petCalendarPage);
      else if (e.target.closest("[data-pet-calendar-all]")) this.makeCalendarPages();
    });
    this.addEventListener("change", (e) => {
      if (e.isTrusted) this.cachedPick = null;
      if (e.isTrusted && e.target.name === "pet-style" && this.calendar) this.calendarStyleChanged(e.target.value);
      // Option picks only (radios, selects, checkboxes); typed text and files are never logged.
      const t = e.target;
      if (t.name && (t.type === "radio" || t.type === "checkbox" || t.tagName === "SELECT") && t.name !== "pet-style")
        petEvents.queue("pick", { field: t.name.replace(/^properties\[(.*)\]$/, "$1").slice(0, 40),
                                  value: t.type === "checkbox" ? t.checked : String(t.value).slice(0, 60) });
      if (e.target.closest("variant-picker") && !["puzzle", "scene_print"].includes(this.dataset.kind) && this.cards[3]?.contains(e.target)) {
        const options = [...(this.apparelVariants[this.dataset.selectedVariant] || [])];
        this.cards[3].querySelectorAll("variant-picker input:checked, variant-picker select").forEach((input) => {
          const selected = input instanceof HTMLSelectElement ? input.selectedOptions[0] : input;
          options[Number(selected.dataset.inputId.split("-")[0]) - 1] = selected.value;
        });
        const variant = Object.entries(this.apparelVariants).find(([, values]) => values.every((value, index) => value === options[index]));
        this.dataset.selectedVariant = variant?.[0] || "";
        this.form.querySelector('[name="id"]').value = this.dataset.selectedVariant;
        // Price changes on the click from embedded variant data; the theme's section fetch only follows.
        this.showVariantPrice();
        this.instantVariant = true;
        return this.syncApparel();
      }
      if (e.target.name === "properties[Print size]") {
        this.printSize = PRINT_SIZE_LABELS[e.target.value];
        return this.renderMockup();
      }
      if (e.target.name === "properties[Contrast]") return this.renderMockup();
      if (e.target.name === "properties[Row order]") this.rowOrderPicked = true;
      if (e.target.name === "properties[Filler]" && !this.rowOrderPicked) this.syncRowOrder(e.target.value);
      if (e.target.closest("[data-pet-apron-designs]")) this.apronPicked = true;
      if (e.target.name === "properties[Design]" || e.target.name === "properties[Palette]") {
        if (this.dataset.kind === "socks") return this.switchKnitLayout();
        filterLifestyle(this);
        return this.syncApparel();
      }
      if (e.target.name === "properties[Layout]" || e.target.name === "properties[Filler]" || e.target.name === "properties[Row order]") return this.switchKnitLayout();
      if (e.target.name === "pet-species") return this.showSpecies(e.target.value);
      if (e.target.name === "pet-look") return this.showLook();
      if (e.target.name === "pet-theme") return this.showTheme();
      if (e.target.name === "pet-border") { this.showStage(); return this.rename(); }
      if (e.target.name !== "pet-style") return;
      track("style_chosen", { style: e.target.value });
      this.styleTouched = true;
      this.styleChanged(e.target);
      this.showExample = true;
      this.showStage();
      if (e.target.value === "custom") this.instruction?.focus();
    });
    if ("puzzleBlocked" in this.dataset) this.showStage();
    // The App Proxy signs these with the shopper's customer id when signed in; text/plain skips a preflight.
    const funnel = (body) => postLog(`${this.dataset.proxyUrl}/pet-funnel`, body, { "Content-Type": "text/plain" });
    petEvents.setContext(this);
    const beacon = (body) => navigator.sendBeacon?.(`${this.dataset.proxyUrl}/pet-funnel`, body) || funnel(body);
    addEventListener("pagehide", () => petEvents.flush(beacon));
    document.addEventListener("visibilitychange", () => document.visibilityState === "hidden" && petEvents.flush(beacon));
    track("page_view", { kind: this.dataset.kind });
    new IntersectionObserver((entries, observer) => {
      if (!entries.some((e) => e.isIntersecting)) return;
      observer.disconnect();
      track("widget_view", { kind: this.dataset.kind });
    }).observe(this);
    const loginId = sessionStorage.getItem(LOGIN_KEY);
    sessionStorage.removeItem(LOGIN_KEY);
    const loginReturn = "signedIn" in this.dataset && !!loginId;
    if (loginReturn) track("login_return", { kind: this.dataset.kind });
    if (!("signedIn" in this.dataset)) {
      petEvents.setSender(funnel);
      sendLog ||= () => {};
      earlyLogs.splice(0);
      track("gate_view", { kind: this.dataset.kind });
      // Signed out, photos and picks stay in this browser; the sign-in link stands where the preview button is.
      this.button.hidden = true;
      this.querySelectorAll("[data-pet-login], [data-pet-guest-login]").forEach((a) => a.addEventListener("click", (e) => {
        e.preventDefault();
        this.signInToPreview(e.currentTarget.href);
      }));
      this.setupGuest();
    }
    this.button.addEventListener("click", () => this.generate());
    this.setupDesign();
    // The page's own picks; the variant picker's re-renders move defaultChecked, so they are kept here.
    this.defaults = this.picks((input) => input.name.startsWith("pet-"));
    this.querySelector("[data-pet-reset]")?.addEventListener("click", () => this.resetSettings());
    this.redo = this.querySelector("[data-pet-redo]");
    this.fix = this.querySelector("[data-pet-fix]");
    this.redoButton = this.querySelector("[data-pet-redo-button]");
    this.querySelector("[data-pet-failure-report]")?.addEventListener("click", () => this.sendFailureReport());
    this.redoButton.addEventListener("click", () => this.redoPreview());
    // Enter would submit the product form around this box.
    this.fix.addEventListener("keydown", (e) => {
      if (e.key !== "Enter") return;
      e.preventDefault();
      this.redoPreview();
    });
    this.nameInput = this.querySelector("[data-pet-name]");
    this.nameInput.addEventListener("change", () => this.rename());
    this.nameInput.addEventListener("input", () => { this.nameEdited = true; this.nameCleared = false; });
    // choose() leaves a focused field alone; show the preview's name once the buyer leaves it untouched.
    this.nameInput.addEventListener("blur", () => {
      if (!this.nameEdited && !this.nameCleared) this.nameInput.value = this.shownPreview()?.name || "";
    });
    this.nameStyle = this.querySelector("[data-pet-name-style]");
    this.nameStyle.addEventListener("change", () => this.rename());
    this.nameStyle.addEventListener("focus", () => this.prefetchSamples());
    this.querySelector("[data-pet-style-info]").addEventListener("click", () => this.toggleStylePanel());
    this.borders = [...this.querySelectorAll('input[name="pet-border"]')]; // none on the tee page
    this.nameInput.addEventListener("keydown", (e) => {
      if (e.key !== "Enter") return;
      e.preventDefault();
      this.rename();
    });
    this.photo.addEventListener("change", () => {
      const file = this.photo.files[0];
      this.photoStart = performance.now();
      if (file) petEvents.queue("photo_upload_start", { photo_type: file.type, photo_kb: Math.round(file.size / 1024) });
      this.pickSaved(null);
      this.showPhoto();
    });
    const files = this.querySelector("[data-pet-files]");
    this.querySelector("[data-pet-files-open]").addEventListener("click", () => files.click());
    files.addEventListener("change", () => {
      if (!files.files.length) return;
      const dt = new DataTransfer();
      for (const file of files.files) dt.items.add(file);
      this.photo.files = dt.files;
      files.value = "";
      this.pickSaved(null);
      this.showPhoto();
    });
    this.instruction = this.querySelector("[data-pet-instruction]");
    this.instruction?.addEventListener("input", () => {
      this.countInstruction();
      this.sceneError("");
    });
    this.dropPhotos();
    this.count = this.querySelector("[data-pet-count]");
    this.extras = []; // one photo per extra pet: {file, url, crop}
    this.countCache = {};
    for (const b of this.querySelectorAll("[data-pet-count-step]"))
      b.addEventListener("click", () => {
        this.countTouched = true;
        this.setCount(Number(this.count.value) + Number(b.dataset.petCountStep));
      });
    this.count.addEventListener("change", () => {
      this.countTouched = true;
      this.setCount(this.count.value);
    });
    /** @type {HTMLInputElement | null} */
    const extraInput = this.querySelector("input[data-pet-extra-photo]");
    extraInput?.addEventListener("change", () => this.addExtras(extraInput));
    // With saved photos, "+" first offers them; "Upload a new photo" opens the file picker.
    // The file input sits at the label's center, so a click can land on either; a passed-on label click reaches it next.
    this.querySelector(".osf-pet__extra-add")?.addEventListener("click", (e) => {
      if (e.target === extraInput && this.pickingFile) return void (this.pickingFile = false);
      if (this.addingPet || !this.savedTiles().length) {
        this.stopAddingPet();
        this.pickingFile = e.target !== extraInput;
        return;
      }
      e.preventDefault();
      this.startAddingPet();
    });
    this.querySelector("[data-pet-saved-upload]")?.addEventListener("click", () => {
      this.stopAddingPet();
      this.pickingFile = true;
      extraInput?.click();
    });
    this.querySelector("[data-pet-saved-cancel]")?.addEventListener("click", () => this.stopAddingPet());
    this.addEventListener("keydown", (e) => e.key === "Escape" && this.addingPet && this.stopAddingPet());
    this.querySelector("[data-pet-consent]").addEventListener("change", () => this.flushPhotos());
    this.querySelector("[data-pet-order-redo] button").addEventListener("click", () => this.redoPreview());
    this.setupCrop();
    const keptCount = sessionStorage.getItem(COUNT_KEY);
    this.countTouched = !!keptCount;
    this.setCount(keptCount || 1, false);
    this.photos = {}; // job id -> photo URL (this session's upload, or fetched from the server)
    this.cropped = {}; // job id -> the cropped photo that preview was drawn from
    this.querySelector("[data-pet-mirror]")?.addEventListener("click", () => this.mirror());
    this.querySelectorAll("[data-pet-flip-to]").forEach((b) =>
      b.addEventListener("click", () => this.flip(b.dataset.petFlipTo === "photo")),
    );
    this.querySelector("[data-pet-delete-all-open]").addEventListener("click", () => this.deleteAll());
    this.querySelector("[data-pet-history-more]").addEventListener("click", () => this.loadMoreHistory());
    this.querySelector("[data-pet-saved-more]")?.addEventListener("click", () => {
      this.savedShown = Infinity;
      if (this.savedList) this.paintSaved(this.savedList);
      else this.drawSaved();
    });
    this.querySelector("[data-pet-delete-dialog] dialog").addEventListener("cancel", (event) => event.preventDefault());
    this.showSeasons();
    this.showTheme(); // memorial products open on Memorial, sweater and socks on Christmas
    const draft = this.restoreDraft(loginId).catch((error) => track("draft_restore_error", { reason: error?.name || "unknown" }));
    // Highlight the next step after any input; history clicks and previews update it too.
    for (const t of ["change", "input", "click"])
      this.addEventListener(t, (e) => {
        if (e.isTrusted) this.stepsTouched = true;
        setTimeout(() => this.updateSteps());
      });
    this.start(loginReturn ? draft : null); // back from sign-in, history waits so it cannot pick over the draft
  }

  // Active = first unfinished step; done steps get a check, later ones dim. Every visit opens Choose a product first
  // (ad visitors open the photo step); after that only headers, Next and cart validation move it, so a pick inside a step never closes it.
  updateSteps(pin = false) {
    const previews = this.previews?.length > 0;
    const done = [
      true,
      previews || (this.design ? !!this.promptInput?.value.trim() : !!this.photo?.files?.length || !!this.sourceJob),
      previews,
      false,
    ];
    const active = done.indexOf(false);
    if (this.design) this.openStep = active;
    else if (!this.stepsTouched) this.openStep = this.fromAd && !this.querySelector("[data-pet-ornaments]:not([hidden])") ? 1 : 0;
    this.stepState = { active, done };
    this.drawSteps(done, active, pin);
  }

  // Step headers become accordion buttons; the card's other children collapse under them (block CSS).
  setupStepToggles() {
    if (this.design) return; // the short design form keeps every step open
    this.stepNames = this.cards.map((card) => card.querySelector(".osf-pet__step").textContent.replace(/^\s*\d+/, "").trim());
    this.nextButtons = this.cards.slice(0, 3).map((card) => {
      const next = document.createElement("button");
      next.type = "button";
      next.className = "osf-pet__next";
      next.hidden = true;
      next.addEventListener("click", () => {
        const to = Number(next.dataset.step);
        this.showStep(to);
        this.cards[to].querySelector(".osf-pet__step-toggle").focus({ preventScroll: true });
      });
      card.append(next);
      return next;
    });
    this.cards.forEach((card, i) => {
      const header = card.querySelector(".osf-pet__step");
      const button = document.createElement("button");
      button.type = "button";
      button.className = "osf-pet__step-toggle";
      button.append(...header.childNodes);
      const summary = document.createElement("span");
      summary.className = "osf-pet__step-summary";
      button.append(summary);
      header.append(button);
      button.addEventListener("click", () => {
        const close = this.openStep === i && !this.allOpen;
        petEvents.queue(close ? "step_close" : "step_open", { step: i + 1 });
        this.showStep(close ? -1 : i);
      });
    });
    this.expandAll = document.createElement("button");
    this.expandAll.type = "button";
    this.expandAll.className = "osf-pet__expand-all";
    this.expandAll.addEventListener("click", () => {
      this.allOpen = !this.allOpen;
      this.stepsTouched = true;
      if (!this.allOpen) this.openStep = -1;
      this.updateSteps();
    });
    this.cards[0].before(this.expandAll);
  }

  // The page hero's upload button: open the photo step and its file picker in the same tap.
  startUpload() {
    this.showStep(1, true);
    this.photo?.click();
  }

  // Opens one step (-1 closes all). Without a scroll target, the opened header stays where the shopper clicked.
  showStep(i, scroll = false) {
    this.openStep = i;
    this.allOpen = false;
    this.stepsTouched = true;
    this.updateSteps(!scroll);
    if (scroll) this.cards[i].scrollIntoView({ block: "start", behavior: "smooth" });
  }

  // The open step (or every step after Expand all) expands; the others show a check when done and the current choice.
  drawSteps(done, active, pin = false) {
    const motion = !matchMedia("(prefers-reduced-motion: reduce)").matches;
    const anchor = pin && this.cards[this.openStep]?.querySelector(".osf-pet__step");
    const top = anchor && anchor.getBoundingClientRect().top;
    const changed = [];
    this.cards.forEach((card, i) => {
      const focused = i === this.openStep;
      // The short design form keeps every step open, so no card slides shut and leaves its fields inert.
      const open = focused || !!this.allOpen || this.design;
      card.id ||= `osf-pet-step-${i + 1}`;
      // A card drawn for the first time (page load, or the buy box a format switch replaced) does not slide.
      if (motion && card.stepDrawn && open === card.classList.contains("is-collapsed"))
        changed.push([card, open, card.getBoundingClientRect().height]);
      card.stepDrawn = true;
      const next = this.nextButtons?.[i];
      if (next) {
        // Next always opens the following step, even when saved photos and previews finish later ones.
        next.dataset.step = i + 1;
        next.textContent = `Next: ${this.stepNames[next.dataset.step]}`;
        next.hidden = !done[i] || !!this.allOpen;
      }
      card.classList.toggle("is-collapsed", !open);
      card.classList.toggle("is-done", done[i] && !focused);
      card.classList.toggle("is-active", focused);
      card.classList.toggle("is-todo", i > active && !open);
      const button = card.querySelector(".osf-pet__step-toggle");
      if (!button) return;
      button.setAttribute("aria-expanded", String(open));
      button.setAttribute("aria-controls", card.id);
    });
    if (this.expandAll) {
      this.expandAll.textContent = this.allOpen ? "Collapse all" : "Expand all";
      this.expandAll.setAttribute("aria-expanded", String(!!this.allOpen));
      this.expandAll.setAttribute("aria-controls", this.cards.map((card) => card.id).join(" "));
    }
    this.drawSummaries();
    changed.forEach(([card, open, from]) => this.animateStep(card, open, from));
    if (anchor) this.holdInView(anchor, top);
  }

  // Slides a card from its old to its new height. A closing card keeps its content painted, fading and inert
  // until the slide ends; then the block CSS hides it. The opening card's content fades in by CSS.
  animateStep(card, open, from) {
    card.endStep?.();
    const to = card.getBoundingClientRect().height;
    const timing = { duration: 250, easing: "cubic-bezier(0.2, 0, 0, 1)", fill: "forwards" };
    const slide = card.animate([{ height: `${from}px` }, { height: `${to}px` }], timing);
    slide.id = "osf-pet-step";
    const parts = [slide];
    let frozen = [];
    if (!open) {
      card.classList.add("is-closing");
      frozen = [...card.querySelectorAll(":scope > :not(.osf-pet__step, .group-block-content, .osf-pet__cart-note), "
        + ":scope > .group-block-content > :not(.osf-pet__step, .osf-pet__cart-note)")].filter((el) => !el.inert);
      for (const el of frozen) {
        el.inert = true;
        parts.push(el.animate([{ opacity: 1 }, { opacity: 0 }], timing));
      }
    }
    card.classList.add("is-animating");
    card.stepAnimation = slide;
    card.endStep = () => {
      card.endStep = card.stepAnimation = null;
      card.classList.remove("is-animating", "is-closing");
      frozen.forEach((el) => { el.inert = false; });
      parts.forEach((part) => part.cancel());
    };
    slide.onfinish = () => card.stepAnimation === slide && card.endStep();
  }

  // Keeps the opened header at the height the shopper clicked it while the cards above it resize.
  holdInView(header, top) {
    const scroller = scrollerOf(header);
    const hold = () => {
      // Instant: the theme's smooth html scrolling would let the header drift for a few frames.
      scroller.scrollBy({ top: header.getBoundingClientRect().top - top, behavior: "instant" });
      if (this.cards.some((card) => card.stepAnimation)) requestAnimationFrame(hold);
    };
    hold();
  }

  // Collapsed headers name the current choice. Step 3 names what the stage shows, matching the redo label.
  drawSummaries() {
    if (!this.stepState) return;
    const shown = this.stagePreview;
    const style = this.querySelector('input[name="pet-style"]:checked');
    const original = this.querySelector('input[name="pet-look"]:checked')?.value === "original";
    const picks = [...this.cards[3].querySelectorAll("variant-picker input:checked, variant-picker select")].map((input) => input.value);
    const price = this.cards[3].querySelector("product-price .price")?.textContent.trim();
    const summaries = [
      this.dataset.formatName,
      this.stepState.done[1] ? `Photo added${this.pets > 1 ? `, ${this.pets} pets` : ""}` : "",
      shown ? `Preview: ${styleName(shown)}` : original ? "Style: Original photo"
        : style ? `Style: ${style.closest("label").textContent.trim()}` : "",
      [...picks, price].filter(Boolean).join(" \u00b7 "),
    ];
    this.cards.forEach((card, i) => {
      const summary = card.querySelector(".osf-pet__step-summary");
      if (summary) summary.textContent = i === this.openStep ? "" : summaries[i] || "";
    });
  }

  // Step 4's card is the theme's buy box group (variant picker, price, buy buttons) from the template.
  wrapBuyBox() {
    const box = this.form?.closest(".group-block");
    const step = this.cards[3];
    const content = box?.querySelector(".group-block-content");
    if (!content || box.contains(this)) return;
    box.classList.add("osf-pet__card");
    content.prepend(...step.children);
    step.remove();
    this.cards[3] = box;
  }

  splitColumns() {
    const block = this.closest(".shopify-block");
    const group = block?.parentElement;
    const steps = this.querySelector(".osf-pet__steps");
    if (!group?.classList.contains("group-block-content") || !steps) return;
    const first = steps.firstElementChild;
    let before = true;
    for (const child of [...group.children]) {
      if (child === block) { before = false; continue; }
      if (before) steps.insertBefore(child, first);
      else steps.append(child);
    }
  }

  // Cartoon/Realistic: swap style thumbnails (realistic ones load on first switch) and the stage example.
  showLook() {
    this.showOriginal();
    this.showThumbs();
    this.showExample = true;
    this.showStage();
  }

  // Original photo: no styles, themes or scene text, only the cutout choice.
  showOriginal() {
    const original = this.querySelector('input[name="pet-look"]:checked')?.value === "original";
    const box = this.querySelector("[data-pet-original]");
    if (box) box.hidden = !original;
    for (const el of this.querySelectorAll('.osf-pet__themes[aria-label="Theme"], .osf-pet__styles, [data-pet-species-row]'))
      el.hidden = original;
    const scene = this.instruction?.closest("div");
    if (scene) scene.hidden = original;
  }

  // Seasonal themes show only between their MM-DD dates (pet_portrait.SEASONS); the range may wrap the year.
  showSeasons() {
    const now = new Date();
    const today = `${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
    for (const label of this.querySelectorAll("[data-season]")) {
      const [from, to] = label.dataset.season.split(",");
      label.hidden = from <= to ? !(from <= today && today <= to) : !(today >= from || today <= to);
    }
  }

  // Only the picked theme's styles show; a hidden pick moves to that theme's first style.
  showTheme() {
    // Puzzle and scene print pages have no theme picker.
    const theme = this.querySelector('input[name="pet-theme"]:checked')?.value;
    if (!theme) return;
    const labels = [...this.querySelectorAll(".osf-pet__style[data-theme]")];
    const species = this.chosenSpecies();
    // data-only marks styles that suit one species (a cat does not fetch sticks or swim for fun).
    for (const label of labels) label.hidden = label.dataset.theme !== theme || (this.pets <= 1 && label.dataset.only && label.dataset.only !== species);
    const picked = this.querySelector('input[name="pet-style"]:checked');
    if (picked?.closest("label").hidden) {
      const first = labels.find((l) => !l.hidden).querySelector("input");
      first.checked = true;
      this.styleChanged(first);
      this.showExample = true;
      this.showStage();
    }
  }

  // Thumbnails follow the look, the Dog | Cat preview and, for 2+ pets, the group examples.
  showThumbs() {
    for (const img of this.querySelectorAll("[data-thumb-realistic], [data-thumb-group], [data-thumb-cat]")) {
      const species = this.previewSpecies(img.closest("label")?.querySelector("input"));
      img.dataset.thumb ||= img.getAttribute("src");
      img.src = this.exampleUrl(img, "thumb", undefined, species) || img.dataset.thumb;
    }
    this.syncSpeciesToggle();
    // Group previews are not species-specific, so the Dog | Cat choice only shows for one pet.
    const row = this.querySelector("[data-pet-species-row]");
    if (row) row.hidden = this.pets > 1 || this.querySelector('input[name="pet-look"]:checked')?.value === "original";
  }

  exampleKey(prefix, group = this.pets > 1, species = "dog", realistic = this.querySelector('input[name="pet-look"]:checked')?.value === "realistic") {
    return prefix + (group ? "Group" : species === "cat" ? "Cat" : "") + (realistic ? "Realistic" : "");
  }

  // First example that exists: exact species/look, then that species, then the dog examples.
  exampleUrl(el, prefix, group, species) {
    const keys = [this.exampleKey(prefix, group, species), this.exampleKey(prefix, group, species, false),
      this.exampleKey(prefix, group, "dog"), this.exampleKey(prefix, group, "dog", false)];
    return keys.map((key) => el.dataset[key]).find(Boolean);
  }

  // One Dog | Cat choice (session, default dog) drives every thumbnail and the stage example. Preview only.
  chosenSpecies() {
    this.species ??= sessionStorage.getItem("osf-pet-preview-species") || "dog";
    return this.species;
  }

  previewSpecies(radio) {
    return radio?.dataset.exampleCat ? this.chosenSpecies() : "dog";
  }

  syncSpeciesToggle() {
    const input = this.querySelector(`input[name="pet-species"][value="${this.chosenSpecies()}"]`);
    if (input) input.checked = true;
  }

  showSpecies(species) {
    this.species = species;
    sessionStorage.setItem("osf-pet-preview-species", species);
    this.showTheme();
    this.showThumbs();
    this.showExample = true;
    this.showStage();
    // Warm the other species' large examples so the next flip is instant.
    const other = species === "cat" ? "dog" : "cat";
    for (const r of this.querySelectorAll('input[name="pet-style"][data-example-cat]'))
      if (!r.closest("label").hidden) new Image().src = this.exampleUrl(r, "example", false, other);
  }

  // About the p75 of finished previews (Oct 2026 logs); scenes draw and upscale a larger image, and group scenes take about twice as long.
  estimateSeconds() {
    if (!["puzzle", "scene_print"].includes(this.outputKind)) return 30;
    return this.pets > 1 ? 360 : 180;
  }

  // Counts down to the estimate, then counts up so a slow preview never reads as stuck.
  etaText(s, total) {
    const left = Math.ceil(total - s);
    if (left > 60) return `About ${Math.ceil(left / 60)} minutes left`;
    if (left > 0) return `About ${left} seconds left`;
    const m = Math.floor(s / 60), sec = Math.floor(s % 60);
    return `Taking longer than usual, still drawing (${m ? `${m} min ${sec} s` : `${sec} s`} so far)`;
  }

  // Eases toward 95% over the estimate; generate() sets 100% on finish.
  startProgress(t0 = Date.now()) {
    const total = this.estimateSeconds();
    const bar = this.querySelector("[data-pet-progress]");
    const eta = this.querySelector("[data-pet-eta]");
    const tick = () => {
      const s = (Date.now() - t0) / 1000;
      const pct = s < total ? (s / total) * 85 : 85 + 10 * (1 - Math.exp(-(s - total) / 20));
      bar.firstElementChild.style.width = `${pct}%`;
      bar.setAttribute("aria-valuenow", String(Math.round(pct)));
      eta.textContent = this.etaText(s, total);
    };
    tick();
    this.progressTimer = setInterval(tick, 250);
  }

  // The checkbox under the wait message; the promise to email only shows while it is checked.
  syncEmailNote() {
    const note = this.querySelector("[data-pet-email-note]");
    const scene = ["puzzle", "scene_print"].includes(this.outputKind);
    note.hidden = !scene;
    note.textContent = this.emailMe.checked
      ? "No need to wait. We'll email you when your puzzle is ready, so feel free to keep browsing or come back later."
      : "No need to wait. Your puzzle will be in your previews when you come back.";
  }

  customerKey() {
    return `${EMAIL_KEY}:${this.tokenValue?.split(".")[0] || ""}`;
  }

  async saveEmailChoice() {
    localStorage.setItem(this.customerKey(), this.emailMe.checked ? "1" : "0");
    this.syncEmailNote();
    const body = new FormData();
    body.append("email_me", String(this.emailMe.checked));
    await this.api("/pet-email-pref", { method: "POST", body }).catch(() => {});
  }

  stopProgress() {
    clearInterval(this.progressTimer);
    this.querySelector("[data-pet-progress] i").style.width = "100%";
  }

  disconnectedCallback() {
    this.previewLifecycle = null;
    this.layoutSwitch = null;
    this.cutout = this.mockupSource = null;
    this.zoomScene?.replaceChildren();
    window.removeEventListener("submit", this.onSubmit, true);
    window.removeEventListener("popstate", this.onFormatPop);
    this.formatRequest?.abort();
    this.zoomDialog?.close();
    this.form?.removeAttribute("data-pet-pending");
  }

  guard(event) {
    if (event.target !== this.form) return;
    if (this.formatRequest || this.variantPending) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
    if (this.calendar && !this.calendarReady(event)) return;
    const renaming = this.renderInput.value && this.nameChanged();
    if (this.renderInput.value && !renaming && !("puzzleBlocked" in this.dataset)
        && (this.outputKind === "portrait" || this.puzzleCartReady())) {
      const job = this.renderInput.value;
      const image = (this.cartImages ||= {})[job];
      if (image === undefined || image instanceof Promise) {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (image === undefined) this.copyCartImage(job, event.submitter);
        return;
      }
      if (image) this.previewUrlInput.value = image;
      return track("add_to_cart", { render: job, variant: this.form.querySelector('[name="id"]')?.value });
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    const signedOut = !this.canPreview();
    track(signedOut ? "cart_blocked_signed_out" : renaming ? "cart_blocked_rename" : "cart_blocked_no_preview");
    // Open the first incomplete step: the photo, the preview, or (puzzles) the product options.
    if (!renaming && !("puzzleBlocked" in this.dataset))
      this.showStep(this.renderInput.value ? 3 : Math.min(this.stepState.active, 2), true);
    const message = "puzzleBlocked" in this.dataset
      ? "Puzzle artwork and purchasing await approval."
      : signedOut ? "Make a preview first. Sign in to make your free preview."
      : renaming ? "Updating your preview. Add to cart again once it shows."
        : this.previews?.find((p) => p.job_id === this.renderInput.value)?.launch_eligible === false
          ? "This puzzle preview cannot be ordered yet. Try the Reading Nook or Office scene."
          : "Make a preview of your pet before adding to cart.";
    this.status.textContent = message;
    // The step 3 status can sit in a hidden form; the note sits beside the buy buttons.
    const note = this.cards[3].querySelector("[data-pet-cart-note]");
    note.textContent = message;
    note.hidden = false;
    if (renaming) this.rename();
  }

  // Carry the picked preview across the Sticker/Magnet/Tee pages so one render can go on any of them.
  syncFormatLinks() {
    const id = this.renderInput.value;
    this.querySelectorAll("[data-pet-format]").forEach((a) => {
      const url = new URL(a.getAttribute("href"), location.origin);
      const targetKind = ["puzzle", "scene_print"].includes(a.dataset.petFormat) ? a.dataset.petFormat : "portrait";
      if (id && targetKind === this.outputKind) url.searchParams.set("pet_render", id);
      else url.searchParams.delete("pet_render");
      a.setAttribute("href", url.pathname + url.search);
    });
  }

  collapseFormats(collapsed) {
    this.querySelector(".osf-pet__formats").toggleAttribute("data-collapsed", collapsed);
    this.changeProduct.setAttribute("aria-expanded", String(!collapsed));
    this.changeProduct.textContent = collapsed ? "Change product" : "Hide products";
  }

  setupFormatNavigation() {
    this.productUrl = location.href;
    this.formatStatus = document.createElement("p");
    this.formatStatus.setAttribute("role", "status");
    this.formatStatus.dataset.petFormatStatus = "";
    this.querySelector(".osf-pet__formats").after(this.formatStatus);
    // On phones the picker shows only the current product until "Change product" opens it (CSS owns the breakpoint).
    this.changeProduct = this.querySelector("[data-pet-change-product]");
    this.changeProduct.hidden = false;
    this.changeProduct.addEventListener("click", () => this.collapseFormats(this.changeProduct.getAttribute("aria-expanded") === "true"));
    this.collapseFormats(true);
    this.addEventListener("click", (event) => {
      const link = event.target.closest("[data-pet-format]");
      if (!link || event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      // Full scenes cannot be transferred to silhouette products: follow the link to that product's own previews.
      const target = ["puzzle", "scene_print"].includes(link.dataset.petFormat) ? link.dataset.petFormat : "portrait";
      if (target !== this.outputKind) return;
      event.preventDefault();
      this.switchFormat(new URL(link.href), true);
    });
    this.onFormatPop = () => {
      const url = new URL(location.href);
      // A same-product pop (the mobile cart drawer closing) must not re-render the pickers and drop the buyer's picks.
      if (!this.formatRequest && decodeURIComponent(url.pathname.split("/").pop()) === this.dataset.productHandle) return;
      this.switchFormat(url, false);
    };
    window.addEventListener("popstate", this.onFormatPop);
    const product = new URL(location.href).searchParams.get("product");
    const link = [...this.querySelectorAll("[data-pet-format]")].find((a) =>
      a.dataset.petFormat === product || new URL(a.href).pathname.endsWith(`/products/${product}`));
    if (link && (["puzzle", "scene_print"].includes(link.dataset.petFormat) ? link.dataset.petFormat : "portrait") === this.outputKind)
      this.switchFormat(new URL(link.href), false);
  }

  setupOrnaments() {
    const panel = this.querySelector('[data-pet-ornaments]');
    const data = panel?.querySelector('[data-pet-ornament-variants]');
    this.ornament = null;
    delete this.querySelector('[data-pet-stage]').dataset.ornamentShape;
    const note = this.querySelector('[data-pet-ornament-note]');
    if (note) note.hidden = true;
    const options = data ? JSON.parse(data.textContent).filter(v => v.config) : [];
    const selected = options.find(v => String(v.id) === this.form?.querySelector('[name="id"]')?.value);
    if (!selected) return filterLifestyle(this);
    this.ornament = selected.config;
    filterLifestyle(this);
    if (note) {
      note.querySelector('[data-pet-ornament-label]').textContent = `Selected: ${selected.config.material}, ${selected.config.label}. ${selected.config.kind === 'acrylic' ? 'Includes a red ribbon for hanging.' : 'Includes a gold string for hanging.'}`;
      note.querySelector('[data-pet-ornament-jump]').onclick = () => {
        panel.scrollIntoView({ block: 'center' });
        panel.querySelector('fieldset:not([hidden]) button[aria-pressed="true"]')?.focus();
      };
      note.hidden = false;
    }
    this.querySelector('[data-pet-stage]').dataset.ornamentShape = selected.config.shape;
    // Chips are the sole selector; the server-rendered form and price remain authoritative.
    const native = this.cards[3].querySelector('variant-picker');
    if (native) { native.hidden = true; native.inert = true; }
    for (const [selector, choices] of [
      ['[data-pet-materials]', [...new Set(options.map(v => v.config.material))].map(material =>
        options.find(v => v.config.material === material && v.config.label === selected.config.label) ||
        options.find(v => v.config.material === material))],
      ['[data-pet-shapes]', options.filter(v => v.config.material === selected.config.material)],
    ]) {
      const row = panel.querySelector(selector);
      row.replaceChildren();
      row.closest('fieldset').hidden = choices.length < 2;
      for (const variant of choices) {
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = selector === '[data-pet-materials]' ? variant.config.material : variant.config.label;
        button.setAttribute('aria-pressed', String(selector === '[data-pet-materials]' ?
          variant.config.material === selected.config.material : variant.id === selected.id));
        button.disabled = !variant.available;
        button.addEventListener('click', () => {
          const url = new URL(this.productUrl);
          url.searchParams.set('variant', variant.id);
          this.switchFormat(url, true);
        });
        row.append(button);
      }
    }
    // With no real choice left, hide the empty panel and the jump that would focus nothing.
    const choosable = !!panel.querySelector('fieldset:not([hidden])');
    panel.hidden = !choosable;
    if (note) note.querySelector('[data-pet-ornament-jump]').hidden = !choosable;
    this.renderMockup();
  }

  async switchFormat(url, push) {
    if (push) this.productUrl = location.href;
    this.formatRequest?.abort();
    const request = new AbortController();
    this.formatRequest = request;
    this.syncPending();
    this.cards[3].querySelector("variant-picker")?.cancelPendingUpdate();
    this.cards[3].inert = true;
    const stage = this.querySelector("[data-pet-stage]");
    stage.classList.add("is-updating");
    this.setRenderState("loading");
    this.formatStatus.classList.add("visually-hidden");
    this.formatStatus.textContent = "Updating format…";
    try {
      const section = this.closest(".shopify-section").id.replace("shopify-section-", "");
      const fetchUrl = new URL(url);
      fetchUrl.searchParams.delete("product");
      fetchUrl.searchParams.set("section_id", section);
      // Switching back to a format already opened reuses its markup, with no wait.
      let text = (this.formatPages ||= new Map()).get(fetchUrl.href);
      if (!text) {
        const response = await fetch(fetchUrl, { signal: request.signal });
        if (!response.ok) throw new Error("Format could not be loaded.");
        text = await response.text();
      }
      const html = new DOMParser().parseFromString(text, "text/html");
      if (request.signal.aborted) return;
      const widget = html.querySelector("osf-pet-portrait");
      const form = html.querySelector("product-form-component form");
      const box = form?.closest(".group-block");
      const title = html.querySelector(".product-details h1");
      const picker = box?.querySelector("variant-picker");
      if (!widget || widget.dataset.productHandle !== decodeURIComponent(url.pathname.split("/").pop())
          || widget.dataset.outputKind !== this.outputKind || !box || !title
          || (picker && picker.dataset.productId !== form.closest("product-form-component").dataset.productId))
        throw new Error("Format could not be loaded.");
      if (widget.querySelector('[data-pet-ornament-variants]') && url.searchParams.has('variant') &&
          form.querySelector('[name="id"]')?.value !== url.searchParams.get('variant'))
        throw new Error("Ornament variant could not be loaded.");
      this.formatPages.set(fetchUrl.href, text);
      const scroll = [window.scrollX, window.scrollY];
      const panes = [...this.querySelectorAll(".osf-scroll-pane")].map((pane) => [pane, pane.scrollTop]);
      const newBox = document.importNode(box, true);
      newBox.classList.add("osf-pet__card");
      newBox.inert = true;
      newBox.querySelector("form").setAttribute("data-pet-pending", "");
      const content = newBox.querySelector(".group-block-content");
      content.prepend(this.cards[3].querySelector(".osf-pet__step"), this.cards[3].querySelector("[data-pet-cart-note]"), this.querySelector("[data-pet-hoodie-options]"), this.querySelector("[data-pet-print-sizes]"));
      const layouts = widget.querySelector("[data-pet-layouts]");
      if (layouts) content.querySelector("[data-pet-print-sizes]").after(document.importNode(layouts, true));
      this.cards[3].replaceWith(newBox);
      this.cards[3] = newBox;
      this.dataset.formId = form.getAttribute("id");
      this.form = document.getElementById(this.dataset.formId);
      // The calendar page grid (and its cart property) belongs to the calendar product only.
      this.querySelector("[data-pet-calendar]")?.remove();
      const nextPlan = widget.querySelector("[data-pet-calendar]");
      if (nextPlan) this.querySelector("[data-pet-versions]").before(document.importNode(nextPlan, true));
      for (const input of this.querySelectorAll("input[form]")) input.setAttribute("form", this.dataset.formId);
      this.querySelector("h1").textContent = title.textContent;
      document.title = `${title.textContent.trim()} – Shirt Happens`;
      // Pattern pickers belong to the product (apron, stocking); take the new product's set before the scenes filter on it.
      this.querySelector("[data-pet-patterns]").innerHTML = widget.querySelector("[data-pet-patterns]")?.innerHTML || "";
      syncLifestyle(this, widget);
      // Example photos are per-product server markup in the stage pane, which the format swap
      // otherwise leaves behind: take the new product's row (or drop it when it has none).
      const examples = this.querySelector(".osf-pet__examples");
      const nextExamples = widget.querySelector(".osf-pet__examples");
      if (examples && nextExamples) examples.replaceWith(document.importNode(nextExamples, true));
      else if (examples) examples.remove();
      else if (nextExamples) this.querySelector("[data-pet-versions]")?.before(document.importNode(nextExamples, true));
      this.dataset.kind = widget.dataset.kind;
      this.dataset.formatName = widget.dataset.formatName;
      this.dataset.productHandle = widget.dataset.productHandle;
      this.dataset.selectedVariant = widget.dataset.selectedVariant;
      this.variantPending = false;
      this.querySelector("[data-pet-variant-prices]").textContent = widget.querySelector("[data-pet-variant-prices]").textContent;
      this.apparelVariants = JSON.parse(widget.querySelector("[data-pet-apparel-variants]").textContent);
      this.knitData = JSON.parse(widget.querySelector('[data-pet-knit-data]').textContent);
      const ornaments = this.querySelector('[data-pet-ornaments]');
      const nextOrnaments = widget.querySelector('[data-pet-ornaments]');
      ornaments.innerHTML = nextOrnaments?.innerHTML || '';
      ornaments.hidden = !nextOrnaments || nextOrnaments.hidden;
      this.setupOrnaments();
      this.querySelector("[data-pet-stage]").className = this.querySelector("[data-pet-stage]").className
        .replace(/osf-pet__canvas--\S+/, `osf-pet__canvas--${this.dataset.kind}`);
      this.syncApparel();
      // Original photo prints only on some formats; follow the new format's offer.
      const offered = !widget.querySelector('input[name="pet-look"][value="original"]').disabled;
      const original = this.querySelector('input[name="pet-look"][value="original"]');
      original.disabled = !offered;
      original.closest("label").hidden = !offered;
      if (!offered && original.checked) {
        this.querySelector('input[name="pet-look"][value="cartoon"]').checked = true;
        this.showLook();
      }
      this.showBorders();
      this.querySelector("[data-pet-border-prop]").disabled = !this.bordered();
      // Versions this format cannot print hide; a hidden pick moves to its nearest shown version.
      if (this.shownPreview() && !this.fits(this.shownPreview())) this.choose(this.shownPreview(), true);
      if (this.previews) {
        this.drawHistoryRow();
        if (this.shownPreview()) this.drawVersions(this.shownPreview().art_of || this.renderInput.value);
      }
      this.drawCalendar();
      this.querySelectorAll("[data-pet-format]").forEach((a) => {
        const current = new URL(a.href).pathname === url.pathname;
        a.classList.toggle("is-current", current);
        if (current) a.setAttribute("aria-current", "page");
        else a.removeAttribute("aria-current");
      });
      this.collapseFormats(true);
      const login = this.querySelector("[data-pet-login]");
      if (login) login.href = `/customer_authentication/login?return_to=${encodeURIComponent(url.pathname + url.search)}`;
      url.searchParams.delete("product");
      if (push) history.pushState(history.state, "", url);
      else history.replaceState(history.state, "", url);
      this.productUrl = url.href;
      this.syncFormatLinks();
      this.updateSteps();
      for (const [pane, top] of panes) pane.scrollTop = top;
      window.scrollTo(...scroll);
      this.formatStatus.textContent = "";
      if (this.nameInput && this.borderChanged()) await this.rename();
    } catch (error) {
      if (error.name === "AbortError") return;
      this.formatStatus.classList.remove("visually-hidden");
      this.formatStatus.textContent = "Could not switch format. Your photo and previews are safe. Try the format again.";
      if (!push) history.replaceState(history.state, "", this.productUrl);
    } finally {
      if (this.formatRequest === request) {
        this.formatRequest = null;
        if (!this.busy || this.drawing) stage.classList.remove("is-updating"); // a running job keeps its own card
        this.cards[3].inert = false;
        this.syncPending();
        this.renderMockup();
      }
    }
  }

  syncPuzzleVariant() {
    if (!this.puzzleSize || !this.form) return;
    const option = this.puzzleSize.selectedOptions[0];
    const id = option && !option.disabled ? option.dataset.shopifyVariant : '';
    this.dataset.selectedVariant = id || '';
    this.form.querySelector('[name="id"]').value = id || '';
    const prices = JSON.parse(this.querySelector('[data-pet-variant-prices]').textContent);
    this.cards?.[3]?.querySelectorAll('product-price .price').forEach((price) => {
      price.textContent = prices[id] || 'Unavailable';
    });
    this.syncPending();
  }

  puzzleCartReady() {
    const option = this.puzzleSize?.selectedOptions[0];
    const preview = this.previews?.find((preview) => preview.job_id === this.renderInput.value);
    return !!option && !option.disabled && !!option.dataset.shopifyVariant
      && this.form?.querySelector('[name="id"]').value === option.dataset.shopifyVariant
      && preview?.launch_eligible === true && this.canPreview();
  }

  showVariantPrice() {
    const prices = JSON.parse(this.querySelector("[data-pet-variant-prices]").textContent);
    this.cards[3].querySelectorAll("product-price .price").forEach((price) => { price.textContent = prices[this.dataset.selectedVariant] || "Unavailable"; });
  }

  syncPending() {
    const preview = this.previews?.find((p) => p.job_id === this.renderInput.value);
    const pending = !!this.formatRequest || !!this.variantPending || !this.renderInput.value || "puzzleBlocked" in this.dataset
      || (preview?.look === "original" && this.querySelector('input[name="pet-look"][value="original"]').disabled)
      || (this.outputKind !== 'portrait' && !this.puzzleCartReady())
      || (!!this.nameInput && this.nameChanged());
    this.form?.toggleAttribute("data-pet-pending", pending);
    if (!pending) this.cards?.[3]?.querySelector("[data-pet-cart-note]")?.setAttribute("hidden", "");
  }

  async start(draft) {
    if ("puzzleBlocked" in this.dataset) {
      this.showStage();
      return;
    }
    if (!this.canPreview()) {
      this.stageLoaded();
      return this.showStage();
    }
    await draft;
    const merged = await this.mergeGuest();
    // An Order link opens with ?pet_render=<id>; carry it into the order field before history loads.
    const asked = new URLSearchParams(location.search).get("pet_render");
    if (asked && !this.renderInput.value) this.renderInput.value = asked;
    if ("signedIn" in this.dataset) this.drawSaved(); // saved photos load alongside history, not after it
    const inline = !merged && this.showInlineHistory(); // merged previews are not in the page's metafield yet
    if (inline) {
      this.resumePending();
      this.prewarmToken().then((ok) => !inline.found && this.reconcileHistory(ok));
      return;
    }
    let loaded = false;
    const cached = this.showCachedHistory();
    try {
      await this.token();
      perf("token_ms", Math.round(performance.now()));
      await this.loadHistory(cached);
      loaded = true;
      this.resumePending();
    } catch (err) {
      track("start_error", { error: String(err.stack || err).slice(0, 150) });
      this.status.textContent = err.message;
    } finally {
      // No preview of this product kind to show: clear "Loading your preview…" back to the example.
      if (!loaded || !this.renderInput.value) {
        this.stageLoaded();
        this.showStage();
      }
    }
  }

  // Past designs the theme inlined from the customer's metafield (processor/pet_public.py), or null.
  readInlineHistory() {
    try {
      const data = JSON.parse(this.querySelector("[data-pet-history-data]")?.textContent || "null");
      return Array.isArray(data?.rows) ? data : null;
    } catch {
      return null;
    }
  }

  // Draws the metafield rows at once, with no service call. Returns whether the picked design is among them.
  showInlineHistory() {
    const data = this.readInlineHistory();
    if (!data) return null;
    this.inline = { rows: data.rows.filter((r) => (r.output_kind || "portrait") === this.outputKind && this.ownsRow(r)), truncated: !!data.truncated };
    const page = this.inlinePage(1);
    this.applyHistory(page.previews, page.more, true);
    historySource("metafield");
    perf("inline_rows", this.inline.rows.length);
    const picked = this.pickedRender();
    return { found: !picked || this.inline.rows.some((r) => r.job_id === picked) };
  }

  // The first `pages` pages of arts (and the picked art when it is older) from the inlined rows.
  inlinePage(pages) {
    const artOf = (r) => r.art_of || r.job_id;
    const { rows, truncated } = this.inline;
    const arts = [...new Set(rows.map(artOf))];
    const keep = new Set(arts.slice(0, pages * HISTORY_SHOWN));
    const picked = rows.find((r) => r.job_id === this.pickedRender());
    if (picked) keep.add(artOf(picked));
    return { previews: rows.filter((r) => keep.has(artOf(r))), more: Math.max(arts.length - pages * HISTORY_SHOWN, 0) + (truncated ? 1 : 0) };
  }

  // The metafield lacked the linked design: ask the service, or show the default when it does not answer.
  async reconcileHistory(tokenOk) {
    try {
      if (!tokenOk) throw new Error("Could not load that preview. Reload the page to try again.");
      await this.loadHistory(null, true);
    } catch (err) {
      this.status.textContent = err.message;
    } finally {
      this.stageLoaded();
      if (!this.renderInput.value) this.showStage();
    }
  }

  // After first paint, wake the service and get the sign-in token without holding up the page. A failure is
  // quiet; every action asks for the token again. Resolves to whether the token arrived.
  prewarmToken() {
    return afterPaint()
      .then(() => this.token())
      .then(() => {
        perf("token_ms", Math.round(performance.now()));
        return true;
      })
      .catch((err) => {
        perf("token_error", err.status || 0);
        return false;
      });
  }

  // A preview started before the buyer left keeps running on the server; poll it again here.
  resumePending() {
    const saved = JSON.parse(localStorage.getItem(this.pendingKey()) || "null");
    if (!saved || saved.output_kind !== this.outputKind || this.busy) return;
    const expired = Date.now() - saved.started_at > PENDING_TTL_MS;
    if (expired || this.previews.some((p) => p.job_id === saved.job_id)) return localStorage.removeItem(this.pendingKey());
    this.pendingJob = { job_id: saved.job_id, started_at: saved.started_at };
    this.makePreview("", undefined, undefined, () => {});
  }

  stageLoaded() {
    this.querySelector("[data-pet-stage]").classList.remove("is-loading");
  }

  // App Proxy token: 15 minutes, bound to the signed-in customer.
  async token() {
    if (this.tokenValue && Date.now() < this.tokenExpires)
      return this.tokenValue;
    this.tokenRequest ||= this.fetchToken().finally(() => (this.tokenRequest = null));
    return this.tokenRequest;
  }

  async fetchToken() {
    if (!this.canPreview()) throw new Error("Sign in to make your free preview.");
    const guest = "guest" in this.dataset ? `&guest=${encodeURIComponent(localStorage.getItem(GUEST_KEY) || "")}` : "";
    let res;
    try {
      res = await petPerf.timedFetch(this.dataset.tokenUrl, () => fetch(`${this.dataset.tokenUrl}?visitor=${petEvents.visitorId()}${guest}`, { credentials: "same-origin" }), { name: "token" });
    } catch {
      throw new Error("Could not connect. Keep this page open, check your connection or content blocker, and try again.");
    }
    if (res.status >= 500)
      throw Object.assign(new Error("The preview service is starting up. Try again in a moment."), { status: res.status });
    const data = res.ok ? await res.json() : {};
    if (guest && res.ok && !data.guest) {
      this.leaveGuest(data); // the guest key expired or moved to an account
      throw Object.assign(new Error("Enter your email to make your free preview."), { status: 401 });
    }
    if (!data.signed_in && !data.guest)
      throw Object.assign(new Error("Sign in again to make previews, then reload the page."), { status: 401 });
    return this.acceptToken(data);
  }

  acceptToken(data) {
    this.tokenValue = data.token;
    this.drawCalendar(); // the saved calendar design is per customer, so it shows once the customer is known
    petPerf.tokenReceived();
    const cached = localStorage.getItem(this.customerKey());
    if (cached) this.emailMe.checked = cached === "1";
    // The token expires after a long visit; refresh it so later logs are not rejected.
    const petLog = async (body) => postLog(`${this.dataset.proxyUrl}/pet-log`, body,
      { "X-Pet-Token": await this.token().catch(() => this.tokenValue), "Content-Type": "application/json" });
    sendLog ||= (body) => petLog(JSON.stringify(body));
    earlyLogs.splice(0).forEach(sendLog);
    petEvents.setSender(petLog);
    this.tokenExpires = Date.now() + (data.expires_in - TOKEN_MARGIN_S) * 1000;
    this.setAccount(data);
    return this.tokenValue;
  }

  // Signed in, or an email guest (processor/pet_guest.py): the shopper has a pet token.
  canPreview() {
    return "signedIn" in this.dataset || "guest" in this.dataset;
  }

  // Signed out on a portrait product: a stored guest key makes this an email guest; else the email gate shows
  // when the processor has guests turned on.
  setupGuest() {
    if (!this.querySelector("[data-pet-email-gate]")) return;
    if (localStorage.getItem(GUEST_KEY)) {
      this.dataset.guest = "";
      this.showGuest();
      return;
    }
    fetch(`${this.dataset.tokenUrl}?visitor=${petEvents.visitorId()}`, { credentials: "same-origin" })
      .then((res) => (res.ok ? res.json() : {}))
      .then((data) => this.showEmailGate(data), () => {});
  }

  showGuest() {
    this.querySelector("[data-pet-gate]").hidden = true;
    this.button.hidden = false;
  }

  leaveGuest(data) {
    localStorage.removeItem(GUEST_KEY);
    delete this.dataset.guest;
    this.tokenValue = null;
    this.button.hidden = true;
    this.querySelector("[data-pet-gate]").hidden = false;
    this.showEmailGate(data);
  }

  showEmailGate(data) {
    const gate = this.querySelector("[data-pet-email-gate]");
    if (!data?.guest_enabled || !gate || !gate.hidden) return;
    this.guestSiteKey = data.turnstile_site_key;
    gate.hidden = false;
    this.querySelector("[data-pet-sign-in-gate]").hidden = true;
    track("email_gate_view", { kind: this.dataset.kind });
    const submit = gate.querySelector("[data-pet-guest-submit]");
    submit.addEventListener("click", () => this.submitGuest());
    // Enter would submit the product form around this box.
    gate.querySelector("[data-pet-guest-email]").addEventListener("keydown", (e) => {
      if (e.key !== "Enter") return;
      e.preventDefault();
      this.submitGuest();
    });
  }

  async submitGuest() {
    const gate = this.querySelector("[data-pet-email-gate]");
    const input = gate.querySelector("[data-pet-guest-email]");
    const status = gate.querySelector("[data-pet-guest-status]");
    const submit = gate.querySelector("[data-pet-guest-submit]");
    if (submit.disabled) return;
    if (!input.value.trim() || !input.checkValidity()) {
      status.textContent = "Enter your email to make your free preview.";
      return input.focus();
    }
    track("email_submit", { kind: this.dataset.kind });
    submit.disabled = true;
    status.textContent = "Checking you are not a robot…";
    try {
      const body = new FormData();
      body.append("email", input.value.trim());
      body.append("turnstile_token", await this.guestTurnstile(gate.querySelector("[data-pet-guest-turnstile]")));
      if (gate.querySelector("[data-pet-guest-news]").checked) body.append("news", "1"); // reminder consent
      status.textContent = "Starting your free preview…";
      const res = await fetch(`${this.dataset.proxyUrl}/guest`, { method: "POST", body, credentials: "same-origin" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.guest_key) throw new Error(data.detail || "Could not start your preview. Try again in a moment.");
      localStorage.setItem(GUEST_KEY, data.guest_key);
      if (gate.querySelector("[data-pet-guest-news]").checked) joinNewsletter(input.value.trim());
      this.dataset.guest = "";
      window.turnstile?.remove(this.guestWidget);
      this.showGuest();
      this.acceptToken(data);
      status.textContent = "";
      this.generate();
    } catch (error) {
      status.textContent = error.message;
      if (this.guestWidget != null) window.turnstile?.reset(this.guestWidget);
    } finally {
      submit.disabled = false;
    }
  }

  // A Turnstile token for the guest sign-up (action guest_mint, which the processor requires). Signed out, the page
  // loads no Turnstile script, so the first sign-up loads it.
  async guestTurnstile(box) {
    if (!window.turnstile)
      await new Promise((resolve) => {
        const script = Object.assign(document.createElement("script"), { src: TURNSTILE_SRC, async: true });
        script.addEventListener("load", resolve);
        script.addEventListener("error", resolve);
        document.head.append(script);
      });
    return new Promise((resolve, reject) => {
      const render = () => {
        if (this.guestWidget != null) return window.turnstile.reset(this.guestWidget);
        this.guestWidget = window.turnstile.render(box, {
          sitekey: this.guestSiteKey, action: "guest_mint",
          callback: (t) => this.guestResolve?.(t), "error-callback": () => this.guestReject?.(new Error("The robot check failed. Reload the page and try again.")),
        });
      };
      this.guestResolve = resolve;
      this.guestReject = reject;
      if (!window.turnstile) return reject(new Error("The robot check did not load. Turn off your content blocker and reload."));
      render();
    });
  }

  // Back from sign-in with a guest key: its previews move to the account when the account has the same email.
  async mergeGuest() {
    const key = "signedIn" in this.dataset && localStorage.getItem(GUEST_KEY);
    if (!key) return false;
    const body = new FormData();
    body.append("guest_key", key);
    try {
      const res = await this.request("/guest-merge", { method: "POST", body });
      if (res.status >= 500) return false; // try again next visit
      localStorage.removeItem(GUEST_KEY);
      return res.ok && (await res.json().catch(() => ({}))).merged > 0;
    } catch {
      return false;
    }
  }

  async request(path, init = {}, perfName) {
    // Signed out, nothing reaches the processor: no upload, photo check or drawing before sign-in or an email.
    if (!this.canPreview()) throw new Error("Sign in to make your free preview.");
    const token = await this.token();
    const url = `${this.dataset.proxyUrl}${path}`;
    try {
      let res = await petPerf.timedFetch(url, () => fetch(url, { ...init, credentials: "same-origin", headers: { ...init.headers, ...petEvents.headers(), "X-Pet-Token": token } }), { name: perfName });
      // Only an explicit body rejection is safe to resend; a network failure may already have queued a job.
      if (res.status === 413 && init.body instanceof FormData && this.dataset.endpoint) {
        track("proxy_upload_limit", { path: path.split("?")[0] });
        const direct = `${this.dataset.endpoint}${path}`;
        res = await petPerf.timedFetch(direct, () => fetch(direct, { ...init, headers: { ...petEvents.headers(), "X-Pet-Token": token } }), { retried: true });
      }
      return res;
    } catch (error) {
      track("network_error", { reason: error.name, path: path.split("?")[0], visibility: document.visibilityState,
        photo_kb: Math.round((this.photo.files[0]?.size || 0) / 1024), photo_type: this.photo.files[0]?.type || "unknown",
        browser: navigator.userAgent.slice(0, 150) });
      throw new Error("Could not connect. Keep this page open, check your connection or content blocker, and try again.");
    }
  }

  // Checkout shows a line image only from Shopify's CDN, so the first add to cart of a preview copies it there.
  // The line keeps the bucket URL when the copy fails or takes over 5 s.
  async copyCartImage(job, submitter) {
    const copy = this.api(`/pet-preview/${job}/cart-image`, { method: "POST" }).then((d) => d.url || null, () => null);
    this.cartImages[job] = Promise.race([copy, new Promise((resolve) => setTimeout(resolve, 5000, null))]);
    this.cartImages[job] = await this.cartImages[job];
    this.form.requestSubmit(submitter);
  }

  async api(path, init = {}) {
    return this.readApi(await this.request(path, init));
  }

  async readApi(res) {
    const data = await res.json().catch(() => null);
    if (!res.ok || !data)
      throw Object.assign(new Error(data?.detail || "The preview service is unavailable. Try again in a moment."),
        { status: res.status, field: data?.field });
    this.setAccount(data);
    return data;
  }

  setAccount(data) {
    if ("remaining" in data) this.setRemaining(data.remaining);
    if ("email_me" in data) {
      this.emailMe.checked = data.email_me;
      localStorage.setItem(this.customerKey(), data.email_me ? "1" : "0");
    }
    this.syncEmailNote();
    if (!data.credit_codes) return;
    const codes = this.querySelector("[data-pet-codes]");
    codes.replaceChildren(
      ...data.credit_codes.map((c) => {
        const chip = document.createElement("span");
        chip.className = "osf-pet__code";
        const b = document.createElement("b");
        b.textContent = c.code;
        chip.append(b, ` · $${c.amount} off`);
        return chip;
      }),
    );
    codes.hidden = !data.credit_codes.length;
  }

  setRemaining(n) {
    this.remaining = n;
    // Calendar pages draw on their design's own allowance, not the free previews.
    const calendar = this.calendar;
    if (calendar) n = calendar.left;
    this.button.textContent = n === 0 ? (calendar ? "No previews left for this calendar" : "No previews left today")
      : `${this.previewFailed ? "Try again" : calendar ? "Make this page" : "Make my preview"}${n == null ? "" : ` (${n} left)`}`;
    if (this.pendingJob) this.button.textContent = "Resume my preview";
    this.button.disabled = (n === 0 && !this.pendingJob) || this.busy || this.preparing;
    if (this.redoButton) this.redoButton.disabled = !!this.pendingJob || n === 0 || this.busy || this.preparing;
    const packs = this.querySelector("[data-pet-packs]");
    if (packs) packs.hidden = n > 0 || !("signedIn" in this.dataset); // packs are bought signed in
  }

  dropPhotos() {
    const zone = this.querySelector("[data-pet-drop]");
    const over = (on) => (e) => {
      e.preventDefault();
      zone.classList.toggle("is-over", on);
    };
    zone.addEventListener("dragover", over(true));
    zone.addEventListener("dragleave", over(false));
    zone.addEventListener("drop", (e) => {
      over(false)(e);
      if (!e.dataTransfer.files.length) return;
      this.photo.files = e.dataTransfer.files;
      this.showPhoto();
    });
    // A pasted image (screenshot, copied photo) becomes the main photo, unless the buyer is typing text.
    document.addEventListener("paste", (e) => {
      const file = [...(e.clipboardData?.files || [])].find((f) => f.type.startsWith("image/"));
      if (!file || e.target.closest?.("textarea, input[type=text]")) return;
      e.preventDefault();
      const dt = new DataTransfer();
      dt.items.add(new File([file], file.name || "pasted.png", { type: file.type }));
      this.photo.files = dt.files;
      this.pickSaved(null);
      this.showPhoto();
      track("photo_pasted");
    });
  }

  // Pet count stepper (1-10). Group counts show plural style names and group thumbnails.
  // The chosen count is kept for the tab across product switches.
  setCount(value, remember = true) {
    // Scene caps (data-max-pets) hide scenes that cannot fit; the stepper goes to the product's max.
    // A restored count on puzzles clamps to the per-product 2-pet cap unless the checked scene declares more.
    const checkedScene = this.querySelector('input[name="pet-style"]:checked');
    const sceneMax = Number(checkedScene?.dataset?.maxPets) || MAX_PETS;
    const maximum = this.outputKind === 'portrait' ? MAX_PETS
      : this.querySelector('input[name="pet-style"]')
        ? (this.outputKind === 'puzzle'
          ? (remember ? MAX_PETS : Number(checkedScene?.dataset?.maxPets) || 2)
          : sceneMax)
        : 2;
    // Each selected photo shows at least one pet, so the count never drops below the photos.
    const wanted = Math.min(MAX_PETS, Math.max(this.photoCount(), Math.round(Number(value)) || 1));
    const n = Math.min(maximum, wanted);
    if (remember) sessionStorage.setItem(COUNT_KEY, String(wanted));
    const clamp = this.querySelector("[data-pet-count-clamp]");
    clamp.hidden = wanted <= maximum;
    clamp.textContent = clamp.hidden ? "" : `This product fits up to ${maximum} pets, so the count is set to ${maximum}.`;
    this.pets = n;
    this.count.value = String(n);
    const [minus, plus] = this.querySelectorAll("[data-pet-count-step]");
    minus.disabled = n <= Math.max(1, this.photoCount());
    plus.disabled = n >= maximum;
    this.querySelector("[data-pet-count-note]").hidden = n <= BIG_GROUP;
    this.querySelector("[data-pet-extras]").hidden = n < 2 && !this.photoUrl && !this.mainLoading;
    const group = n > 1;
    if (!this.design) {
      this.querySelector("[data-pet-name-label]").textContent = group ? "Pet names (optional)" : "Pet name (optional)";
      const nameHint = this.querySelector("[data-pet-name-order]");
      if (nameHint) nameHint.hidden = !group;
      this.nameInput.placeholder = group ? (n === 2 ? "e.g. Milo & Luna" : "e.g. Biscuit, Milo & Max") : "e.g. Biscuit";
    }
    for (const input of this.querySelectorAll('input[name="pet-style"]:not([value="custom"])')) {
      const span = input.closest("label").querySelector(".osf-pet__style-card > span");
      span.dataset.one ||= span.textContent;
      // Only everyday names pluralize ("Pirates"); "Mona Lisa" or "Party Hat" stay as they are.
      const plural = input.closest("label").dataset.theme === "everyday" ? `${span.dataset.one}s` : span.dataset.one;
      span.textContent = group ? GROUP_NAMES[input.value] || plural : span.dataset.one;
    }
    for (const input of this.querySelectorAll('input[name="pet-style"][data-max-pets], input[name="pet-style"][data-skip-pets]')) {
      const label = input.closest("label");
      label.hidden = n > Number(input.dataset.maxPets ?? Infinity) || (input.dataset.skipPets || "").split(",").includes(String(n));
      // A hidden pick moves to the first style that still fits.
      if (label.hidden && input.checked) this.querySelector(".osf-pet__style:not([hidden]) input").checked = true;
    }
    this.showThumbs();
    this.showStage();
  }

  // Prefill the count from a cheap vision check of each photo; the buyer's own count wins.
  async countPhotos() {
    const main = this.photo.files[0]
      ? { file: this.photo.files[0] }
      : this.sourceJob && { source: this.sourceJob };
    const photos = [main && { ...main, crop: this.mainCrop }, ...this.extras].filter(Boolean);
    const hint = this.querySelector("[data-pet-count-hint]");
    const run = (this.countRun = (this.countRun || 0) + 1);
    this.detectedPets = [];
    this.showStage();
    if (!photos.length) return (hint.textContent = "");
    if (!this.canPreview()) return (hint.textContent = "Set how many pets to draw. We count them once you sign in.");
    hint.textContent = "Counting the pets in your photo…";
    try {
      const detections = await Promise.all(photos.map((p) => this.countOne(p)));
      if (run !== this.countRun) return;
      // count null: the check was slow or down; the buyer sets the count.
      if (detections.some((detection) => detection.count == null)) return (hint.textContent = "Set how many pets to draw.");
      const total = detections.reduce((sum, detection) => sum + detection.count, 0);
      this.detectedPets = detections.flatMap((detection) => detection.pets || []);
      if (!total) return (hint.textContent = "We could not spot a pet. Set the count yourself.");
      if (!this.countTouched) this.setCount(total);
      else this.showStage();
      hint.textContent = `We see ${total} pet${total > 1 ? "s" : ""}. Change the count if that is wrong.`;
    } catch {
      if (run === this.countRun) hint.textContent = "Set how many pets to draw.";
    }
  }

  countOne({ file, source, crop = "" }) {
    const key = `${source || `${file.name}:${file.size}:${file.lastModified}`}|${crop}`;
    // Cache the request, not the answer: a restore counts the same photo twice in a row and must send one request.
    if (!(key in this.countCache)) {
      const cached = (this.countCache[key] = (async () => {
        const body = new FormData();
        if (source) body.append("source_job_id", source);
        else body.append("photo", await resize(file), "pet.jpg");
        body.append("crop", crop);
        return this.api("/pet-count", { method: "POST", body });
      })());
      const forget = () => this.countCache[key] === cached && delete this.countCache[key];
      cached.then((detection) => {
        petEvents.queue("pet_count", { count: detection.count, crop });
        if (detection.count == null) forget();
      }, forget);
    }
    return this.countCache[key];
  }

  savedTiles() {
    return [...this.querySelectorAll("[data-pet-saved-row] button[data-job-id]:not(:disabled), [data-pet-saved-row] button[data-local-photo]")];
  }

  startAddingPet() {
    this.addingPet = true;
    const saved = this.querySelector("[data-pet-saved]");
    saved.classList.add("is-adding");
    this.querySelector("[data-pet-saved-adding]").hidden = false;
    saved.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
    this.savedTiles()[0]?.focus({ preventScroll: true });
  }

  stopAddingPet() {
    this.addingPet = false;
    this.querySelector("[data-pet-saved]")?.classList.remove("is-adding");
    const adding = this.querySelector("[data-pet-saved-adding]");
    if (adding) adding.hidden = true;
  }

  // A saved photo, or one uploaded earlier this visit, becomes the next pet's photo; with no photo yet it is pet 1.
  async addSavedPet({ job, local }, via) {
    this.stopAddingPet();
    petEvents.queue("saved_photo_pick", { as: "pet", via });
    const loading = job && this.loadSaved(job);
    loading?.catch(() => {});
    await this.mainLoading; // a pet 1 that is still loading keeps its place
    if (!this.photoUrl) return job ? this.pickSaved(job) : this.pickLocal(local);
    const note = this.querySelector("[data-pet-photo-limit]");
    const taken = job
      ? this.pickedJob() === job || this.extras.some((e) => e.source === job || this.photoJobs?.get(e.file) === job)
      : this.photo.files[0] === local.file || this.extras.some((e) => e.file === local.file);
    if (taken) {
      note.hidden = false;
      note.textContent = "That photo is already one of your pets.";
      return;
    }
    if (this.photoCount() >= MAX_PETS) return this.photoLimit(MAX_PETS + 1);
    let file = local?.file;
    let url = local?.url;
    if (job) {
      try {
        url = await loading;
        const blob = await (await fetch(url)).blob();
        file = new File([blob], "pet.jpg", { type: blob.type || "image/jpeg" });
      } catch {
        return this.savedPhotoFailed();
      }
    }
    this.extras.push({ file, url, crop: "", uid: ++photoUid, source: job });
    this.photoLimit(0);
    this.setCount(sessionStorage.getItem(COUNT_KEY) || this.pets, false);
    this.drawExtras();
    this.orderChanged();
    this.countPhotos();
    this.rememberPhotos();
    this.querySelector("[data-pet-order-live]").textContent = `Added as pet ${this.photoCount()}.`;
  }

  // Mouse or pen: drag a saved tile onto the pet photos to add it, or onto the upload box to use it as pet 1.
  // Touch keeps the swipe for scrolling; there "+" then a tap on the saved photo adds it.
  dragSavedTile(button, img, pick) {
    img.draggable = false;
    button.addEventListener("pointerdown", (down) => {
      if (down.button > 0 || down.pointerType === "touch") return;
      let ghost = null;
      const zones = () => [this.querySelector("[data-pet-extras]"), this.querySelector("[data-pet-drop]")]
        .filter((z) => z && !z.hidden && z.getClientRects().length);
      const under = (ev) => zones().find((z) => { const r = z.getBoundingClientRect();
        return ev.clientX >= r.left && ev.clientX <= r.right && ev.clientY >= r.top && ev.clientY <= r.bottom; });
      const move = (ev) => {
        if (!ghost && Math.hypot(ev.clientX - down.clientX, ev.clientY - down.clientY) < 8) return;
        if (!ghost) {
          ghost = img.cloneNode();
          ghost.className = "osf-pet__saved-ghost";
          document.body.append(ghost);
          button.classList.add("is-dragging");
        }
        ghost.style.translate = `${ev.clientX - 32}px ${ev.clientY - 32}px`;
        const over = under(ev);
        for (const z of zones()) z.classList.toggle("is-over", z === over);
      };
      const up = (ev) => {
        button.removeEventListener("pointermove", move);
        button.removeEventListener("pointerup", up);
        button.removeEventListener("pointercancel", up);
        if (!ghost) return;
        ghost.remove();
        button.classList.remove("is-dragging");
        const over = ev.type === "pointerup" && under(ev);
        for (const z of zones()) z.classList.remove("is-over");
        this.savedDragged = true;
        setTimeout(() => (this.savedDragged = false));
        if (over?.matches("[data-pet-extras]")) this.addSavedPet(pick, "drag");
        else if (over) pick.job ? this.pickSaved(pick.job) : this.pickLocal(pick.local);
      };
      button.setPointerCapture?.(down.pointerId);
      button.addEventListener("pointermove", move);
      button.addEventListener("pointerup", up);
      button.addEventListener("pointercancel", up);
    });
  }

  addExtras(input) {
    const files = [...input.files];
    const room = MAX_PETS - Math.max(1, this.photoCount());
    this.savePhotos(files.slice(0, room));
    for (const file of files.slice(0, room)) this.extras.push({ file, url: URL.createObjectURL(file), crop: "", uid: ++photoUid });
    this.photoLimit(files.length > room ? MAX_PETS + 1 : 0);
    input.value = "";
    this.setCount(sessionStorage.getItem(COUNT_KEY) || this.pets, false);
    this.drawExtras();
    this.orderChanged();
    this.countPhotos();
    this.rememberPhotos();
  }

  // Saved photos are kept by job id; a new upload is kept as its resized JPEG.
  async rememberPhotos() {
    const run = (this.rememberRun = (this.rememberRun || 0) + 1);
    try {
      const file = this.photo.files[0];
      const main = this.sourceJob ? { source: this.sourceJob } : file && { data: await dataUrl(await resize(file)) };
      const extras = await Promise.all(this.extras.map(async (e) => ({ data: await dataUrl(await resize(e.file)), crop: e.crop })));
      if (run !== this.rememberRun) return;
      const kept = { customerId: this.dataset.customerId || "", main: main && { ...main, crop: this.mainCrop }, extras };
      try {
        sessionStorage.setItem(PHOTOS_KEY, JSON.stringify(kept));
      } catch {
        // Over the storage quota: keep the main photo only.
        sessionStorage.setItem(PHOTOS_KEY, JSON.stringify({ ...kept, extras: [] }));
      }
    } catch {
      if (run === this.rememberRun) sessionStorage.removeItem(PHOTOS_KEY);
    }
  }

  // The sign-in link keeps the photos, crops, count, text and picks for the round trip; nothing is uploaded first.
  // The draft id goes in this tab's sessionStorage, so only this tab's return can restore it.
  async signInToPreview(href) {
    track("login_click", { kind: this.dataset.kind });
    const id = crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    sessionStorage.setItem(LOGIN_KEY, id);
    this.status.textContent = "Keeping your photos and picks, then sending you to sign in…";
    try {
      await timeLimit(this.keepDraft(id), 10000);
    } catch (error) {
      track("draft_save_error", { reason: error?.name || "unknown" });
    } finally {
      location.assign(href);
    }
  }

  async keepDraft(id) {
    const main = this.photo.files[0] || null;
    const others = (this.localPhotos || []).map((l) => l.file).filter((f) => f !== main && !this.extras.some((e) => e.file === f));
    const draft = {
      id, at: Date.now(), kind: this.dataset.kind, main: main && await packFile(main), mainCrop: this.mainCrop || "",
      extras: await Promise.all(this.extras.map(async ({ file, crop }) => ({ file: await packFile(file), crop }))),
      others: await Promise.all(others.map(packFile)),
      count: this.countTouched ? this.pets : null, picks: this.picks(() => true),
      consent: this.querySelector("[data-pet-consent]").checked,
      instruction: this.instruction?.value || "", name: this.nameInput.value, prompt: this.promptInput?.value || "",
      calendar: this.calendar && { active: this.calendar.active, styles: this.calendar.styles },
    };
    try {
      await saveDraft(draft);
    } catch (error) {
      if (!draft.others.length) throw error;
      track("draft_save_error", { reason: error?.name || "unknown", retry: true });
      await saveDraft({ ...draft, others: [] }); // over quota: picks, consent and pet photos still fit
    }
  }

  // After sign-in the signed-out draft comes back (photos are uploaded then, with consent); without one, this tab's photos.
  // Only this tab's sign-in return (or its signed-out reload) restores; every other load deletes the draft.
  async restoreDraft(loginId) {
    const draft = loginId ? await loadDraft().catch(() => null) : null;
    if (!draft || draft.id !== loginId || Date.now() - draft.at > DRAFT_TTL_MS || draft.kind !== this.dataset.kind) {
      clearDraft();
      this.restorePhotos(); // not awaited: a saved photo's download must not hold up history
      return;
    }
    if (draft.calendar && this.calendar) {
      if ("signedIn" in this.dataset) await this.token().catch(() => {}); // saved pages are kept per customer token
      Object.assign(this.calendar.styles, draft.calendar.styles);
      this.calendar.active = draft.calendar.active;
      if ("signedIn" in this.dataset) this.saveCalendar();
    }
    this.applyOptions(draft.picks);
    this.querySelector("[data-pet-consent]").checked = draft.consent;
    if (this.instruction) this.instruction.value = draft.instruction;
    if (this.promptInput) this.promptInput.value = draft.prompt;
    this.nameInput.value = draft.name;
    this.nameEdited = !!draft.name;
    const extras = draft.extras.map(({ file, crop }) => ({ file: unpackFile(file), crop }));
    this.savePhotos([...draft.others.map(unpackFile), ...extras.map((e) => e.file)]);
    try {
      this.extras = await Promise.all(extras.map(async ({ file, crop }) => {
        const url = URL.createObjectURL(file);
        return { file, url, crop, uid: ++photoUid, shown: crop ? await cropUrl(url, crop) : undefined };
      }));
      this.drawExtras();
      if (draft.main) {
        const dt = new DataTransfer();
        dt.items.add(unpackFile(draft.main));
        this.photo.files = dt.files;
        await this.showPhoto();
        if (draft.mainCrop) {
          this.mainCrop = draft.mainCrop;
          this.showMainPhoto(await cropUrl(this.photoUrl, this.mainCrop));
        }
      }
    } catch {
      // An unreadable photo: the picks still come back.
    }
    if (draft.count) this.setCount(draft.count);
    this.countTouched = !!draft.count;
    this.countPhotos();
    this.rememberPhotos();
    this.drawCalendar?.();
    this.status.textContent = "signedIn" in this.dataset
      ? "Your photos and picks are back. Make your preview when you are ready."
      : "Your photos and picks are back. Sign in to make your preview.";
    clearDraft(); // the page holds them now; the next sign-in click saves a new draft
    track("draft_restored", { photos: (draft.main ? 1 : 0) + draft.extras.length + draft.others.length });
  }

  forgetPhotos() {
    this.rememberRun = (this.rememberRun || 0) + 1;
    sessionStorage.removeItem(PHOTOS_KEY);
  }

  async restorePhotos() {
    const kept = JSON.parse(sessionStorage.getItem(PHOTOS_KEY) || "null");
    if (kept && kept.customerId !== (this.dataset.customerId || "")) sessionStorage.removeItem(PHOTOS_KEY); // another shopper's
    if (!kept || kept.customerId !== (this.dataset.customerId || "")) return;
    const touched = this.countTouched;
    try {
      this.extras = await Promise.all(kept.extras.map(async ({ data, crop }) => {
        const file = fileFromDataUrl(data);
        const url = URL.createObjectURL(file);
        return { file, url, crop, uid: ++photoUid, shown: crop ? await cropUrl(url, crop) : undefined };
      }));
      this.drawExtras();
      if (kept.main?.data) {
        const dt = new DataTransfer();
        dt.items.add(fileFromDataUrl(kept.main.data));
        this.photo.files = dt.files;
        await this.showPhoto();
      } else if (kept.main?.source) {
        await this.photoFor(kept.main.source);
        this.pickSaved(kept.main.source);
      }
      if (kept.main?.crop) {
        this.mainCrop = kept.main.crop;
        this.showMainPhoto(await cropUrl(this.photoUrl, this.mainCrop));
      }
    } catch {
      // A deleted saved photo or unreadable data: start without it.
    }
    this.countTouched = touched;
    this.countPhotos();
    this.rememberPhotos();
  }

  photoCount() {
    return (this.photoUrl ? 1 : 0) + (this.extras?.length || 0);
  }

  // Numbered tiles, pet 1 at the far left: drag (mouse or touch), arrow keys or the move buttons reorder them.
  drawExtras() {
    const row = this.querySelector("[data-pet-extras-row]");
    const add = row.querySelector(".osf-pet__extra-add");
    const entries = [this.photoUrl && { main: true, url: this.shownPhotoUrl || this.photoUrl }, ...this.extras].filter(Boolean);
    const many = entries.length > 1;
    this.querySelector("[data-pet-order-help]").hidden = !many;
    const tiles = entries.map((e, i) => {
      const n = i + 1;
      const tile = document.createElement("div");
      tile.className = "osf-pet__extra";
      tile.dataset.petOrderTile = String(i);
      const pick = document.createElement("button");
      pick.type = "button";
      pick.className = "osf-pet__extra-pick";
      pick.setAttribute("aria-label", many ? `Pet ${n} of ${entries.length}. Crop this photo; arrow keys move it` : `Crop the photo of pet ${n}`);
      const img = document.createElement("img");
      img.src = e.shown || e.url;
      img.alt = "";
      img.draggable = false;
      const badge = document.createElement("span");
      badge.className = "osf-pet__extra-num";
      badge.textContent = String(n);
      badge.setAttribute("aria-hidden", "true");
      pick.append(img, badge);
      pick.addEventListener("click", () => {
        if (this.orderDragged) return;
        if (e.main) return this.querySelector("[data-pet-crop-open]").click();
        this.openCrop({ url: e.url, crop: e.crop, apply: (c) => (e.crop = c), show: (u) => (img.src = e.shown = u) });
      });
      pick.addEventListener("keydown", (ev) => {
        const step = { ArrowLeft: -1, ArrowRight: 1 }[ev.key];
        if (!step || !many) return;
        ev.preventDefault();
        this.movePhoto(i, i + step);
      });
      tile.append(pick);
      if (!e.main) {
        const remove = document.createElement("button");
        remove.type = "button";
        remove.className = "osf-pet__extra-remove";
        remove.textContent = "×";
        remove.setAttribute("aria-label", `Remove the photo of pet ${n}`);
        remove.addEventListener("click", () => {
          this.extras.splice(i - 1, 1);
          this.drawExtras();
          this.setCount(sessionStorage.getItem(COUNT_KEY) || this.pets, false);
          this.countPhotos();
          this.rememberPhotos();
          this.orderChanged();
        });
        tile.append(remove);
      }
      if (many) {
        const moves = document.createElement("span");
        moves.className = "osf-pet__extra-moves";
        for (const [step, text, word] of [[-1, "‹", "left"], [1, "›", "right"]]) {
          const b = document.createElement("button");
          b.type = "button";
          b.textContent = text;
          b.dataset.petMove = word;
          b.disabled = !entries[i + step];
          b.setAttribute("aria-label", `Move pet ${n} ${word}`);
          b.addEventListener("click", () => this.movePhoto(i, i + step));
          moves.append(b);
        }
        tile.append(moves);
        this.dragTile(tile, pick, i);
      }
      return tile;
    });
    row.replaceChildren(...tiles, add);
    this.querySelector("[data-pet-extras]").hidden = !this.photoUrl && this.pets < 2 && !this.mainLoading;
  }

  // Pointer events cover mouse, touch and pen; a short move is still a tap (crop).
  dragTile(tile, handle, from) {
    handle.addEventListener("pointerdown", (down) => {
      if (down.button > 0) return;
      const row = tile.parentElement;
      let to = from, moved = false;
      this.orderDragged = false;
      const move = (ev) => {
        if (!moved && Math.hypot(ev.clientX - down.clientX, ev.clientY - down.clientY) < 8) return;
        moved = true;
        tile.classList.add("is-dragging");
        tile.style.translate = `${ev.clientX - down.clientX}px ${ev.clientY - down.clientY}px`;
        const tiles = [...row.querySelectorAll("[data-pet-order-tile]")];
        const over = tiles.findIndex((t) => { const r = t.getBoundingClientRect();
          return ev.clientX >= r.left && ev.clientX <= r.right && ev.clientY >= r.top - 20 && ev.clientY <= r.bottom + 20; });
        if (over >= 0) to = over;
        tiles.forEach((t, k) => t.classList.toggle("is-drop", moved && k === to && k !== from));
      };
      const up = () => {
        handle.removeEventListener("pointermove", move);
        handle.removeEventListener("pointerup", up);
        handle.removeEventListener("pointercancel", up);
        tile.style.translate = "";
        tile.classList.remove("is-dragging");
        if (!moved) return;
        this.orderDragged = true;
        setTimeout(() => (this.orderDragged = false));
        if (to !== from) this.movePhoto(from, to);
        else this.drawExtras();
      };
      handle.setPointerCapture?.(down.pointerId);
      handle.addEventListener("pointermove", move);
      handle.addEventListener("pointerup", up);
      handle.addEventListener("pointercancel", up);
    });
  }

  // Moves one photo to a new left-to-right slot; slot 1 is the main photo the processor gets first.
  async movePhoto(from, to) {
    const count = this.photoCount();
    if (to < 0 || to >= count || from === to || !this.photoUrl) return;
    let main = { file: this.photo.files[0], url: this.photoUrl, crop: this.mainCrop || "", shown: this.shownPhotoUrl, uid: this.mainUid };
    if (!main.file) {
      // A saved photo has no local file; fetch it once so it can sit in any slot.
      const blob = await (await fetch(this.photoUrl)).blob();
      main.file = new File([blob], "pet.jpg", { type: blob.type || "image/jpeg" });
    }
    const list = [main, ...this.extras];
    list.splice(to, 0, ...list.splice(from, 1));
    [main] = list;
    this.extras = list.slice(1);
    const dt = new DataTransfer();
    dt.items.add(main.file);
    this.pickSaved(null);
    this.photo.files = dt.files;
    this.photoUrl = main.url;
    this.mainCrop = main.crop;
    this.mainUid = main.uid;
    this.showMainPhoto(main.shown || main.url);
    this.querySelector("[data-pet-crop]").hidden = true;
    this.reorderNames(from, to, count);
    this.rememberPhotos();
    this.querySelector(`[data-pet-order-tile="${to}"] .osf-pet__extra-pick`)?.focus();
    this.querySelector("[data-pet-order-live]").textContent = `Pet ${from + 1} is now pet ${to + 1} of ${count}.`;
    this.orderChanged();
  }

  // Names typed one per photo move with their pet.
  reorderNames(from, to, count) {
    const names = splitNames(this.petName());
    if (names.length !== count) return;
    names.splice(to, 0, ...names.splice(from, 1));
    this.nameInput.value = joinNames(names);
    this.nameEdited = true;
    this.nameCleared = false;
    this.nameInput.dispatchEvent(new Event("input", { bubbles: true }));
  }

  photoOrder() {
    return [this.mainUid, ...this.extras.map((e) => e.uid)];
  }

  // A shown preview of the same photos in another order offers a redo in the new order.
  orderChanged() {
    const shown = this.orderOf?.[this.renderInput.value];
    const now = this.photoOrder();
    const order = shown && shown.length === now.length && now.every((u) => shown.includes(u))
      ? now.map((u) => shown.indexOf(u) + 1) : null;
    this.redoOrder = order && order.some((k, i) => k !== i + 1) ? order.join(",") : "";
    this.querySelector("[data-pet-order-redo]").hidden = !this.redoOrder;
  }

  // Square crop box over the whole photo: drag a corner to resize, inside to move.
  // Sends "x,y,size" fractions (pet_portrait.crop_photo).
  setupCrop() {
    const view = this.querySelector("[data-pet-crop-view]");
    this.querySelector("[data-pet-crop-open]").addEventListener("click", () =>
      this.openCrop({ url: this.photoUrl, crop: this.mainCrop, apply: (c) => (this.mainCrop = c) }),
    );
    let drag = null;
    const point = (e) => {
      const r = view.getBoundingClientRect();
      const k = this.cropState.w / r.width;
      return { x: (e.clientX - r.left) * k, y: (e.clientY - r.top) * k };
    };
    view.addEventListener("pointerdown", (e) => {
      const st = this.cropState;
      if (!st) return;
      const p = point(e);
      const corner = e.target.dataset.corner;
      if (corner) {
        // The opposite corner stays put; sx/sy point from it towards the dragged one.
        const sx = corner.includes("e") ? 1 : -1;
        const sy = corner.includes("s") ? 1 : -1;
        drag = { sx, sy, ax: sx > 0 ? st.x : st.x + st.s, ay: sy > 0 ? st.y : st.y + st.s };
      } else if (p.x >= st.x && p.x <= st.x + st.s && p.y >= st.y && p.y <= st.y + st.s) {
        drag = { dx: p.x - st.x, dy: p.y - st.y };
      } else return;
      e.preventDefault();
      view.setPointerCapture(e.pointerId);
    });
    view.addEventListener("pointermove", (e) => {
      if (!drag) return;
      const st = this.cropState;
      const p = point(e);
      if (drag.sx) {
        const room = Math.min(drag.sx > 0 ? st.w - drag.ax : drag.ax, drag.sy > 0 ? st.h - drag.ay : drag.ay);
        st.s = Math.min(room, Math.max(st.min, (p.x - drag.ax) * drag.sx, (p.y - drag.ay) * drag.sy));
        st.x = drag.sx > 0 ? drag.ax : drag.ax - st.s;
        st.y = drag.sy > 0 ? drag.ay : drag.ay - st.s;
      } else {
        st.x = Math.min(st.w - st.s, Math.max(0, p.x - drag.dx));
        st.y = Math.min(st.h - st.s, Math.max(0, p.y - drag.dy));
      }
      this.drawCrop();
    });
    for (const t of ["pointerup", "pointercancel"]) view.addEventListener(t, () => (drag = null));
    this.querySelector("[data-pet-crop-reset]").addEventListener("click", () => this.closeCrop(""));
    this.querySelector("[data-pet-crop-cancel]").addEventListener("click", () => {
      this.querySelector("[data-pet-crop]").hidden = true;
    });
    this.querySelector("[data-pet-crop-done]").addEventListener("click", () => {
      const st = this.cropState;
      const f = (v) => Math.max(0, v).toFixed(4);
      this.closeCrop(`${f(st.x / st.w)},${f(st.y / st.h)},${f(st.s / Math.min(st.w, st.h))}`);
    });
  }

  openCrop(target) {
    const box = this.querySelector("[data-pet-crop]");
    const img = this.querySelector("[data-pet-crop-img]");
    this.cropTarget = target;
    this.cropState = null;
    img.onload = () => {
      const w = img.naturalWidth;
      const h = img.naturalHeight;
      const short = Math.min(w, h);
      // 10% of the short side, but at least the server minimum once the upload is resized to MAX_PX.
      const min = Math.min(short, Math.max(short * 0.1, CROP_MIN_PX / Math.min(1, MAX_PX / Math.max(w, h))));
      const [x, y, size] = (target.crop || "").split(",").map(Number);
      const s = target.crop ? Math.max(min, size * short) : short;
      this.cropState = {
        w, h, min, s,
        x: target.crop ? Math.min(w - s, x * w) : (w - s) / 2,
        y: target.crop ? Math.min(h - s, y * h) : (h - s) / 2,
      };
      // Tall photos stay within the screen.
      this.querySelector("[data-pet-crop-view]").style.width = `min(100%, 360px, ${(60 * w) / h}vh)`;
      this.drawCrop();
    };
    img.src = target.url;
    box.hidden = false;
    box.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }

  drawCrop() {
    const st = this.cropState;
    const style = this.querySelector("[data-pet-crop-box]").style;
    style.left = `${(100 * st.x) / st.w}%`;
    style.top = `${(100 * st.y) / st.h}%`;
    style.width = `${(100 * st.s) / st.w}%`;
    style.height = `${(100 * st.s) / st.h}%`;
  }

  async closeCrop(value) {
    const target = this.cropTarget;
    target.apply(value);
    this.querySelector("[data-pet-crop]").hidden = true;
    this.countPhotos();
    this.rememberPhotos();
    const url = value ? await cropUrl(target.url, value) : target.url;
    if (target.show) target.show(url);
    else this.showMainPhoto(url);
  }

  // The thumbnail, stage inset and crop button show the photo as it will be sent.
  showMainPhoto(url) {
    this.shownPhotoUrl = url;
    // Returning buyers have a preview picked; show the new or cropped photo on the style example instead.
    this.showExample = true;
    const thumb = this.querySelector("[data-pet-photo-thumb]");
    thumb.src = url;
    thumb.hidden = false;
    this.querySelector(".osf-pet__file-icon").setAttribute("hidden", "");
    this.querySelector("[data-pet-crop-label]").textContent = this.mainCrop ? "Edit crop" : "Crop photo";
    this.querySelector("[data-pet-crop-note]").textContent = this.mainCrop
      ? "Cropped. Only the square shown is used."
      : "Crop to your pet for a closer portrait.";
    this.drawExtras();
    this.showStage();
  }

  // A new main photo: whole photo, fresh auto count.
  photoChanged() {
    this.mainCrop = "";
    this.countTouched = false;
    this.querySelector("[data-pet-crop]").hidden = true;
    this.querySelector("[data-pet-photo-tools]").hidden = false;
    this.mainUid = ++photoUid;
    this.setCount(sessionStorage.getItem(COUNT_KEY) || this.pets, false);
    this.countPhotos();
    this.rememberPhotos();
    this.showMainPhoto(this.photoUrl);
    this.calendarPhotoChanged();
  }

  // Several files picked at once become pets 1..n in pick order, up to MAX_PETS.
  takeMany(files) {
    const kept = files.slice(0, MAX_PETS);
    this.extras = kept.slice(1).map((file) => ({ file, url: URL.createObjectURL(file), crop: "", uid: ++photoUid }));
    const dt = new DataTransfer();
    dt.items.add(kept[0]);
    this.photo.files = dt.files;
    this.photoLimit(files.length);
    this.drawExtras();
    this.savePhotos(kept.slice(1));
  }

  // Every upload joins the saved photos before any preview, so changing a photo keeps the old one. The consent
  // box is what lets us keep photos, so uploads wait for it. The server merges the same picture into one tile.
  // Until then each upload shows at once as a local tile, so picking another saved photo never hides it.
  savePhotos(files) {
    (this.unsavedPhotos ||= []).push(...files);
    (this.localPhotos ||= []).unshift(...[...files].reverse().map((file) => ({ file, url: URL.createObjectURL(file) })));
    this.drawSaved();
    this.flushPhotos();
  }

  async flushPhotos() {
    if (!this.unsavedPhotos?.length || !this.querySelector("[data-pet-consent]").checked || !("signedIn" in this.dataset)) return;
    const files = this.unsavedPhotos.splice(0);
    this.photoJobs ||= new Map(); // uploaded file -> its saved photo's job id
    // One at a time, so the newest upload is the newest saved photo.
    for (const file of files) {
      const body = new FormData();
      body.append("photo", await resize(file), "pet.jpg");
      body.append("consent", "true");
      const saved = await this.api("/pet-photo", { method: "POST", body }).catch(() => null);
      const local = this.localPhotos.find((l) => l.file === file);
      if (!saved?.job_id || !local) continue; // a failed save keeps its local tile for this visit
      this.photoJobs.set(file, saved.job_id);
      this.photos[saved.job_id] ||= local.url;
      this.localPhotos.splice(this.localPhotos.indexOf(local), 1);
    }
    this.drawSaved();
    this.calendarPhotoChanged(); // the open page's new upload now has a saved id
  }

  photoLimit(picked) {
    const note = this.querySelector("[data-pet-photo-limit]");
    note.hidden = picked <= MAX_PETS;
    note.textContent = note.hidden ? "" : `Up to ${MAX_PETS} pets fit in one design, so only the first ${MAX_PETS} photos were kept.`;
  }

  async showPhoto() {
    if (this.photo.files.length > 1) this.takeMany([...this.photo.files]);
    else this.photoLimit(0);
    let file = this.photo.files[0];
    if (!file) return;
    const run = this.photoPrepareRun = (this.photoPrepareRun || 0) + 1;
    this.preparing = true;
    this.setRemaining(this.remaining);
    this.status.textContent = "Getting your photo ready…";
    try {
      try { await resize(file); } catch {
        file = await this.convertPhoto(file);
        await resize(file);
      }
      this.savePhotos([file]); // also when a newer photo replaced this one while it was getting ready
      if (run !== this.photoPrepareRun) return;
      this.sourceJob = null; // the new upload replaces a picked saved photo
      const dt = new DataTransfer();
      dt.items.add(file);
      this.photo.files = dt.files;
      if (this.photoUrl?.startsWith("blob:") && !Object.values(this.photos).includes(this.photoUrl)
          && !Object.values(this.cropped).includes(this.photoUrl) && this.pendingJob?.photoUrl !== this.photoUrl)
        URL.revokeObjectURL(this.photoUrl);
      this.photoUrl = URL.createObjectURL(file);
      this.status.textContent = "";
      this.querySelector("[data-pet-file-label]").textContent = "Change photo";
      track("photo_added", { photo_type: file.type, photo_kb: Math.round(file.size / 1024),
                             ms: this.photoStart ? Math.round(performance.now() - this.photoStart) : undefined });
      this.photoChanged();
    } catch (error) {
      if (run !== this.photoPrepareRun) return;
      this.status.textContent = `${error.message} Choose another photo or try again.`;
      track("photo_prepare_error", { reason: error.name, photo_type: file.type || "unknown", photo_kb: Math.round(file.size / 1024) });
    } finally {
      if (run === this.photoPrepareRun) {
        this.preparing = false;
        this.setRemaining(this.remaining);
      }
    }
  }

  // Chrome cannot decode HEIC (iPhone photos): the processor turns it into a JPEG that replaces the pick.
  async convertPhoto(file) {
    const label = this.querySelector("[data-pet-file-label]");
    label.textContent = "Getting your photo ready…";
    if (!this.canPreview())
      throw new Error("This photo format opens after you sign in. Choose a JPEG or PNG, or sign in first.");
    if (file.size > 10 * 1024 * 1024)
      throw new Error("This photo format needs conversion and is over 10 MB. Export it as JPEG or choose a smaller photo.");
    const body = new FormData();
    body.append("photo", file, "pet-upload");
    const res = await this.request("/pet-photo-convert", { method: "POST", body });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "We could not read that photo.");
    const jpeg = new File([await res.blob()], file.name.replace(/\.\w+$/, "") + ".jpg", { type: "image/jpeg" });
    track("photo_converted", { from: file.type || "unknown" });
    return jpeg;
  }

  syncApparel() {
    const kind = this.dataset.kind === "tee" ? "shirt" : this.dataset.kind;
    this.syncKnitPicks();
    const apparel = kind in APPAREL_AREAS;
    const goods = GOODS[kind];
    const hoodie = kind === "hoodie";
    const hoodieOptions = this.querySelector("[data-pet-hoodie-options]");
    const wasChest = this.chestPrint;
    this.chestPrint = hoodie && this.apparelVariants[this.dataset.selectedVariant]?.[2] === "Yes (+$15)";
    // A lifestyle photo would hide the new front/back or chest-print preview.
    if (wasChest !== this.chestPrint) lifestyleReset(this);
    // Only the chest print goes on the front: picking it shows the front, dropping it shows the back.
    if (wasChest !== this.chestPrint || !this.chestPrint) this.hoodieView = this.chestPrint ? "front" : "back";
    const back = hoodie && this.hoodieView === "back";
    hoodieOptions.hidden = !hoodie;
    hoodieOptions.querySelectorAll("input").forEach((input) => {
      input.disabled = !hoodie;
      if (input.name === "properties[Chest print]") input.value = this.chestPrint ? "On" : "Off";
    });
    this.querySelector("[data-pet-hoodie-views]").hidden = !this.chestPrint;
    this.querySelectorAll("[data-pet-hoodie-view]").forEach((button) => button.setAttribute("aria-pressed", String(button.dataset.petHoodieView === this.hoodieView)));
    const controls = this.querySelector("[data-pet-print-sizes]");
    controls.hidden = !apparel || hoodie;
    controls.querySelectorAll("input").forEach((input) => {
      input.disabled = !apparel || hoodie;
      input.checked = PRINT_SIZE_LABELS[input.value] === this.printSize;
    });
    const options = this.apparelVariants[this.dataset.selectedVariant] || [];
    const areas = back ? HOODIE_BACK_AREAS : APPAREL_AREAS[kind];
    this.apparelArea = apparel ? areas[options.find((option) => option in areas)] : null;
    const garment = this.querySelector("[data-pet-garment]");
    garment.hidden = !apparel && !goods;
    if (apparel || goods) {
      const src = kind === "stocking" ? this.stockingPattern() : kind === "apron" ? this.apronPattern() : garment.dataset[back ? "hoodieBackSrc" : `${kind}Src`];
      if (garment.getAttribute("src") !== src) garment.src = src;
    }
    this.syncGarmentColor();
    const stage = this.querySelector("[data-pet-stage]");
    stage.style.setProperty("--pet-front-left", back ? "33.5%" : "30%");
    stage.style.setProperty(
      "--pet-front-top",
      back ? "31%" : hoodie ? "33%" : "25%",
    );
    stage.style.setProperty("--pet-front-width", back ? "33%" : "40%");
    if (this.apparelArea)
      stage.style.setProperty(
        "--pet-front-ratio",
        `${this.apparelArea[0]} / ${this.apparelArea[1]}`,
      );
    if (goods) {
      ["left", "top", "width"].forEach((name, i) => stage.style.setProperty(`--pet-front-${name}`, `${goods.rect[i] * 100}%`));
      stage.style.setProperty("--pet-front-ratio", `${goods.area[0]} / ${goods.area[1]}`);
    }
    this.showStage();
  }

  // Knit Design shows on portrait layouts (pet rows knit the pets into the Christmas rows); Palette only on Christmas
  // Classic. Disabled inputs keep hidden picks off the cart line while the hidden rows keep the prior pick.
  syncKnitPicks() {
    const designs = this.querySelector("[data-pet-stocking-designs]");
    if (!designs) return;
    const portrait = this.knitLayout() === "portrait";
    const classic = !portrait || designs.querySelector("input:checked")?.value === "Christmas Classic";
    for (const [row, shown] of [[designs, portrait], [this.querySelector("[data-pet-stocking-palettes]"), classic]]) {
      row.hidden = !shown;
      row.querySelectorAll("input").forEach((input) => { input.disabled = !shown; });
    }
  }

  // The sock knit tile for a layout: "layout-pick" when the shopper picked a Design (portrait) or Palette.
  knitTileKey(layout) {
    if (this.dataset.kind !== "socks") return layout;
    const key = (name) => this.querySelector(`input[name="properties[${name}]"]:checked`)?.dataset.knitKey;
    const pick = (layout === "portrait" && key("Design")) || key("Palette");
    return pick ? `${layout}-${pick}` : layout;
  }

  // The stocking's pattern photo for the Design pick, or the Palette pick on Christmas Classic.
  stockingPattern() {
    const pick = this.querySelector("[data-pet-stocking-palettes] input:checked:enabled") || this.querySelector("[data-pet-stocking-designs] input:checked");
    return pick?.dataset.patternSrc || this.querySelector("[data-pet-garment]").dataset.stockingSrc;
  }

  // A scene restored from an older preview belongs to that style; the apron pattern follows the new style.
  styleChanged(style) {
    if (this.instruction && this.instruction.value === this.restoredInstruction) this.instruction.value = "";
    this.countInstruction();
    this.matchApronPattern(style);
  }

  // The service refused the scene text: say why beside the box. No job started and no preview was used.
  sceneError(message) {
    const error = this.querySelector("[data-pet-instruction-error]");
    if (!error || !this.instruction) return;
    error.textContent = message;
    error.hidden = !message;
    if (message) this.instruction.setAttribute("aria-invalid", "true");
    else this.instruction.removeAttribute("aria-invalid");
  }

  countInstruction() {
    const count = this.querySelector("[data-pet-instruction-count]");
    if (count && this.instruction) count.textContent = this.instruction.value.length;
  }

  // Holiday styles pick the matching apron pattern until the buyer picks one.
  matchApronPattern(style) {
    if (this.apronPicked) return;
    const value = APRON_THEME_PATTERNS[style.closest("label")?.dataset.theme] || "Grill Master";
    const input = this.querySelector(`[data-pet-apron-designs] input[value="${value}"]`);
    if (!input || input.checked) return;
    input.checked = true;
    filterLifestyle(this);
    this.syncApparel();
  }

  // The apron's pattern photo for the Design pick; Grill Master is the garment's own photo.
  apronPattern() {
    const design = this.querySelector('[data-pet-apron-designs] input:checked');
    return design?.dataset.patternSrc || this.querySelector("[data-pet-garment]").dataset.apronSrc;
  }

  // Tints the blank to the selected variant's color; waits for the blank's load event.
  syncGarmentColor() {
    const garment = this.querySelector("[data-pet-garment]");
    const tint = this.querySelector("[data-pet-garment-tint]");
    const color = (this.apparelVariants[this.dataset.selectedVariant] || [])[0]?.toLowerCase();
    const hex = !garment.hidden && GARMENT_COLORS[this.dataset.kind]?.[color];
    const ready = hex && garment.complete && garment.naturalWidth && garment.currentSrc === garment.src;
    const key = ready ? `${garment.src}|${hex}` : "";
    tint.hidden = !ready;
    tint.dataset.color = ready ? color : "";
    if (ready && tint.dataset.key !== key) tintGarment(tint, garment, hex);
    tint.dataset.key = key;
  }

  // Fetched as blobs: <img> requests let the CDN re-encode PNG data maps as lossy WebP, scrambling u, v.
  knitMapsFor(urls) {
    if (this.knitMaps?.url === urls.uv) return this.knitMaps.maps;
    const entry = this.knitMaps = { url: urls.uv, maps: undefined };
    entry.ready = Promise.all(['white', 'black', 'uv', 'mask'].map(async (name) => {
      const response = await fetch(urls[name]);
      if (!response.ok) throw new Error(`Knit mockup ${name} failed`);
      return createImageBitmap(await response.blob(), { colorSpaceConversion: 'none', premultiplyAlpha: 'none' });
    })).then(([white, black, uv, mask]) => {
      entry.maps = { side: uv.width, tone: urls.tone, white: pixels(white), black: pixels(black), uv: pixels(uv), mask: pixels(mask) };
    }, () => { entry.maps = null; }).then(() => { if (this.knitMaps === entry) this.renderMockup(); });
    return undefined;
  }

  // Loaded and decoded once per URL; `ready` settles either way, callers check naturalWidth.
  knitImage(url) {
    let img = this.knitTiles.get(url);
    if (!img) {
      img = new Image();
      img.crossOrigin = "anonymous";
      img.src = url;
      img.ready = img.decode().catch(() => {});
      this.knitTiles.set(url, img);
    }
    return img;
  }

  // Renders again once a still-loading image settles.
  renderWhenReady(img) {
    const lifecycle = this.previewLifecycle;
    img.rerender ||= img.ready.then(() => {
      if (this.isConnected && this.previewLifecycle === lifecycle)
        this.renderMockup();
    });
  }

  knitLayout() {
    if (this.querySelector('input[name="properties[Layout]"]:checked')?.value !== 'Pet row') return 'portrait';
    const snow = this.querySelector('input[name="properties[Filler]"]:checked')?.value === 'Snowflakes';
    const petFirst = this.querySelector('input[name="properties[Row order]"]:checked')?.value !== 'Tree/snowflake row first';
    return { true: { true: 'petrow_snowflakes_top', false: 'petrow_snowflakes' }, false: { true: 'petrow', false: 'petrow_trees_below' } }[snow][petFirst];
  }

  // Until the shopper picks a row order, the filler sets it as before: trees keep pets on top, snowflakes below.
  syncRowOrder(filler) {
    const order = filler === 'Snowflakes' ? 'Tree/snowflake row first' : 'Pet row first';
    this.querySelector(`input[name="properties[Row order]"][value="${order}"]`).checked = true;
  }

  // The current mockup stays on screen until the next layout is drawn, then crossfades out.
  async switchKnitLayout() {
    const stage = this.querySelector("[data-pet-stage]");
    const canvas = this.querySelector("[data-pet-mockup]");
    const run = this.layoutSwitch = {};
    const fillers = this.querySelector("[data-pet-fillers]");
    this.syncKnitPicks();
    fillers.hidden = this.knitLayout() === "portrait";
    for (const input of fillers.querySelectorAll("input")) input.disabled = fillers.hidden;
    stage.querySelector(".osf-pet__hold")?.remove();
    if (stage.dataset.renderState === "ready" && !canvas.hidden) {
      const hold = document.createElement("canvas");
      hold.className = "osf-pet__hold";
      hold.setAttribute("aria-hidden", "true");
      hold.width = canvas.width;
      hold.height = canvas.height;
      hold.getContext("2d").drawImage(canvas, 0, 0);
      canvas.after(hold);
    }
    this.layoutSpinner ||= slowSpinner(stage);
    const tile = this.knitImage(this.knitData[this.dataset.kind].tiles[this.knitTileKey(this.knitLayout())]);
    this.knitMapsFor(this.knitData.mockups);
    await Promise.all([tile.ready, this.knitMaps.ready]);
    if (run !== this.layoutSwitch) return;
    // After the tile: the new layout's scene photos would share a slow network with it.
    filterLifestyle(this);
    preloadLifestyle(this);
    const stopSpinner = this.layoutSpinner;
    this.layoutSpinner = null;
    await stopSpinner();
    if (run !== this.layoutSwitch) return;
    this.renderMockup();
    const hold = stage.querySelector(".osf-pet__hold");
    if (!hold) return;
    if (matchMedia("(prefers-reduced-motion: reduce)").matches) return hold.remove();
    hold.getBoundingClientRect(); // Commits opacity 1 so the fade runs even when the switch was instant.
    hold.style.opacity = "0";
    setTimeout(() => hold.remove(), 300);
  }

  // After the first knit render, fetch every layout's tile in the current picks so later switches are instant.
  preloadKnitLayouts() {
    const tiles = this.knitData?.[this.dataset.kind]?.tiles;
    if (!tiles) return;
    for (const layout of Object.keys(tiles).filter((key) => !key.includes("-"))) this.knitImage(tiles[this.knitTileKey(layout)]);
  }

  // Picking the Original look then paints at once instead of queuing behind the style thumbnails.
  warmOriginalExample() {
    const example = this.querySelector("[data-pet-original]")?.dataset.example;
    if (!example || this.originalExample) return;
    this.originalExample = new Image();
    this.originalExample.crossOrigin = "anonymous";
    this.originalExample.src = example;
  }

  setRenderState(state) {
    const stage = this.querySelector("[data-pet-stage]");
    stage.dataset.renderState = state;
    // A stage still loading after 10 s reports once per page, with what it was showing.
    if (state !== "loading") this.stuckTimer = clearTimeout(this.stuckTimer);
    else this.stuckTimer ||= setTimeout(() => stage.dataset.renderState === "loading" && petPerf.stall("stage_loading", {
      stage: { kind: this.dataset.kind, look: this.querySelector('input[name="pet-look"]:checked')?.value ?? null,
        style: this.querySelector('input[name="pet-style"]:checked')?.value ?? null, example: !!this.showExample,
        art_complete: !!this.querySelector("[data-pet-stage-art]")?.complete } }), 10000);
    if (state === "ready") {
      afterLoad(() => {
        preloadLifestyle(this);
        this.warmOriginalExample();
        this.preloadKnitLayouts();
      });
    }
    this.querySelector("[data-pet-render-error]").hidden = state !== "error";
    this.zoomButton.setAttribute("aria-disabled", String(state !== "ready"));
    if (state !== "ready") this.zoomScene.replaceChildren();
    this.zoomStatus.textContent =
      state === "error"
        ? "Preview unavailable. Try another preview."
        : state === "loading"
          ? "Loading preview…"
          : "";
    if (state === "ready") { this.stageLoaded();
      if (this.zoomDialog.open) this.updateZoom();
    }
  }

  // A history row shows its tile image first; the mockup waits for the full preview and its mask pair. A row with a
  // public preview does not wait: the plain picture shows now and the mockup replaces it when the bytes arrive.
  awaitingBytes() {
    const preview = this.shownPreview();
    return this.outputKind === "portrait" && !this.showExample && !!preview && !preview.preview_data_url
      && !preview.preview_url && !preview.hydrateFailed;
  }

  customerPreview() {
    const stage = this.querySelector("[data-pet-stage]");
    if (
      this.outputKind !== "portrait" ||
      this.showExample ||
      stage.classList.contains("is-photo")
    )
      return null;
    const preview = this.shownPreview();
    if (!preview) return null;
    const image = this.querySelector("[data-pet-stage-art]");
    if (image.dataset.recolored) return null; // the browser's ring redraw has no verified mask pair
    const mode =
      this.dataset.kind === "hoodie" && preview.name
        ? `hoodie-${this.hoodieView}`
        : "standard";
    const hoodie = mode !== "standard";
    const hoodieSrc = hoodie && (this.hoodieView === "front" ? preview.portrait_preview_data_url : preview.hoodie_back_preview_data_url);
    const maskUrl = hoodie ? preview.hoodie_preview_masks?.[this.hoodieView] : preview.preview_mask_data_url;
    // A hoodie view is drawn and cut together on the server; its cut must match the drawn picture's size.
    const pair = hoodie ? { width: image.naturalWidth, height: image.naturalHeight } : preview.verified_preview_pair;
    const verified = hoodie
      ? !!hoodieSrc && image.src === hoodieSrc && pair.width > 0 && pair.height > 0 && /^data:image\/png;base64,[A-Za-z0-9+/]+=*$/.test(maskUrl || "")
      : mode === "standard" &&
      !!preview.preview_data_url &&
      image.src === preview.preview_data_url &&
      !!pair &&
      pair.render_id === preview.job_id &&
      pair.display_mode === mode &&
      typeof pair.recipe === "string" &&
      pair.recipe.length > 0 &&
      /^[0-9a-f]{64}$/.test(pair.identity) &&
      Number.isInteger(pair.width) &&
      Number.isInteger(pair.height) &&
      pair.width > 0 &&
      pair.height > 0 &&
      pair.width === image.naturalWidth &&
      pair.height === image.naturalHeight &&
      /^data:image\/png;base64,[A-Za-z0-9+/]+=*$/.test(
        maskUrl,
      );
    const mask = verified
      ? this.knitImage(maskUrl)
      : null;
    const usable =
      !!mask &&
      mask.complete &&
      mask.naturalWidth === pair.width &&
      mask.naturalHeight === pair.height;
    const key = JSON.stringify([
      preview.job_id,
      mode,
      image.src,
      pair?.recipe,
      pair?.identity,
      verified ? maskUrl : null,
      mask?.complete,
      mask?.naturalWidth,
      mask?.naturalHeight,
    ]);
    return { mask, usable, key };
  }

  originalPreview() {
    const stage = this.querySelector("[data-pet-stage]");
    this.cutout = this.mockupSource = null;
    this.layoutSwitch = null;
    this.layoutSpinner?.();
    this.layoutSpinner = null;
    stage.querySelector(".osf-pet__hold")?.remove();
    stage.classList.remove("has-mockup");
    stage.classList.add("is-original-preview");
    this.querySelector("[data-pet-mockup]").hidden = true;
    return true;
  }

  renderMockup() {
    if (!this.isConnected) return;
    this.setRenderState("loading");
    if (this.formatRequest) return;
    const stage = this.querySelector("[data-pet-stage]");
    const art = this.querySelector("[data-pet-stage-art]");
    const garment = this.querySelector("[data-pet-garment]");
    if (!art.complete || art.currentSrc !== art.src) return;
    if (!art.naturalWidth || !art.naturalHeight)
      return this.setRenderState("error");
    if (this.awaitingBytes()) return; // hydrate() redraws the stage once the full preview arrives
    const customer = this.customerPreview();
    stage.classList.remove("is-original-preview");
    if (customer && this.mockupSource !== customer.key) {
      this.cutout = this.mockupSource = null;
      stage.querySelector(".osf-pet__hold")?.remove();
    }
    if (customer?.mask && !customer.mask.complete) {
      this.renderWhenReady(customer.mask);
      return;
    }
    if (customer && !customer.usable) {
      this.originalPreview();
      return this.setRenderState("ready");
    }
    this.renderFailed = false;
    const ready = this.composeMockup(customer);
    if (this.renderFailed) return this.setRenderState("error");
    if (!ready) return;
    if (stage.classList.contains("is-original-preview"))
      return this.setRenderState("ready");
    if (!stage.classList.contains("is-photo") && !garment.hidden) {
      if (!garment.complete || garment.currentSrc !== garment.src) return;
      if (!garment.naturalWidth) return this.setRenderState("error");
    }
    const background = getComputedStyle(stage).backgroundImage.match(
      /url\(["']?(.*?)["']?\)/,
    )?.[1];
    if (background && !stage.classList.contains("is-photo")) {
      const image = this.knitImage(background);
      if (!image.complete) return this.renderWhenReady(image);
      if (!image.naturalWidth) return this.setRenderState("error");
    }
    this.setRenderState("ready");
  }

  composeMockup(customer = this.customerPreview()) {
    const stage = this.querySelector("[data-pet-stage]");
    const image = this.querySelector("[data-pet-stage-art]");
    const canvas = this.querySelector("[data-pet-mockup]");
    stage.classList.remove("has-mockup", "is-original-preview");
    canvas.hidden = true;
    if (stage.classList.contains("is-photo") || this.outputKind !== "portrait")
      return true;
    if (customer?.mask && !customer.mask.complete) { this.renderWhenReady(customer.mask); return false; }
    if (customer && !customer.usable) return this.originalPreview();
    // Before a preview, the Original look shows the buyer's photo (or the example) whole, with no peel.
    const photo = !customer && this.querySelector('input[name="pet-look"]:checked')?.value === "original";
    const key = customer ? customer.key : photo ? `original|${image.src}` : image.src;
    if (this.mockupSource !== key) {
      this.mockupSource = key;
      this.cutout = photo ? photoCutout(image)
        : mockupCutout(image, customer ? customer.mask : null, image.dataset.recolored === "1");
    }
    if (!this.cutout) {
      if (customer) return this.originalPreview();
      this.renderFailed = true; return false;
    }
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      this.renderFailed = true; return false;
    }
    const [x, y, w, h] = this.cutout.bounds;
    if (this.ornament) {
      this.renderOrnament(ctx, this.cutout);
      canvas.setAttribute('aria-label', `${image.alt} on ${this.ornament.material}, ${this.ornament.label}`);
      canvas.hidden = false;
      stage.classList.add("has-mockup");
      return true;
    }
    if (["sweater", "socks"].includes(this.dataset.kind)) {
      const layout = this.knitLayout();
      const config = this.knitData[this.dataset.kind];
      const key = this.knitTileKey(layout);
      const tile = this.knitImage(config.tiles[key]);
      const base = config.body ? this.knitImage(config.tiles.portrait) : tile;
      const maps = this.knitMapsFor(this.knitData.mockups);
      for (const img of [tile, base]) if (!img.complete) this.renderWhenReady(img);
      if (!tile.complete || !base.complete || maps === undefined) return false;
      if (!tile.naturalWidth || !base.naturalWidth || maps === null) {
        this.renderFailed = true; return false;
      }
      const area = this.knitData.variants[this.dataset.selectedVariant] || config.area;
      const knit = document.createElement('canvas');
      renderKnit(knit.getContext('2d'), this.cutout, tile, config, area, layout, base, key);
      composeKnit(ctx, knit, maps);
      canvas.setAttribute('aria-label', `${image.alt}, ${this.dataset.kind}, ${layout === 'portrait' ? 'portrait layout' : `repeating pet rows with ${layout.includes('snowflakes') ? 'snowflakes' : 'trees'}`} preview`);
      canvas.hidden = false;
      stage.classList.add("has-mockup");
      return true;
    }
    const format = OsfPetPortrait.FORMAT_PRINTS[this.dataset.kind];
    if (format) {
      if (!this.renderFormatStage(ctx, canvas, format)) return false;
      canvas.setAttribute("aria-label", `${image.alt} on a ${this.dataset.kind}, product preview`);
      canvas.hidden = false;
      stage.classList.add("has-mockup");
      return true;
    }
    const dieCut = ["sticker", "magnet"].includes(this.dataset.kind);
    const kind = this.dataset.kind === "tee" ? "shirt" : this.dataset.kind;
    if (kind in APPAREL_AREAS && !this.apparelArea) return;
    const goods = GOODS[kind];
    const area = goods?.area || this.apparelArea || [560, 560];
    const [aw, ah] = area;
    const hoodieBack = kind === "hoodie" && this.hoodieView === "back";
    const box = goods ? [(goods.art[2] - goods.art[0]) * aw, (goods.art[3] - goods.art[1]) * ah, (goods.art[0] + goods.art[2]) / 2 * aw, (goods.art[1] + goods.art[3]) / 2 * ah] : hoodieBack ? [.90 * aw, .72 * ah, .5 * aw, 0] : this.apparelArea ? apparelBox(kind, area, kind === "hoodie" ? "small" : this.printSize) : [dieCut ? 536 : 560, dieCut ? 536 : 560, 280, 280];
    // Goods canvases render at displayed size x DPR (not a fixed 560) so the browser never
    // upscales the bitmap.
    const target = goods ? Math.max(560, Math.min(1680, Math.round((stage.clientWidth || 560) * goods.rect[2] * Math.min(2, window.devicePixelRatio || 1))))
      : kind === "ornament" ? sharpSize() : 560;
    const scale = Math.min(box[0] / w, box[1] / h), ratio = target / aw;
    canvas.width = target;
    canvas.height = Math.round(target * ah / aw);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    // Every goods pet (stocking, apron) composites smoothly at full resolution with no halo, like print_files.
    ctx.drawImage(this.cutout.canvas, x, y, w, h, (box[2] - w * scale / 2) * ratio, (hoodieBack ? .22 * ah : box[3] - h * scale / 2) * ratio, w * scale * ratio, h * scale * ratio);
    // Die-cut white border for sticker/magnet; the apron's Border option draws the print's 3% ring (pet_border.WIDTH).
    const apronBorder = kind === "apron" && this.querySelector('[name="properties[Contrast]"]:checked')?.value === "Border";
    if (dieCut || apronBorder) {
      const backing = document.createElement("canvas");
      backing.width = canvas.width;
      backing.height = canvas.height;
      const back = backing.getContext("2d");
      back.drawImage(canvas, 0, 0);
      back.globalCompositeOperation = "source-in";
      back.fillStyle = "white";
      back.fillRect(0, 0, canvas.width, canvas.height);
      ctx.globalCompositeOperation = "destination-over";
      const halo = apronBorder ? .03 * this.cutout.canvas.width * scale * ratio : goods ? 3 : 6;
      // The wide apron ring fills in 3px steps; stickers keep their single ring of offsets.
      for (let r = halo; r > 0; r -= apronBorder ? 3 : halo) {
        for (let i = 0; i < 16; i++) {
          const angle = i * Math.PI / 8;
          ctx.drawImage(backing, Math.cos(angle) * r, Math.sin(angle) * r);
        }
      }
      ctx.globalCompositeOperation = "source-over";
    }
    canvas.setAttribute("aria-label", `${image.alt} on a ${this.dataset.kind}${kind === "hoodie" ? `, ${this.hoodieView} print, approximate placement` : this.apparelArea ? `, ${this.printSize} print, approximate placement` : ""}`);
    canvas.hidden = false;
    stage.classList.add("has-mockup");
    return true;
  }

  formatSize(format) {
    const options = this.apparelVariants[this.dataset.selectedVariant] || [];
    const sizes = Object.keys(format.areas);
    return [sizes.find((name) => options.some((option) => option === name || option.startsWith(`${name} `))) || sizes[0], options];
  }

  // The print file multiplied onto the product's blank photo where pet_format_stages.py fitted it.
  renderFormatStage(ctx, canvas, format) {
    const [size, options] = this.formatSize(format);
    const stages = this.stageData[this.dataset.kind] || {};
    const stage = stages[options.join(" ")] || stages[options.find((o) => o.startsWith(size))] || stages[size];
    if (!stage) { this.renderFailed = true; return false; }
    const photo = this.knitImage(stage.src);
    if (!photo.complete) { this.renderWhenReady(photo); return false; }
    if (!photo.naturalWidth) { this.renderFailed = true; return false; }
    const print = document.createElement("canvas");
    this.renderFormat(print.getContext("2d"), print, format, "#fff");
    const S = canvas.width = canvas.height = 1200;
    ctx.drawImage(photo, 0, 0, S, S);
    ctx.save();
    ctx.globalCompositeOperation = "multiply";
    if (stage.clip) {
      // Print past the visible face (pillow) wraps out of view.
      ctx.beginPath();
      for (const [px, py] of stage.clip) ctx.lineTo(px * S, py * S);
      ctx.clip();
    }
    // Strips and cells overlap by a pixel; warp them unblended first so the seams are not multiplied twice.
    const warp = document.createElement("canvas");
    warp.width = warp.height = S;
    const w = warp.getContext("2d");
    if (stage.cyl) {
      // Mug: print columns wrapped onto the fitted cylinder, front half only; rows are the fitted tenths.
      // The radius narrows toward the base (r + dr t), so each row band uses its own radius.
      const { cx, r, dr, k, uc, top, bottom, rows } = stage.cyl, steps = 240, ph = print.height / (rows.length - 1);
      for (let i = 0; i < steps; i++) {
        const u = (i + 0.5) / steps, th = k * (u - uc);
        if (Math.abs(th) >= Math.PI / 2) continue;
        const y0 = top[0] + top[1] * Math.cos(th), y1 = bottom[0] + bottom[1] * Math.cos(th);
        for (let j = 0; j < rows.length - 1; j++) {
          const ya = y0 + rows[j] * (y1 - y0), yb = y0 + rows[j + 1] * (y1 - y0), rt = r + dr * (rows[j] + rows[j + 1]) / 2;
          const x = cx + rt * Math.sin(th), half = rt * Math.cos(th) * k / steps / 2;
          w.drawImage(print, i * print.width / steps, j * ph, print.width / steps, ph, (x - half) * S - 0.5, ya * S, half * 2 * S + 1, (yb - ya) * S + 0.5);
        }
      }
    } else if (stage.mesh) {
      // Pillow: the puff spaces the tenths unevenly, so each tenth-by-tenth cell lands on its measured lines.
      const { xs, ys } = stage.mesh, cw = print.width / (xs.length - 1), ch = print.height / (ys.length - 1);
      for (let i = 0; i < xs.length - 1; i++) {
        for (let j = 0; j < ys.length - 1; j++) {
          w.drawImage(print, i * cw, j * ch, cw, ch, xs[i] * S, ys[j] * S, (xs[i + 1] - xs[i]) * S + 0.5, (ys[j + 1] - ys[j]) * S + 0.5);
        }
      }
    }
    if (stage.cyl || stage.mesh) ctx.drawImage(warp, 0, 0);
    else {
      // Calendar: each made page shows its preview (the shown preview on its own page), others their style's example.
      const shown = this.calendar?.pages && Object.keys(this.calendar.pages).find((page) => this.calendar.pages[page] === this.renderInput.value);
      const pageImage = (page, example) => {
        const row = this.calendarRow(page);
        return page === (shown || "front_cover") ? print
          : this.knitImage(row ? this.previewSrc(row) : (this.calendar && this.styleImage(this.calendarStyle(page))) || example);
      };
      const pages = [pageImage("front_cover", ""), ...(stage.months || []).map(([month, src]) => pageImage(month.toLowerCase(), src))];
      if (pages.some((img) => !img.complete && !img.getContext)) { this.renderWhenReady(pages.find((img) => !img.complete && !img.getContext)); return false; }
      stage.affine.forEach(([a, b, c, d, e, f], i) => {
        const img = pages[i], iw = img.naturalWidth || img.width, ih = img.naturalHeight || img.height;
        // Month art keeps its aspect, centred on the page's print box.
        const fit = Math.min(print.width / iw, print.height / ih);
        ctx.setTransform(a * S / print.width, b * S / print.width, c * S / print.height, d * S / print.height, e * S, f * S);
        ctx.drawImage(img, (print.width - iw * fit) / 2, (print.height - ih * fit) / 2, iw * fit, ih * fit);
      });
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.globalCompositeOperation = "source-over";
      ctx.fillStyle = "#222";
      ctx.font = `600 ${(stage.font || 0) * S}px sans-serif`;
      (stage.labels || []).forEach(([x, y], i) => ctx.fillText(stage.months[i][0], x * S, y * S));
    }
    ctx.restore();
    return true;
  }

  // The selected size's print file in miniature (print_files.placed, bandana_canvas) on the product's base color.
  renderFormat(ctx, canvas, format, base = format.base) {
    const [size, options] = this.formatSize(format);
    const [aw, ah] = format.areas[size];
    const k = 1120 / Math.max(aw, ah), W = Math.round(aw * k), H = Math.round(ah * k);
    const [x, y, w, h] = this.cutout.bounds;
    canvas.width = W;
    canvas.height = H;
    canvas.dataset.frame = options.some((option) => option.endsWith("White")) ? "white" : "black";
    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = base;
    if (this.dataset.kind === "bandana") {
      // Triangle, point down; the art sits high, where it is wide.
      ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(W, 0); ctx.lineTo(W / 2, H); ctx.closePath(); ctx.fill();
      const s = Math.min(H * 0.5 / h, W * 0.3 / w);
      ctx.drawImage(this.cutout.canvas, x, y, w, h, (W - w * s) / 2, H * 0.08, w * s, h * s);
      return;
    }
    ctx.fillRect(0, 0, W, H);
    const s = Math.min(H * format.fill / h, W / format.centres.length * format.fill / w);
    for (const c of format.centres) ctx.drawImage(this.cutout.canvas, x, y, w, h, W * c - w * s / 2, (H - h * s) / 2, w * s, h * s);
  }

  setupZoom() {
    // The stage itself opens the zoom view.
    this.zoomButton = this.querySelector("[data-pet-stage]");
    this.zoomDialog = this.querySelector("[data-pet-zoom]");
    this.zoomScene = this.querySelector("[data-pet-zoom-scene]");
    this.zoomStatus = this.querySelector("[data-pet-zoom-status]");
    this.zoomScale = 1;
    this.zoomX = this.zoomY = 0;
    const surface = this.querySelector("[data-pet-zoom-surface]");
    const pointers = new Map();
    const metrics = () => {
      const points = [...pointers.values()];
      return {
        x: points.reduce((s, p) => s + p.x, 0) / points.length,
        y: points.reduce((s, p) => s + p.y, 0) / points.length,
        distance:
          points.length === 2
            ? Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y)
            : 0,
      };
    };
    const open = (event) => {
      // Links and buttons inside the stage (the failure report) keep their own action.
      if (event.target.closest("a, button") || this.zoomButton.getAttribute("aria-disabled") === "true") return;
      this.zoomDialog.showModal();
      petEvents.queue("zoom_open", {}, this.renderInput.value || undefined);
      this.updateZoom();
    };
    this.zoomButton.addEventListener("click", open);
    this.zoomButton.addEventListener("keydown", (event) => {
      if ((event.key !== "Enter" && event.key !== " ") || event.target.closest("a")) return;
      event.preventDefault();
      open(event);
    });
    this.querySelector("[data-pet-zoom-close]").addEventListener("click", () =>
      this.zoomDialog.close(),
    );
    this.zoomDialog.addEventListener("close", () => {
      this.zoomScene.replaceChildren();
      pointers.clear();
      if (this.isConnected) this.zoomButton.focus({ preventScroll: true });
    });
    this.querySelectorAll("[data-pet-zoom-step]").forEach((button) =>
      button.addEventListener("click", () => {
        this.zoomScale = Math.min(
          4,
          Math.max(1, this.zoomScale + Number(button.dataset.petZoomStep)),
        );
        this.positionZoom();
      }),
    );
    surface.addEventListener(
      "wheel",
      (event) => {
        event.preventDefault();
        this.zoomScale = Math.min(
          4,
          Math.max(1, this.zoomScale - event.deltaY * 0.005),
        );
        this.positionZoom();
      },
      { passive: false },
    );
    surface.addEventListener("pointerdown", (event) => {
      if (event.pointerType === "mouse" && event.button !== 0) return;
      pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      surface.setPointerCapture(event.pointerId);
    });
    surface.addEventListener("pointermove", (event) => {
      if (!pointers.has(event.pointerId)) return;
      const before = metrics();
      pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      const after = metrics();
      if (before.distance && after.distance)
        this.zoomScale = Math.min(
          4,
          Math.max(1, (this.zoomScale * after.distance) / before.distance),
        );
      this.zoomX += after.x - before.x;
      this.zoomY += after.y - before.y;
      this.positionZoom();
    });
    for (const name of ["pointerup", "pointercancel", "lostpointercapture"])
      surface.addEventListener(name, (event) =>
        pointers.delete(event.pointerId),
      );
  }

  updateZoom() {
    const stage = this.querySelector("[data-pet-stage]");
    if (stage.dataset.renderState !== "ready") return;
    const clone = stage.cloneNode(true);
    for (const node of [clone, ...clone.querySelectorAll("*")]) {
      for (const attribute of [...node.attributes])
        if (attribute.name.startsWith("data-pet-") || attribute.name === "id")
          node.removeAttribute(attribute.name);
    }
    for (const name of ["role", "tabindex", "aria-label", "aria-disabled"]) clone.removeAttribute(name);
    const sourceCanvases = stage.querySelectorAll("canvas");
    clone.querySelectorAll("canvas").forEach((canvas, i) => {
      canvas.width = sourceCanvases[i].width;
      canvas.height = sourceCanvases[i].height;
      canvas.getContext("2d").drawImage(sourceCanvases[i], 0, 0);
    });
    clone
      .querySelectorAll(".osf-pet__loading, .osf-pet__skeleton, .osf-pet__hold")
      .forEach((node) => node.remove());
    this.zoomScene.style.aspectRatio = getComputedStyle(stage).aspectRatio;
    this.zoomScene.replaceChildren(clone);
    this.zoomScale = 1;
    this.zoomX = this.zoomY = 0;
    this.positionZoom();
  }

  positionZoom() {
    const surface = this.querySelector("[data-pet-zoom-surface]");
    const maxX = Math.max(
      0,
      (this.zoomScene.offsetWidth * this.zoomScale - surface.clientWidth) / 2,
    );
    const maxY = Math.max(
      0,
      (this.zoomScene.offsetHeight * this.zoomScale - surface.clientHeight) / 2,
    );
    this.zoomX = Math.min(maxX, Math.max(-maxX, this.zoomX));
    this.zoomY = Math.min(maxY, Math.max(-maxY, this.zoomY));
    this.zoomScene.style.transform = `translate(${this.zoomX}px, ${this.zoomY}px) scale(${this.zoomScale})`;
    this.querySelector("[data-pet-zoom-level]").textContent =
      `${Math.round(this.zoomScale * 100)}%`;
  }

  renderOrnament(ctx, cutout) {
    const size = sharpSize();
    ctx.canvas.width = ctx.canvas.height = size;
    ctx.scale(size / 560, size / 560);
    const option = this.ornament;
    const [x, y, w, h] = cutout.bounds;
    const [bx, by, bw, bh] = option.bounds || [0, 0, 1, 1];
    const side = option.bounds ? 350 : 300;
    const left = (560 - side) / 2, top = 180;
    ctx.clearRect(0, 0, 560, 560);
    let hanger = option.hole || [.5, .1];
    ctx.save(); ctx.translate(left, top); ctx.scale(side, side);
    if (option.shape !== 'custom') {
      const shape = new Path2D();
      if (option.shape === 'round') shape.ellipse(bx + bw / 2, by + bh / 2, bw / 2, bh / 2, 0, 0, Math.PI * 2);
      else {
        // Silhouette traced from the Printify heart photo (processor/pet_heart_outline.json), in 0..1 of the heart box.
        HEART_OUTLINE.forEach(([px, py], i) => { const xx = bx + px * bw, yy = by + py * bh;
          if (i) shape.lineTo(xx, yy); else shape.moveTo(xx, yy); });
        shape.closePath();
      }
      ctx.fillStyle = '#fffdf7'; ctx.fill(shape); ctx.clip(shape);
    }
    const [sx, sy, sw, sh] = option.safe;
    const scale = Math.min(sw / w, sh / h);
    const ax = sx + (sw-w*scale)/2, ay = sy + (sh-h*scale)/2;
    ctx.drawImage(cutout.canvas, x, y, w, h, ax, ay, w*scale, h*scale);
    if (option.hole) {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.beginPath(); ctx.arc(option.hole[0], option.hole[1], option.hole[2], 0, Math.PI * 2); ctx.fill();
      ctx.globalCompositeOperation = 'source-over';
    }
    ctx.restore();
    ctx.globalCompositeOperation = 'destination-over';
    // Two gold strands knotted above the hole, like the string the ceramic ornament ships with.
    ctx.strokeStyle = option.kind === 'acrylic' ? '#b3262e' : '#c9a227'; ctx.lineWidth = 2.5;
    const hx = left + side*hanger[0], hy = top + side*hanger[1];
    for (const dx of [-5, 5]) { ctx.beginPath(); ctx.moveTo(hx, 15); ctx.quadraticCurveTo(hx + dx * 2, (15 + hy) / 2, hx + dx * .4, hy); ctx.stroke(); }
    ctx.globalCompositeOperation = 'source-over';
  }

  // Colored borders replace the edge of the art, which on a photo would cut fur; photo cut-outs keep the white cut.
  photoCut(preview) {
    const look = this.querySelector('input[name="pet-look"]:checked')?.value;
    if (preview?.look === "original" || look === "original") return true;
    if (preview) return preview.keep !== undefined;  // a realistic Just My Pet cut from one photo
    return look === "realistic" && /^justpet(bg)?$/.test(this.querySelector('input[name="pet-style"]:checked')?.value || "");
  }

  showBorders(preview) {
    this.querySelector(".osf-pet__borders").hidden = !this.bordered() || this.photoCut(preview);
  }

  // The stage shows the picked preview, or the selected style's example (with the buyer's photo inset).
  showStage() {
    // A running or failed job keeps its card on every format: the formats share one preview.
    this.querySelector("[data-pet-skeleton]").hidden = !(this.drawing || this.jobFailed);
    const stage = this.querySelector("[data-pet-stage]");
    const art = this.querySelector("[data-pet-stage-art]");
    const caption = this.querySelector("[data-pet-caption]");
    caption.setAttribute("data-clarity-mask", "");
    const inset = this.querySelector("[data-pet-stage-photo]");
    const preview =
      !this.showExample &&
      this.previews?.find((p) => p.job_id === this.renderInput.value);
    const radio = this.querySelector('input[name="pet-style"]:checked');
    if (this.outputKind !== "portrait") {
      const realistic = this.querySelector('input[name="pet-look"][value="realistic"]');
      // Puzzles have no working Realistic look: hide it and fall back to Cartoon. Other formats keep the pet-count rule.
      realistic.closest("label").hidden = this.outputKind === "puzzle";
      realistic.disabled = this.outputKind === "puzzle" || this.pets > 1;
      if (realistic.disabled && realistic.checked)
        this.querySelector('input[name="pet-look"][value="cartoon"]').checked = true;
    }
    const photo = preview && this.showPhotoSide && this.cropped[preview.job_id];
    delete art.dataset.recolored;
    stage.classList.toggle("is-preview", !!preview);
    stage.classList.toggle("is-photo", !!photo);
    this.querySelector("[data-pet-flip]").hidden = !preview || preview.has_photo === false;
    const mirror = this.querySelector("[data-pet-mirror]");
    if (mirror) mirror.hidden = !preview || !!photo || this.outputKind !== "portrait" || !!this.design;
    this.querySelector("[data-pet-watermark-note]").hidden = !preview || !!photo;
    // Cut-out Just My Pet (keep set) repeats exactly on redo; with background, a redo keeps less or more of it.
    const cutout = !!preview && preview.keep !== undefined;
    if (this.redo) this.redo.hidden = !preview || (preview.has_photo === false && !this.design) || preview.look === "original" || (cutout && preview.style !== "justpetbg");
    const keep = this.querySelector("[data-pet-keep]");
    if (keep) keep.hidden = !cutout;
    for (const [sel, show] of [["[data-pet-redo-text]", !cutout], ["[data-pet-keep-text]", cutout]]) {
      const text = this.querySelector(sel);
      if (text) text.hidden = !show;
    }
    if (this.fix) this.fix.hidden = cutout;
    const redoStyle = this.querySelector("[data-pet-redo-style]");
    if (preview && redoStyle) redoStyle.textContent = ` (${styleName(preview)})`;
    this.stagePreview = preview || null;
    this.drawSummaries();
    this.showBorders(preview);
    for (const b of this.querySelectorAll("[data-pet-flip-to]"))
      b.setAttribute("aria-pressed", String((b.dataset.petFlipTo === "photo") === !!photo));
    if (photo) {
      art.src = photo;
      art.alt = "Your original photo";
      caption.textContent = "Your photo";
    } else if (preview) {
      let display = this.previewSrc(preview);
      if (this.dataset.kind === "hoodie" && preview.name)
        display = (this.hoodieView === "front" ? preview.portrait_preview_data_url : preview.hoodie_back_preview_data_url) || display;
      const size = this.puzzleSize?.value;
      art.src = preview.puzzle_previews?.[size] || preview.puzzle_preview_urls?.[size] || display;
      art.alt = `Your ${preview.style} preview`;
      caption.textContent = `${preview.style === "design" ? "Your design" : `Your preview: ${label(preview)}`}${preview.instruction ? `. Scene: ${preview.instruction}` : ""}${preview.fix ? `. Fix: ${preview.fix}` : ""}${preview.ocr_warning ? `. ${preview.ocr_warning}` : ""}${preview.low_res ? ". This photo is low resolution, so the print may look soft. A closer, sharper photo prints best." : ""}`;
    } else if (this.querySelector('input[name="pet-look"]:checked')?.value === "original") {
      // Before an upload, a real cutout example (CC0 photo), never AI art.
      art.src = this.shownPhotoUrl || this.querySelector("[data-pet-original]").dataset.example;
      art.alt = this.shownPhotoUrl ? "Your photo" : "Example original photo of a dog";
      caption.textContent = this.shownPhotoUrl ? "Original photo: your own photo, with no AI changes"
        : "Example: Original photo, the whole photo, with no AI changes";
    } else if (this.design) {
      caption.textContent = "Example only. Describe your design and make a preview to see it here.";
    } else if (radio.dataset.example) {
      art.src = this.exampleUrl(radio, "example", undefined, this.previewSpecies(radio));
      this.syncSpeciesToggle();
      const name = radio.closest("label").querySelector(".osf-pet__style-card > span").textContent;
      art.alt = `Example ${name} pet portrait`;
      caption.textContent = `Example: ${name} style`;
    } else {
      caption.textContent = "Your own style: describe it below";
    }
    if (this.puzzleSize) {
      const large = this.puzzleSize.value === "87921";
      stage.style.aspectRatio = this.outputKind === "scene_print" ? "3 / 2" : large ? "6300 / 5100" : "4395 / 3450";
      drawPuzzleCuts(stage.querySelector(".osf-pet__puzzle-grid"), large ? 26 : 18, large ? 20 : 14, large ? 6300 / 5100 : 4395 / 3450);
    }
    // While drawing, the example sits behind the progress card; the photo inset made it look like the result.
    inset.hidden = !!preview || !this.shownPhotoUrl || this.busy;
    if (this.shownPhotoUrl) inset.src = this.shownPhotoUrl;
    this.recolorStage(art, preview, photo);
    this.renderMockup();
  }

  // A border picked on the stage shows at once, before the server's exact art (or for a style example, at all).
  recolorStage(art, preview, photo) {
    const border = this.border();
    const from = preview ? preview.border || "white" : "white";
    if (border === from || photo || this.photoCut(preview) || this.outputKind !== "portrait") return;
    const base = art.src, key = `${base.slice(-200)}|${base.length}|${border}`;
    this.recolored ||= new Map();
    const done = (url) => { art.src = url; art.dataset.recolored = "1"; };
    if (this.recolored.has(key)) return done(this.recolored.get(key));
    const maskUrl = preview?.preview_mask_data_url;
    if (preview && !maskUrl) return;
    Promise.all([loadImage(base), maskUrl ? loadImage(maskUrl) : null]).then(([image, mask]) => {
      if (art.src !== base || this.border() !== border) return;
      const canvas = recolorRing(image, mask, border);
      if (!canvas) return;
      if (this.recolored.size > 12) this.recolored.clear();
      this.recolored.set(key, canvas.toDataURL("image/png"));
      done(this.recolored.get(key));
    }).catch(() => {});
  }

  // The buyer's stored photo for a preview, whole or as cropped for it (same token and owner check as
  // previews); cached per job.
  async photoFor(id, cropped = false) {
    const cache = cropped ? this.cropped : this.photos;
    if (cache[id]) return cache[id];
    const loads = (this.photoLoads ||= new Map()); // one request per photo, however many tiles ask
    const key = `${cropped}:${id}`;
    if (!loads.has(key)) loads.set(key, (async () => {
      const res = await this.request(`/pet-preview/${id}/photo${cropped ? "?cropped=true" : ""}`);
      if (!res.ok) throw new Error("The original photo is not available for this preview.");
      return (cache[id] = URL.createObjectURL(await res.blob()));
    })().finally(() => loads.delete(key)));
    return loads.get(key);
  }

  savedCacheKey() {
    return this.dataset.customerId ? `${SAVED_CACHE_KEY}:${this.dataset.customerId}` : "";
  }

  readSavedCache() {
    try {
      const c = JSON.parse(localStorage.getItem(this.savedCacheKey()) || "null");
      return c && Date.now() - c.saved_at < HISTORY_CACHE_TTL_MS && Array.isArray(c.photos) ? c : null;
    } catch {
      return null;
    }
  }

  saveSavedCache() {
    const key = this.savedCacheKey();
    if (!key) return;
    const photos = this.savedList.map((p) => ({ job_id: p.job_id, photo_key: p.photo_key }));
    const thumbs = Object.fromEntries(photos.slice(0, SAVED_PHOTOS).filter((p) => this.thumbs[p.job_id])
      .map((p) => [p.job_id, this.thumbs[p.job_id]]));
    try {
      if (photos.length) localStorage.setItem(key, JSON.stringify({ photos, thumbs, saved_at: Date.now() }));
      else localStorage.removeItem(key);
    } catch {} // storage full or blocked: saved photos just load from the server
  }

  // A small JPEG of a loaded photo for the cache; the full photo still loads before a pick uses it.
  async thumbOf(url) {
    const img = new Image();
    img.src = url;
    await img.decode();
    const scale = Math.min(1, SAVED_THUMB_PX / Math.min(img.naturalWidth, img.naturalHeight));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(img.naturalWidth * scale);
    canvas.height = Math.round(img.naturalHeight * scale);
    canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/jpeg", 0.75);
  }

  // Photo/Preview toggle: this session's upload, else the stored photo (same token and owner check as previews).
  async flip(toPhoto) {
    const id = this.renderInput.value;
    this.showPhotoSide = toPhoto;
    if (toPhoto) {
      try {
        await this.photoFor(id, true);
      } catch (err) {
        this.showPhotoSide = false;
        this.status.textContent = err.message;
      }
    }
    if (this.renderInput.value === id) this.showStage();
  }

  // The session pick is per output kind, so a portrait pick never opens on the puzzle page.
  // Design and pet pages share an output kind, so their kept render and pending job are keyed apart.
  sessionKey() {
    return `${SESSION_KEY}:${this.outputKind}${this.design ? ":design" : ""}`;
  }

  pendingKey() {
    return this.design ? `${PENDING_KEY}:design` : PENDING_KEY;
  }

  pickedRender() {
    // A gallery link or format switch opens this page with ?pet_render=<id>; else this session's pick.
    return new URLSearchParams(location.search).get("pet_render") || sessionStorage.getItem(this.sessionKey()) || "";
  }

  historyCacheKey() {
    const id = this.dataset.customerId;
    return id ? `${HISTORY_CACHE_KEY}:${id}:${this.outputKind}${this.design ? ":design" : ""}` : "";
  }

  readHistoryCache() {
    try {
      const saved = JSON.parse(localStorage.getItem(this.historyCacheKey()) || "null");
      return saved && Date.now() - saved.saved_at < HISTORY_CACHE_TTL_MS && Array.isArray(saved.previews) ? saved : null;
    } catch {
      return null;
    }
  }

  // Only the first page of light rows; an etag is kept only when the rows are exactly what the server sent.
  saveHistoryCache(etag = "") {
    const key = this.historyCacheKey();
    if (!key) return;
    const arts = [...new Set((this.previews || []).map((p) => p.art_of || p.job_id))].slice(0, HISTORY_SHOWN);
    const token = this.calendar?.token;
    const rows = this.previews.filter((p) => arts.includes(p.art_of || p.job_id) || (token && p.calendar === token)).slice(0, HISTORY_CACHE_ROWS)
      .map((p) => Object.fromEntries(Object.entries(p).filter(([k]) => !hasBytes(k) && k !== "hydrateFailed")));
    try {
      if (rows.length) localStorage.setItem(key, JSON.stringify({ previews: rows, more: this.historyMore, etag, saved_at: Date.now() }));
      else localStorage.removeItem(key);
    } catch {} // storage full or blocked: the page just loads without the cache
  }

  // Before the sign-in token answers: draw the last history this browser saw, then revalidate in loadHistory.
  showCachedHistory() {
    const cached = this.readHistoryCache();
    if (!cached) return null;
    this.applyHistory(cached.previews, cached.more, true);
    historySource("cache");
    perf("cache_render_ms", Math.round(performance.now()));
    perf("cache_rows", cached.previews.length);
    return cached;
  }

  // Keep the bytes a row already fetched when a fresh copy of the row replaces it.
  keepBytes(rows) {
    return rows.map((row) => {
      const old = this.previews?.find((p) => p.job_id === row.job_id);
      return old ? { ...row, ...Object.fromEntries(Object.entries(old).filter(([k]) => hasBytes(k))) } : row;
    });
  }

  // Designs and pet portraits share an output kind and a history; each page shows only its own.
  ownsRow(row) {
    return (row.style === "design") === this.design;
  }

  applyHistory(rows, more, fromCache = false) {
    const previews = rows.filter((p) => this.ownsRow(p));
    const picked = this.pickedRender();
    const shown = !!picked && this.renderInput.value === picked && !!this.shownPreview();
    // A tile picked from the cached row restored that copy's settings; the fresh row's settings win.
    const repick = !fromCache && !!picked && this.cachedPick === picked;
    this.historyCached = fromCache;
    this.cachedPick = null;
    this.previews = this.keepBytes(previews);
    this.historyMore = more;
    this.historyPages = 1;
    const chosen = this.previews.find((p) => p.job_id === picked);
    // A pre-seeded Order-link id that history does not confirm goes back to the empty default (never judged from the cache).
    if (!fromCache && !chosen && picked === this.renderInput.value) this.renderInput.value = "";
    if (!fromCache) track("history_loaded", { previews: previews.length, picked_found: !!chosen || !picked });
    try {
      if (chosen && repick) this.restoreSettings(chosen);
      if (chosen && !(shown && !fromCache)) {
        // A gallery or format link opens that preview with its settings; this session's pick keeps the page's.
        if (new URLSearchParams(location.search).get("pet_render") === chosen.job_id && !shown) this.restoreSettings(chosen);
        this.choose(chosen, true);
      }
    } finally {
      this.drawHistory();
    }
  }

  async loadHistory(cached = null, quiet = false) {
    const picked = this.pickedRender();
    // The block's inline script sets this before first paint on real sign-ins; keep the example hidden until then.
    if (!cached && !quiet) this.querySelector("[data-pet-stage]").classList.toggle("is-loading", !!picked);
    if (picked && !quiet && !this.shownPreview()) this.querySelector("[data-pet-caption]").textContent = "Loading your preview…";
    // A calendar's 13 pages and redos can outnumber a page of arts; the service adds this design's every page.
    const query = `?limit=${HISTORY_SHOWN}&render=${encodeURIComponent(picked)}&output_kind=${this.outputKind}`
      + (this.calendar?.token ? `&calendar=${encodeURIComponent(this.calendar.token)}` : "");
    const began = performance.now();
    const res = await this.request(`/pet-previews${query}`, cached?.etag ? { headers: { "If-None-Match": cached.etag } } : {}, "history");
    perf("history_ms", Math.round(performance.now() - began));
    perf("history_status", res.status);
    perf("history_server_timing", res.headers.get("server-timing") || "");
    if (res.status === 304 && cached) {
      this.historyFresh = true;
      this.saveHistoryCache(cached.etag);
      petPerf.markAfterPaint("history_fresh");
      return;
    }
    const data = await this.readApi(res);
    this.historyFresh = true;
    this.inline = null; // the service's rows replace the metafield's
    this.applyHistory(data.previews, data.more || 0);
    historySource("processor");
    petPerf.markAfterPaint("history_fresh");
    this.saveHistoryCache(res.headers.get("etag") || "");
  }

  async loadMoreHistory() {
    const button = this.querySelector("[data-pet-history-more]");
    button.disabled = true;
    try {
      const query = `?limit=${HISTORY_SHOWN}&offset=${this.historyPages * HISTORY_SHOWN}&output_kind=${this.outputKind}`;
      const local = this.inline && this.historyPages * HISTORY_SHOWN < new Set(this.inline.rows.map((r) => r.art_of || r.job_id)).size;
      const data = local ? this.inlinePage(this.historyPages + 1) : await this.api(`/pet-previews${query}`);
      this.historyPages += 1;
      this.historyMore = data.more || 0;
      const known = new Set(this.previews.map((p) => p.job_id));
      this.previews = [...this.previews, ...data.previews.filter((p) => !known.has(p.job_id) && this.ownsRow(p))]
        .sort((a, b) => b.created_ts - a.created_ts);
      this.drawHistory();
    } catch (err) {
      this.status.textContent = err.message;
    } finally {
      button.disabled = false;
    }
  }

  previewSrc(p) {
    return p.preview_data_url || p.preview_url || (p.preview_image_path ? `${this.dataset.endpoint}${p.preview_image_path}` : "");
  }

  thumbSrc(p) {
    return p.preview_data_url || p.thumb_url || (p.thumb_path ? `${this.dataset.endpoint}${p.thumb_path}` : this.previewSrc(p));
  }

  // The stage needs the preview bytes and mask pair; a list row has only URLs, so fetch the one row picked.
  async hydrate(preview) {
    if (preview.preview_data_url || preview.status !== "preview" && preview.status !== "ordered") return;
    preview.hydrateFailed = false;
    this.hydrating ||= new Map();
    if (!this.hydrating.has(preview.job_id)) {
      const began = performance.now();
      this.hydrating.set(preview.job_id, afterPaint().then(() => this.api(`/pet-preview/${preview.job_id}`)).then((full) => {
        perf("hydrate_ms", Math.round(performance.now() - began));
        const row = this.previews.find((p) => p.job_id === preview.job_id);
        if (row) Object.assign(row, Object.fromEntries(Object.entries(full).filter(([k]) => hasBytes(k))));
      }).finally(() => this.hydrating.delete(preview.job_id)));
    }
    try {
      await this.hydrating.get(preview.job_id);
    } catch (err) {
      track("hydrate_error", { error: String(err.message || err).slice(0, 100), status: err.status || 0 });
      const gone = err.status === 404;
      const picked = this.renderInput.value === preview.job_id;
      if (gone) {
        this.previews = this.previews.filter((p) => p.job_id !== preview.job_id);
        if (picked) this.renderInput.value = "";
        this.drawHistory();
      } else {
        for (const row of this.previews.filter((p) => p.job_id === preview.job_id)) row.hydrateFailed = true;
      }
      // A row with a public picture is already on screen: a failed upgrade to the mockup is not worth an error.
      if (picked && (gone || !preview.preview_url))
        this.status.textContent = gone ? "That preview is no longer available." : "Could not load this preview. Tap it to try again.";
    }
    if (this.renderInput.value === preview.job_id || !this.renderInput.value) {
      this.showStage();
      this.stageLoaded();
    }
  }

  // Versions of one art (name and border changes) share art_of; newest first.
  versionsOf(artOf) {
    return this.previews.filter((p) => (p.art_of || p.job_id) === artOf);
  }

  // The one compatibility rule: colored borders print only on formats with the border picker.
  fits(p) {
    return (p.border || "white") === "white" || this.bordered();
  }

  bordered() {
    return ["sticker", "magnet"].includes(this.dataset.kind);
  }

  shownVersions(artOf) {
    return this.versionsOf(artOf).filter((p) => this.fits(p));
  }

  // Same art, same name if possible; null when this art has no version this format prints (choose redraws it white).
  nearestFit(p) {
    const versions = this.shownVersions(p.art_of || p.job_id);
    return versions.find((v) => (v.name || "") === (p.name || "")) || versions[0] || null;
  }

  // One tile per art; its name and border versions sit under the stage (drawVersions).
  drawHistory(pick = true) {
    try {
      this.drawHistoryRow();
    } finally {
      this.drawSaved();
      this.updateSteps();
      petPerf.markAfterPaint("history_painted");
    }
    if (pick && this.previews.length && !this.renderInput.value) this.choose(this.previews[0], true);
    if (this.historyCacheKey()) this.saveHistoryCache();
    this.drawCalendar();
  }

  drawHistoryRow() {
    const box = this.querySelector("[data-pet-history]");
    const row = this.querySelector("[data-pet-history-row]");
    // Every art shows on every format; a colored-border-only art redraws white when picked here.
    const arts = [...new Set(this.previews.map((p) => p.art_of || p.job_id))];
    const drawing = this.drawing ? [this.drawingTile()] : [];
    row.replaceChildren(
      ...drawing,
      ...arts.map((artOf, index) => {
        const shown = this.shownVersions(artOf);
        const versions = shown.length ? shown : this.versionsOf(artOf);
        const p = versions.find((v) => v.job_id === this.renderInput.value) || versions[0];
        const b = document.createElement("button");
        b.type = "button";
        b.dataset.artOf = artOf;
        b.setAttribute("aria-pressed", String(versions.some((v) => v.job_id === this.renderInput.value)));
        b.setAttribute("aria-label", `Use the ${label(p)} preview`);
        const img = document.createElement("img");
        img.src = this.thumbSrc(p);
        img.alt = `Watermarked ${p.style} preview`;
        img.width = img.height = 72;
        img.loading = index < 8 ? "eager" : "lazy";
        img.decoding = "async";
        img.addEventListener("load", () => petPerf.mark("first_tile_image"), { once: true });
        // A public tile that is gone (a deleted design the metafield still lists): try the service's copy once.
        if (p.thumb_url && p.thumb_path) img.addEventListener("error", () => {
          if (img.src === p.thumb_url) img.src = `${this.dataset.endpoint}${p.thumb_path}`;
        });
        b.append(img);
        if (versions.length > 1) {
          const n = document.createElement("span");
          n.className = "osf-pet__history-count";
          n.textContent = versions.length;
          n.setAttribute("aria-label", `${versions.length} versions`);
          b.append(n);
        }
        b.addEventListener("click", () => {
          petEvents.queue("history_pick", { versions: versions.length }, p.job_id);
          const picked = this.selectedVersions?.[artOf];
          this.choose(versions.find((v) => v.job_id === picked) || versions[0]);
        });
        return this.previewTile(b, p.job_id, "preview");
      }),
    );
    box.hidden = !arts.length && !drawing.length;
    const more = this.querySelector("[data-pet-history-more]");
    if (more) more.hidden = !this.historyMore;
    perf("tiles_drawn_ms", Math.round(performance.now()));
    if (arts.length) petPerf.markAfterPaint("first_tile");
  }

  // The preview being drawn, first in "Your previews" until it finishes.
  drawingTile() {
    const tile = document.createElement("div");
    tile.className = "osf-pet__history-drawing";
    tile.dataset.petHistoryDrawing = "";
    tile.setAttribute("role", "img");
    tile.setAttribute("aria-label", "Drawing your preview");
    const spinner = document.createElement("span");
    spinner.className = "osf-pet__history-spinner";
    const text = document.createElement("span");
    text.textContent = "Drawing…";
    tile.append(spinner, text);
    return tile;
  }

  previewTile(button, jobId, scope) {
    const tile = document.createElement("div");
    tile.className = "osf-pet__preview-tile";
    const remove = document.createElement("button");
    remove.type = "button";
    remove.className = "osf-pet__item-delete";
    remove.textContent = "×";
    remove.setAttribute("aria-label", scope === "version" ? "Delete this version" : "Delete this preview and its versions");
    remove.addEventListener("click", () => this.deletePreview(jobId, scope));
    tile.append(button, remove);
    tile.dataset.deleteId = jobId;
    if (this.deleting?.(jobId)) this.markDeleting(tile);
    return tile;
  }

  // Step 2: one tile per distinct photo behind the buyer's previews, so a new style needs no new upload.
  // The server merges re-uploads of the same picture into one tile.
  async drawSaved() {
    const run = (this.savedRun = (this.savedRun || 0) + 1);
    this.thumbs ||= {};
    if (!this.savedList) {
      const cached = this.readSavedCache();
      if (cached) {
        this.savedList = cached.photos;
        Object.assign(this.thumbs, cached.thumbs);
      }
    }
    const loading = this.querySelector("[data-pet-saved-loading]");
    if (this.savedList) this.paintSaved(this.savedList);
    else if (loading && "signedIn" in this.dataset) {
      loading.hidden = false;
      this.querySelector("[data-pet-saved]").hidden = false;
    }
    // Saved photos are for accounts; signed out and email guests keep this tab's photos only.
    const fetched = "signedIn" in this.dataset ? await this.api("/pet-photos").then((d) => d.photos, () => null) : null;
    if (run !== this.savedRun) return; // a newer list is on its way
    if (fetched) this.savedList = fetched;
    this.paintSaved(this.savedList || []);
    if (fetched) this.saveSavedCache();
  }

  paintSaved(photos) {
    const row = this.querySelector("[data-pet-saved-row]");
    const saved = this.querySelector("[data-pet-saved]");
    const deleteAll = this.querySelector("[data-pet-delete-all]");
    if (!row) return;
    this.querySelector("[data-pet-saved-loading]")?.setAttribute("hidden", "");
    const unsaved = (this.localPhotos || []).map((l) => this.localTile(l));
    // Tiles show at once and fill in as each photo arrives, so one slow photo cannot hide the row.
    const tiles = photos.slice(0, (this.savedShown ||= SAVED_PHOTOS)).map((p) => {
      const tile = document.createElement("div");
      tile.className = "osf-pet__saved-tile";
      const b = document.createElement("button");
      b.type = "button";
      b.dataset.jobId = p.job_id;
      b.setAttribute("aria-pressed", String(p.job_id === this.pickedJob()));
      b.setAttribute("aria-label", "Use this saved photo");
      const img = document.createElement("img");
      img.alt = "";
      b.append(img);
      const thumb = this.thumbs[p.job_id];
      if (this.photos[p.job_id] || thumb) img.src = this.photos[p.job_id] || thumb;
      else b.disabled = true; // no picture to show yet
      if (!img.src || this.photoLoads?.has(`false:${p.job_id}`)) tile.classList.add("is-loading");
      // A cached thumbnail shows the tile; its full photo loads only when picked, so a page load does not fetch every photo.
      if (!this.photos[p.job_id] && !thumb) {
        this.photoFor(p.job_id).then(
          (url) => {
            if (!thumb) img.src = url;
            b.disabled = false;
            tile.classList.remove("is-loading");
            if (!this.thumbs[p.job_id]) this.thumbOf(url).then((t) => { this.thumbs[p.job_id] = t; this.saveSavedCache(); }, () => {});
          },
          () => { tile.remove(); },
        );
      }
      b.addEventListener("click", () => {
        if (this.savedDragged) return;
        if (this.addingPet) return this.addSavedPet({ job: p.job_id }, "tap");
        petEvents.queue("saved_photo_pick");
        this.pickSaved(p.job_id);
      });
      this.dragSavedTile(b, img, { job: p.job_id });
      const remove = document.createElement("button");
      remove.type = "button";
      remove.className = "osf-pet__saved-delete";
      remove.textContent = "×";
      remove.setAttribute("aria-label", "Delete this saved photo");
      remove.addEventListener("click", () => this.deleteSaved(p.job_id));
      tile.append(b, remove);
      tile.dataset.deleteId = `photo:${p.job_id}`; // a photo shares its job id with a preview
      if (this.deleting?.(tile.dataset.deleteId)) this.markDeleting(tile);
      return tile;
    });
    const shown = [...unsaved, ...tiles];
    const more = this.querySelector("[data-pet-saved-more]");
    const hiddenCount = Math.max(0, photos.length - this.savedShown);
    row.replaceChildren(...shown, ...(more ? [more] : []));
    if (saved) saved.hidden = !shown.length;
    if (more) {
      more.hidden = !hiddenCount;
      more.textContent = `+${hiddenCount} more`;
      more.setAttribute("aria-label", `Show ${hiddenCount} more saved photos`);
    }
    if (deleteAll) deleteAll.hidden = !this.previews?.length && !photos.length;
  }

  // The saved photo the next preview uses: a picked tile, or the saved copy of the current upload.
  pickedJob() {
    return this.sourceJob || this.photoJobs?.get(this.photo?.files?.[0]) || null;
  }

  // An upload this visit that is not saved yet (the consent box is still unticked).
  localTile(local) {
    const tile = document.createElement("div");
    tile.className = "osf-pet__saved-tile";
    const b = document.createElement("button");
    b.type = "button";
    b.dataset.localPhoto = "";
    b.setAttribute("aria-pressed", String(!this.sourceJob && this.photo?.files?.[0] === local.file));
    b.setAttribute("aria-label", "Use this photo");
    const img = document.createElement("img");
    img.src = local.url;
    img.alt = "";
    b.append(img);
    b.addEventListener("click", () => {
      if (this.savedDragged) return;
      if (this.addingPet) return this.addSavedPet({ local }, "tap");
      this.pickLocal(local);
    });
    this.dragSavedTile(b, img, { local });
    const remove = document.createElement("button");
    remove.type = "button";
    remove.className = "osf-pet__saved-delete";
    remove.textContent = "×";
    remove.setAttribute("aria-label", "Remove this photo");
    remove.addEventListener("click", () => {
      this.localPhotos.splice(this.localPhotos.indexOf(local), 1);
      this.unsavedPhotos = (this.unsavedPhotos || []).filter((f) => f !== local.file);
      if (this.photo.files[0] === local.file) {
        this.photo.value = "";
        this.clearSaved();
      }
      this.drawSaved();
    });
    tile.append(b, remove);
    return tile;
  }

  pickLocal(local) {
    petEvents.queue("saved_photo_pick");
    this.sourceJob = null;
    const dt = new DataTransfer();
    dt.items.add(local.file);
    this.photo.files = dt.files;
    this.photoUrl = URL.createObjectURL(local.file);
    this.querySelector("[data-pet-file-label]").textContent = "Change photo";
    this.photoChanged();
    this.drawSaved();
  }

  confirmDelete(title, description, action) {
    if (this.busy || this.deleteConfirmation) return Promise.resolve(false);
    const component = this.querySelector("[data-pet-delete-dialog]");
    const opener = document.activeElement;
    const accept = component.querySelector("[data-pet-delete-accept]");
    const cancel = component.querySelector("[data-pet-delete-cancel]");
    component.querySelector("[data-pet-delete-title]").textContent = title;
    component.querySelector("[data-pet-delete-description]").textContent = description;
    accept.textContent = action;
    accept.disabled = cancel.disabled = false;
    let accepted = false;
    this.deleteConfirmation = new Promise((resolve) => {
      accept.onclick = () => {
        if (accept.disabled) return;
        accepted = true;
        accept.disabled = cancel.disabled = true;
        resolve(true); // the delete feedback starts with the close animation, not after it
        component.closeDialog();
      };
      cancel.onclick = () => component.closeDialog();
      component.addEventListener("dialog:open", () => {
        trapFocus(component.querySelector("dialog"));
        cancel.focus();
      }, { once: true });
      component.addEventListener("dialog:close", () => {
        removeTrapFocus();
        accept.onclick = cancel.onclick = null;
        this.deleteConfirmation = null;
        if (opener?.isConnected) opener.focus({ preventScroll: true });
        resolve(accepted);
      }, { once: true });
      component.showDialog();
    });
    return this.deleteConfirmation;
  }

  // Shared delete feedback: matching tiles dim and fold away at once, and come back if the request fails.
  async removing(gone, request) {
    const motion = !matchMedia("(prefers-reduced-motion: reduce)").matches;
    const result = this.querySelector("[data-pet-delete-result]");
    const all = this.querySelector("[data-pet-delete-all-open]");
    this.deleting = gone;
    all.disabled = true;
    result.textContent = "Deleting…";
    result.hidden = false;
    const tiles = [...this.querySelectorAll("[data-delete-id]")].filter((t) => gone(t.dataset.deleteId));
    for (const t of tiles) this.markDeleting(t, false);
    if (motion) requestAnimationFrame(() => tiles.forEach((t) => t.classList.add("is-leaving")));
    try {
      const [data] = await Promise.all([request(), motion && sleep(220)]);
      return data;
    } catch (err) {
      for (const t of this.querySelectorAll(".is-deleting")) {
        t.classList.remove("is-deleting", "is-leaving");
        for (const b of t.querySelectorAll("button")) b.disabled = false;
      }
      throw err;
    } finally {
      this.deleting = null;
      all.disabled = false;
    }
  }

  // A tile drawn while its delete runs (a history refresh) starts already folded.
  markDeleting(tile, leaving = !matchMedia("(prefers-reduced-motion: reduce)").matches) {
    tile.style.setProperty("--tile-width", `${tile.offsetWidth || 72}px`);
    tile.classList.add("is-deleting");
    tile.classList.toggle("is-leaving", leaving);
    for (const b of tile.querySelectorAll("button")) b.disabled = true;
  }

  deleteResult(message) {
    const result = this.querySelector("[data-pet-delete-result]");
    result.textContent = message;
    result.hidden = false;
    result.focus();
  }

  // Art needed by an unshipped order goes once it ships; the photos go immediately.
  async deleteAll() {
    if (!await this.confirmDelete("Delete all your pet photos and portraits?",
      "This deletes every pet photo you added and every preview, version and portrait we made from them, including ones you ordered. This cannot be undone. Art for an order that has not shipped yet is deleted automatically once it ships.",
      "Delete everything") || this.busy) return;
    this.busy = true;
    try {
      const { waiting } = await this.removing(() => true, () => this.api("/pet-data", { method: "DELETE" }));
      track("all_deleted", { waiting });
      this.photos = {};
      this.thumbs = {};
      this.savedList = [];
      this.cropped = {};
      this.previews = [];
      this.localPhotos = [];
      this.unsavedPhotos = [];
      this.photo.value = "";
      this.clearSaved();
      this.forgetPhotos();
      this.renderInput.value = "";
      sessionStorage.removeItem(this.sessionKey());
      this.form?.setAttribute("data-pet-pending", "");
      this.showExample = true;
      const done = waiting
        ? `Deleted. ${waiting} portrait${waiting > 1 ? "s" : ""} for orders that have not shipped yet will be deleted automatically once they ship.`
        : "Deleted. All your pet photos and portraits are gone.";
      this.previewUrlInput.value = "";
      this.syncFormatLinks();
      this.drawHistory();
      this.drawVersions("");
      this.showStage();
      this.deleteResult(done);
    } catch (err) {
      this.deleteResult(`Could not delete your pet photos and portraits. ${err.message} Please try again.`);
    } finally {
      this.busy = false;
    }
  }

  async deletePreview(jobId = this.renderInput.value, scope = "preview") {
    const target = this.previews.find((p) => p.job_id === jobId);
    if (!target || this.busy) return;
    const artOf = target.art_of || target.job_id;
    const item = scope === "version" ? "version" : "preview and its versions";
    if (!await this.confirmDelete(`Delete this ${item}?`,
      `This deletes this ${item}. This cannot be undone, and it will not give you back a free attempt.`,
      scope === "version" ? "Delete version" : "Delete preview") || this.busy) return;
    this.busy = true;
    try {
      const gone = scope === "version" ? [jobId] : this.versionsOf(artOf).map((p) => p.job_id);
      const { deleted } = await this.removing((id) => gone.includes(id), () => this.api(`/pet-preview/${jobId}?scope=${scope}`, { method: "DELETE" }));
      track("preview_deleted", { count: deleted.length });
      this.previews = this.previews.filter((p) => !deleted.includes(p.job_id));
      for (const id of deleted) {
        delete this.photos[id];
        delete this.cropped[id];
      }
      if (deleted.includes(this.sourceJob)) this.clearSaved();
      if (deleted.includes(this.renderInput.value)) {
        this.renderInput.value = "";
        sessionStorage.removeItem(this.sessionKey());
        const url = new URL(location.href);
        url.searchParams.delete("pet_render");
        history.replaceState(null, "", url);
        const next = this.versionsOf(artOf)[0] || this.previews[0];
        if (next) this.choose(next);
        else {
          this.form?.setAttribute("data-pet-pending", "");
          this.previewUrlInput.value = "";
          this.showExample = true;
          this.syncFormatLinks();
        }
      }
      this.drawHistory();
      this.drawVersions(this.shownPreview()?.art_of || this.renderInput.value);
      this.showStage();
      this.deleteResult(`${scope === "version" ? "Version" : "Preview"} deleted.`);
    } catch (err) {
      this.deleteResult(`Could not delete this ${item}. ${err.message} Please try again.`);
    } finally {
      this.busy = false;
    }
  }

  // Deletes every stored copy of the picture; previews made from it stay, without the photo view or redo.
  async deleteSaved(jobId) {
    if (this.busy || !await this.confirmDelete("Delete this photo?",
      "Your previews and orders stay, but to make more from this photo or redo them, you will have to add it again.",
      "Delete photo") || this.busy) return;
    this.busy = true;
    try {
      const { deleted } = await this.removing((id) => id === `photo:${jobId}`, () => this.api(`/pet-preview/${jobId}/photo`, { method: "DELETE" }));
      this.savedList = this.savedList?.filter((p) => !deleted.includes(p.job_id));
      for (const id of deleted) {
        delete this.thumbs?.[id];
        delete this.photos[id];
        delete this.cropped[id];
        const p = this.previews.find((x) => x.job_id === id);
        if (p) p.has_photo = false;
      }
      if (deleted.includes(this.sourceJob)) this.clearSaved();
      this.drawSaved();
      this.showStage();
      this.deleteResult("Photo deleted.");
    } catch (err) {
      this.deleteResult(`Could not delete this photo. ${err.message} Please try again.`);
    } finally {
      this.busy = false;
    }
  }

  // Back to no photo after the picked saved photo is deleted.
  clearSaved() {
    this.pickSaved(null);
    this.photoUrl = this.shownPhotoUrl = "";
    this.querySelector("[data-pet-photo-thumb]").hidden = true;
    this.querySelector(".osf-pet__file-icon").removeAttribute("hidden");
    this.querySelector("[data-pet-file-label]").textContent = "Upload a photo";
    this.querySelector("[data-pet-photo-tools]").hidden = true;
    this.querySelector("[data-pet-crop]").hidden = true;
    this.mainCrop = "";
    this.drawExtras();
    this.rememberPhotos();
  }

  // The full photo behind a saved tile; the tile spins while it loads.
  loadSaved(jobId) {
    const tile = () => this.querySelector(`[data-pet-saved-row] button[data-job-id="${jobId}"]`)?.parentElement;
    if (!this.photos[jobId]) tile()?.classList.add("is-loading");
    return this.photoFor(jobId).finally(() => tile()?.classList.remove("is-loading"));
  }

  savedPhotoFailed() {
    const note = this.querySelector("[data-pet-photo-limit]");
    note.hidden = false;
    note.textContent = "That saved photo did not load. Try again.";
    track("saved_photo_error");
  }

  pressSaved(jobId) {
    for (const b of this.querySelectorAll("[data-pet-saved-row] button[aria-pressed]"))
      b.setAttribute("aria-pressed", String(b.dataset.jobId === jobId));
  }

  pickSaved(jobId) {
    const previous = this.sourceJob;
    this.sourceJob = jobId;
    this.pressSaved(jobId);
    if (!jobId) return;
    if (!this.photos[jobId]) this.pickWhileLoading(jobId, previous);
    else this.usePickedPhoto(jobId);
  }

  // A cached thumbnail was picked: it shows in the upload box now, and the full photo replaces it when it arrives.
  pickWhileLoading(jobId, previous) {
    const shown = this.querySelector("[data-pet-photo-thumb]");
    const label = this.querySelector("[data-pet-file-label]");
    const before = { src: shown.src, hidden: shown.hidden, label: label.textContent };
    if (this.thumbs[jobId]) {
      shown.src = this.thumbs[jobId];
      shown.hidden = false;
      this.querySelector(".osf-pet__file-icon").setAttribute("hidden", "");
    }
    label.textContent = "Getting your photo ready…";
    const loading = (this.mainLoading = this.loadSaved(jobId).then(
      () => this.sourceJob === jobId && this.usePickedPhoto(jobId),
      () => {
        if (this.sourceJob !== jobId) return;
        this.savedPhotoFailed();
        this.sourceJob = previous;
        this.pressSaved(previous);
        if (!this.photoUrl) return this.clearSaved();
        Object.assign(shown, { src: before.src, hidden: before.hidden });
        label.textContent = before.label;
      },
    ).finally(() => {
      if (this.mainLoading !== loading) return;
      this.mainLoading = null;
      this.drawExtras();
    }));
    this.querySelector("[data-pet-extras]").hidden = false;
  }

  usePickedPhoto(jobId) {
    this.photo.value = "";
    this.photoUrl = this.photos[jobId];
    this.querySelector("[data-pet-file-label]").textContent = "Using a saved photo. Tap to upload another";
    this.photoChanged();
  }

  choose(preview, preserveName = false) {
    if ((preview.output_kind || "portrait") !== this.outputKind) return;
    if (!this.fits(preview)) preview = this.nearestFit(preview) || preview;
    this.jobFailed = false;
    if (!preserveName) {
      this.nameEdited = false;
      this.nameCleared = false;
      this.nameInput.value = preview.name || "";
    }
    const artOf = preview.art_of || preview.job_id;
    (this.selectedVersions ||= {})[artOf] = preview.job_id;
    // Load's fallback pick of the newest preview keeps the page's defaults; buyer picks restore settings.
    if (!preserveName) this.restoreSettings(preview);
    if (!preserveName && this.historyCached) this.cachedPick = preview.job_id;
    if (preview.job_id !== this.renderInput.value) {
      if (this.fix) this.fix.value = "";
      if (this.instruction) this.instruction.value = this.restoredInstruction = preview.instruction || "";
      this.countInstruction();
    }
    lifestyleReset(this); // a scene photo would hide the picked preview
    this.renderInput.value = preview.job_id;
    this.styleInput.value = label(preview);
    // Cart line image: the public bucket copy first; the signed path grants only this watermarked preview.
    this.previewUrlInput.value = preview.preview_url || (preview.preview_image_path
      ? `${this.dataset.endpoint}${preview.preview_image_path}`
      : "");
    const nameProp = this.querySelector("[data-pet-name-prop]");
    nameProp.value = preview.name || "";
    nameProp.disabled = !preview.name; // no empty "Pet name" line on the order
    nameProp.name = (preview.pets || 1) > 1 ? "properties[Pet names]" : "properties[Pet name]";
    const orderProp = this.querySelector("[data-pet-order-prop]");
    orderProp.value = preview.pet_order || "";
    orderProp.disabled = !preview.pet_order;
    const countProp = this.querySelector("[data-pet-count-prop]");
    countProp.value = String(preview.pets || 1);
    countProp.disabled = (preview.pets || 1) < 2;
    // A preview picked on page load leaves the name field blank, so an old name never goes into a new preview.
    // Typed text stays; the shown art and its cart line keep their own name.
    if (preserveName && !this.nameEdited) {
      this.nameInput.value = ""; // also drops text the browser restored into the field
      this.nameCleared = true;
    }
    this.nameStyle.value = preview.name_style || "classic";
    const border = this.borders.find((r) => r.value === (preview.border || "white"))
      || this.borders.find((r) => r.value === "white"); // a format without the picked color prints white
    if (border) border.checked = true;
    this.querySelector("[data-pet-border-prop]").value = border?.closest("label").textContent.trim() || "White";
    sessionStorage.setItem(this.sessionKey(), preview.job_id);
    this.showPhotoSide = false;
    this.dataset.ready = "";
    this.syncPending();
    this.syncFormatLinks();
    this.showExample = false;
    this.showStage();
    this.syncPuzzleVariant();
    for (const b of this.querySelectorAll("[data-pet-history-row] button[data-art-of]")) {
      b.setAttribute("aria-pressed", String(b.dataset.artOf === artOf));
      if (b.dataset.artOf === artOf) b.querySelector("img").src = this.thumbSrc(preview);
    }
    this.drawVersions(artOf);
    this.drawStylePanel();
    this.calendarPicked(preview);
    this.hydrate(preview);
    // A colored-border preview from the sticker page: redraw it white here (free, no image call).
    if (!["sticker", "magnet"].includes(this.dataset.kind) && (preview.border || "white") !== "white") setTimeout(() => this.rename());
  }

  // The picked preview's own settings: count, theme, style and look. Fields an older record lacks stay as they are.
  restoreSettings(preview) {
    if (preview.pets) this.setCount(preview.pets);
    const style = this.radio("pet-style", preview.style);
    const look = this.radio("pet-look", preview.look);
    if (look && !look.disabled) look.checked = true;
    const crop = this.radio("pet-original", preview.style);
    if (crop) crop.checked = true;
    this.showOriginal();
    const theme = style && this.querySelector(`input[name="pet-theme"][value="${style.closest("label").dataset.theme}"]`);
    if (theme && !theme.closest("label").hidden) theme.checked = true;
    this.showTheme();
    if (style && !style.closest("label").hidden) style.checked = true;
    this.showThumbs();
    this.applyOptions(preview.options || []);
  }

  // Checked product picks (variant color/size, layout, filler) as [name, value]; `also` adds the widget's own radios.
  picks(also = () => false) {
    return [this, this.cards?.[3]].flatMap((box) => [...(box?.querySelectorAll('input[type="radio"]:checked') || [])])
      .filter((input) => also(input) || input.name.startsWith("properties[") || input.closest("variant-picker"))
      .map((input) => [input.name, input.value]);
  }

  // Click each pick so the variant picker, price and mockup follow; a value this page lacks is skipped.
  applyOptions(picks) {
    for (const pick of picks) {
      const input = this.radio(...pick);
      if (input && !input.checked && !input.disabled) input.click();
    }
  }

  // The widget's or buy box's radio with this name and value (stored values are matched, never put in a selector).
  radio(name, value) {
    return [this, this.cards?.[3]].flatMap((box) => [...(box?.querySelectorAll('input[type="radio"]') || [])])
      .find((input) => input.name === name && input.value === value);
  }

  // Back to the page's own defaults and the style example; no preview stays picked.
  resetSettings() {
    const [settings, options] = [this.defaults.filter(([n]) => n.startsWith("pet-")), this.defaults.filter(([n]) => !n.startsWith("pet-"))];
    const radio = ([name, value]) => this.radio(name, value);
    for (const pick of settings) { const input = radio(pick); if (input) input.checked = true; }
    this.showOriginal();
    this.showTheme();
    this.countTouched = false;
    this.setCount(1);
    if (this.instruction) this.instruction.value = "";
    this.countInstruction();
    this.nameEdited = false;
    this.nameInput.value = "";
    this.applyOptions(options);
    this.jobFailed = false;
    this.renderInput.value = "";
    this.styleInput.value = "";
    this.previewUrlInput.value = "";
    sessionStorage.removeItem(this.sessionKey());
    const url = new URL(location.href);
    url.searchParams.delete("pet_render");
    history.replaceState(null, "", url);
    for (const b of this.querySelectorAll("[data-pet-history-row] button[data-art-of]")) b.setAttribute("aria-pressed", "false");
    this.querySelector("[data-pet-versions]").hidden = true;
    const nameProp = this.querySelector("[data-pet-name-prop]");
    nameProp.value = "";
    nameProp.disabled = true;
    this.querySelector("[data-pet-order-prop]").disabled = true;
    this.querySelector("[data-pet-count-prop]").disabled = true;
    this.syncPending();
    this.styleTouched = false;
    this.showPhotoSide = false;
    this.showExample = true;
    this.syncFormatLinks();
    this.showThumbs();
    this.showStage();
  }

  // Under the stage: every name and border version of the shown art, so trying borders stays in one place.
  drawVersions(artOf) {
    const box = this.querySelector("[data-pet-versions]");
    if (!box) return;
    const versions = this.shownVersions(artOf);
    box.hidden = !versions.length;
    // Every page renders the border radios; only formats that print a border show them.
    const bordered = this.querySelector(".osf-pet__borders")?.hidden === false;
    const borderName = (v) =>
      this.borders.find((r) => r.value === (v.border || "white"))?.closest("label").textContent.trim() || "White";
    box.querySelector("[data-pet-versions-row]").replaceChildren(
      ...versions.map((v) => {
        const b = document.createElement("button");
        b.type = "button";
        b.setAttribute("aria-pressed", String(v.job_id === this.renderInput.value));
        const text = [v.name, bordered ? borderName(v) : "", v.mirrored ? "Mirrored" : ""].filter(Boolean).join(" · ") || "No name";
        b.setAttribute("aria-label", `Show the ${text} version`);
        const img = document.createElement("img");
        img.src = this.thumbSrc(v);
        img.alt = "";
        img.width = img.height = 72;
        img.decoding = "async";
        const cap = document.createElement("span");
        cap.textContent = text;
        b.append(img, cap);
        b.dataset.jobId = v.job_id;
        b.addEventListener("click", () => this.choose(v));
        return this.previewTile(b, v.job_id, "version");
      }),
    );
  }

  // pages: calendar page -> style to make in one request (Make the remaining pages); else the picked page.
  async generate(pages = null) {
    if (this.busy || this.preparing) return;
    if (this.pendingJob) {
      const { photoUrl, croppedUrl } = this.pendingJob;
      return this.makePreview("", photoUrl, croppedUrl, () => {});
    }
    if (this.design) return this.designPreview(this.promptInput.value);
    if ("puzzleBlocked" in this.dataset) {
      this.status.textContent = "Puzzle artwork awaits final launch approval.";
      return;
    }
    const file = this.photo.files[0];
    const source = !file && this.sourceJob;
    if (!file && !source)
      return (this.status.textContent = "Add a photo of your pet first.");
    if (!this.querySelector("[data-pet-consent]").checked)
      return (this.status.textContent =
        "Please confirm you have the right to use this photo.");
    const look =
      this.querySelector('input[name="pet-look"]:checked')?.value || "cartoon";
    const original = look === "original";
    let style = original ? this.querySelector('input[name="pet-original"]:checked').value
      : this.querySelector('input[name="pet-style"]:checked').value;
    // "Your idea" has no preset scene: the scene box is the whole idea.
    if (style === "custom" && !this.instruction.value.trim()) {
      this.instruction.focus();
      return (this.status.textContent = "Describe the scene for your idea first.");
    }
    const pets = this.pets;
    const extras = pets > 1 ? this.extras : [];
    if (original && extras.length)
      return (this.status.textContent = "An original photo uses one photo with every pet in it. Remove the extra photos.");
    if (extras.length + 1 > pets)
      return (this.status.textContent = "You added more photos than pets. Raise the count or remove a photo.");
    const photos = extras.map((e) => `${e.file.name}:${e.file.size}:${e.crop}`).join("|");
    const instruction = original ? "" : this.instruction.value.trim();
    const key = `${source ? `saved:${source}` : `${file.name}:${file.size}`}:${this.mainCrop}:${photos}:${pets}:${style}:${look}:${instruction}`;
    // Only the name or border changed: redraw it on the same art, with no image call and no preview used.
    if (!pages && key === this.lastKey && this.nameChanged()) return this.rename();
    const calendar = this.calendar && await this.calendarToken();
    if (this.calendar && !calendar) return;
    const order = this.photoOrder().slice(0, extras.length + 1);
    const made = await this.makePreview("/pet-preview", this.photoUrl, this.shownPhotoUrl, async (body) => {
      if (source) body.append("source_job_id", source);
      else body.append("photo", await resize(file), "pet.jpg");
      body.append("crop", this.mainCrop || "");
      body.append("pets", String(pets));
      for (const e of extras) {
        body.append("more_photos", await resize(e.file), "pet.jpg");
        body.append("more_crops", e.crop);
      }
      body.append("output_kind", this.outputKind);
      body.append("page_path", location.pathname);  // the preview-ready email links back here
      body.append("style", style);
      body.append("look", look);
      body.append("email_me", String(this.emailMe.checked));
      body.append("instruction", instruction);
      body.append("name", this.petName());
      body.append("name_style", this.nameStyle.value);
      body.append("border", this.border());
      body.append("options", JSON.stringify(this.picks()));
      body.append("consent", "true");
      if (calendar) {
        body.append("calendar", calendar);
        body.append("calendar_pages", JSON.stringify(pages || { [this.calendar.active]: style }));
      }
      // The same photo and style again means the buyer did not like it: use the higher quality.
      body.append("retry", String(key === this.lastKey));
    }, () => (this.lastKey = key));
    if (made?.job_id) (this.orderOf ||= {})[made.job_id] = order;
    this.orderChanged();
  }

  // Same photos, style, look and count as the shown preview, plus the buyer's short fix; uses one preview.
  async redoPreview() {
    const id = this.renderInput.value;
    const fix = this.fix.value.trim();
    if (this.busy || !id) return;
    // A design has no photo to redraw: a redo is a new description.
    if (this.design) {
      this.promptInput.value = fix;
      if (await this.designPreview(fix)) this.fix.value = "";
      return;
    }
    track("redo", { render: id, with_fix: !!fix, reorder: !!this.redoOrder });
    const order = this.redoOrder;
    const done = await this.makePreview(`/pet-preview/${id}/redo`, this.photos[id], this.cropped[id], (body) => {
      body.append("fix", fix);
      if (order) body.append("order", order);
      body.append("instruction", this.instruction.value.trim());
      body.append("name", this.petName());
      body.append("name_style", this.nameStyle.value);
      body.append("border", this.border());
      body.append("options", JSON.stringify(this.picks()));
      const keep = this.querySelector("[data-pet-keep] input:checked");
      if (keep) body.append("keep", keep.value);
    });
    if (done) {
      this.fix.value = "";
      const same = this.querySelector('[data-pet-keep] input[value="same"]');
      if (same) same.checked = true;
    }
    if (done?.job_id) (this.orderOf ||= {})[done.job_id] = order ? this.photoOrder() : this.orderOf?.[id];
    this.orderChanged();
  }

  // The page describes a design in words instead of taking a photo.
  get design() {
    return "design" in this.dataset;
  }

  setupDesign() {
    if (!this.design) return;
    this.promptInput = this.querySelector("[data-design-prompt]");
    for (const b of this.querySelectorAll("[data-design-example]")) b.addEventListener("click", () => {
      this.promptInput.value = b.textContent.trim();
      this.promptInput.focus();
    });
  }

  // Describe-a-design preview: the processor screens the words, so a refusal shows its generic message only.
  async designPreview(text) {
    const prompt = text.trim().replace(/\s+/g, " ");
    if (this.busy || this.preparing) return null;
    if (!prompt) return void (this.status.textContent = "Describe your design first.");
    if (!this.querySelector("[data-pet-consent]").checked)
      return void (this.status.textContent = "Please confirm your description has no real people, brands or logos.");
    // Only the credit name or border changed: redraw it on the same art, with no image call.
    if (prompt === this.lastKey && this.nameChanged()) return void this.rename();
    const name = this.petName();
    const done = await this.makePreview("/design-preview", undefined, undefined, (body) => {
      body.append("prompt", prompt);
      body.append("consent", "true");
      body.append("page_path", location.pathname);
    }, () => (this.lastKey = prompt));
    // The design endpoint takes no name; a typed credit name is added to the new art for free.
    if (done && name && this.shownPreview() === done) {
      this.nameInput.value = name; // showing the new art reset the field to its blank name
      await this.rename();
    }
    return done;
  }

  petName() {
    return this.nameInput.value.trim().replace(/\s+/g, " ");
  }

  // The name for a redraw of the shown art: a field left blank on page load keeps the art's own name.
  artName(shown) {
    return this.nameCleared && !this.petName() ? shown.name || "" : this.petName();
  }

  shownPreview() {
    return this.previews?.find((p) => p.job_id === this.renderInput?.value);
  }

  // The picked border; formats without a picker print no colored ring, so they show the white version.
  border() {
    if (!this.bordered()) return "white";
    return this.borders?.find((r) => r.checked)?.value || "white";
  }

  borderChanged() {
    const shown = this.shownPreview();
    return !!shown && this.border() !== (shown.border || "white");
  }

  // A new name, a new ribbon for a named preview, or a new border.
  nameChanged() {
    const shown = this.shownPreview();
    if (!shown) return false;
    const name = this.artName(shown);
    return this.outputKind === "portrait" && (name !== (shown.name || "")
      || (!!name && this.nameStyle.value !== (shown.name_style || "classic")) || this.borderChanged());
  }

  // The unsaved sample of the shown art with this name, style and border: one request per combination.
  nameSample(jobId, name, style, border) {
    const key = [jobId, name, style, border].join("|");
    this.samples ||= new Map();
    if (!this.samples.has(key)) {
      const query = new URLSearchParams({ name, name_style: style, border });
      const sample = this.request(`/pet-preview/${jobId}/name-sample?${query}`).then(async (res) => {
        if (!res.ok) throw new Error("sample");
        return URL.createObjectURL(await res.blob());
      });
      sample.catch(() => this.samples.delete(key));
      this.samples.set(key, sample);
      if (this.samples.size > 60) {
        const [oldest] = this.samples.keys();
        this.samples.get(oldest).then(URL.revokeObjectURL, () => {});
        this.samples.delete(oldest);
      }
    }
    return this.samples.get(key);
  }

  // A new name or ribbon shows on the stage as soon as its sample arrives; the saved version replaces it.
  showSample(shown) {
    if (!shown || this.showExample || !this.nameChanged()) return;
    const name = this.artName(shown), style = this.nameStyle.value, border = this.border();
    if (name === (shown.name || "") && style === (shown.name_style || "classic")) return; // a border recolors at once
    this.nameSample(shown.job_id, name, style, border).then((url) => {
      if (this.renderInput.value !== shown.job_id || this.nameStyle.value !== style || this.artName(shown) !== name) return;
      const art = this.querySelector("[data-pet-stage-art]");
      art.src = url;
      delete art.dataset.recolored;
      this.querySelector("[data-pet-stage]").classList.remove("is-updating");
    }).catch(() => {});
  }

  // Draw every ribbon for the shown art in the background, three at a time, so flipping through is instant.
  async prefetchSamples() {
    const shown = this.shownPreview(), name = this.petName();
    if (!shown || !name || this.outputKind !== "portrait" || this.prefetching) return;
    this.prefetching = true;
    const styles = [...this.nameStyle.options].map((o) => o.value);
    const border = this.border();
    try {
      const lane = async () => {
        for (let style = styles.shift(); style; style = styles.shift())
          await this.nameSample(shown.job_id, name, style, border).catch(() => {});
      };
      await Promise.all([lane(), lane(), lane()]);
    } finally {
      this.prefetching = false;
    }
  }

  toggleStylePanel() {
    const button = this.querySelector("[data-pet-style-info]");
    const panel = this.querySelector("[data-pet-name-styles]");
    panel.hidden = !panel.hidden;
    button.setAttribute("aria-expanded", String(!panel.hidden));
    if (!panel.hidden) this.drawStylePanel();
  }

  // Every name style as a button, drawn on the buyer's own pet once a name is typed; a click picks it.
  drawStylePanel() {
    const panel = this.querySelector("[data-pet-name-styles]");
    if (panel.hidden) return;
    const shown = this.shownPreview(), name = this.petName(), border = this.border();
    const live = !!shown && !!name && this.outputKind === "portrait";
    this.querySelector("[data-pet-name-styles-hint]").hidden = live;
    this.querySelector("[data-pet-name-styles-grid]").replaceChildren(...[...this.nameStyle.options].map((o) => {
      const b = document.createElement("button");
      b.type = "button";
      b.setAttribute("aria-pressed", String(o.value === this.nameStyle.value));
      if (live) {
        const img = document.createElement("img");
        img.alt = "";
        img.loading = "lazy";
        this.nameSample(shown.job_id, name, o.value, border).then((url) => (img.src = url)).catch(() => {});
        b.append(img);
      }
      b.append(o.textContent);
      b.addEventListener("click", () => {
        this.nameStyle.value = o.value;
        this.nameStyle.dispatchEvent(new Event("change"));
        for (const other of b.parentElement.children) other.setAttribute("aria-pressed", String(other === b));
      });
      return b;
    }));
    if (live) this.prefetchSamples();
  }

  // Same art, new name ribbon or border (drawn by the server in code): a new preview id, no preview used.
  async rename() {
    const shown = this.shownPreview();
    this.showSample(shown);
    if (this.busy) return (this.renameAgain = true); // a fast second pick runs when this one ends
    // A new photo or style is on the stage: the name and border wait for "Make my preview".
    if (this.showExample || !this.nameChanged()) return;
    const name = this.artName(shown);
    const onlyBorder = name === (shown.name || "") && this.nameStyle.value === (shown.name_style || "classic");
    // A version tried before shows at once, with no request.
    const same = this.versionsOf(shown.art_of || shown.job_id).find((v) => (v.name || "") === name
      && (v.name_style || "classic") === this.nameStyle.value && (v.border || "white") === this.border()
      && !!v.mirrored === !!shown.mirrored);
    if (same) {
      this.choose(same);
      this.status.textContent = "";
      return this.updateSteps();
    }
    const canvas = this.querySelector("[data-pet-stage]");
    canvas.classList.add("is-updating");
    this.busy = true;
    this.button.disabled = true;
    this.redoButton.disabled = true;
    this.status.textContent = onlyBorder ? "Changing the border…"
      : name ? `Adding ${name} to your preview…` : "Taking the name off your preview…";
    try {
      const body = new FormData();
      body.append("name", name);
      body.append("name_style", this.nameStyle.value);
      body.append("border", this.border());
      const done = await this.saveVersion(shown, body);
      const stillShown = this.renderInput.value === shown.job_id;
      // A newer pick made while this saved stays on screen; it saves next.
      if (stillShown && !this.renameAgain) this.choose(done);
      await this.querySelector("[data-pet-stage-art]").decode().catch(() => {});
      this.status.textContent = `${onlyBorder ? "Border" : "Name"} updated. This did not use a preview.`;
    } catch (err) {
      this.status.textContent = err.message;
      // The stage shows an approximate border; put back the one this preview really has.
      const current = this.borders.find((r) => r.value === (shown.border || "white"));
      if (current) current.checked = true;
      this.showStage();
    } finally {
      canvas.classList.remove("is-updating");
      this.busy = false;
      if (this.renameAgain) {
        this.renameAgain = false;
        setTimeout(() => this.rename());
      }
      this.updateSteps();
      this.syncPending();
      this.setRemaining(this.remaining);
    }
  }

  // A new version of the shown art drawn by the server in code (name, border or mirror); it joins the history.
  async saveVersion(shown, body) {
    const done = await this.api(`/pet-preview/${shown.job_id}/name`, { method: "POST", body });
    this.photos[done.job_id] = this.photos[shown.job_id];
    this.cropped[done.job_id] = this.cropped[shown.job_id];
    this.previews = [done, ...this.previews.filter((p) => p.job_id !== done.job_id)];
    this.drawHistory();
    return done;
  }

  // The same art flipped left to right, so a pet riding right rides left. A version tried before shows at once.
  async mirror() {
    const shown = this.shownPreview();
    if (!shown || this.busy || this.showExample) return;
    const same = this.versionsOf(shown.art_of || shown.job_id).find((v) => (v.name || "") === (shown.name || "")
      && (v.name_style || "classic") === (shown.name_style || "classic") && (v.border || "white") === (shown.border || "white")
      && !!v.mirrored !== !!shown.mirrored);
    if (same) {
      this.choose(same);
      return this.updateSteps();
    }
    const stage = this.querySelector("[data-pet-stage]");
    stage.classList.add("is-updating");
    this.busy = true;
    this.button.disabled = this.redoButton.disabled = true;
    this.status.textContent = "Mirroring your preview…";
    try {
      const body = new FormData();
      body.append("name", shown.name || "");
      body.append("name_style", shown.name_style || "classic");
      body.append("border", shown.border || "white");
      body.append("mirror", "true");
      const done = await this.saveVersion(shown, body);
      if (this.renderInput.value === shown.job_id) this.choose(done);
      await this.querySelector("[data-pet-stage-art]").decode().catch(() => {});
      this.status.textContent = "Mirrored. This did not use a preview.";
    } catch (err) {
      this.status.textContent = err.message;
    } finally {
      stage.classList.remove("is-updating");
      this.busy = false;
      this.updateSteps();
      this.syncPending();
      this.setRemaining(this.remaining);
    }
  }

  // The running or last job started on the product page now shown.
  // Post a preview job, poll it, and show it first in the history. Returns the finished job, or null.
  async makePreview(path, photoUrl, croppedUrl, fill, onQueued = () => {}) {
    const skeleton = this.querySelector("[data-pet-skeleton]");
    this.jobFailed = false;
    this.sceneError("");
    this.lastJobId = "";
    this.failureReport = null;
    this.busy = this.drawing = true;
    this.button.disabled = true;
    this.redoButton.disabled = true;
    skeleton.classList.remove("is-failed");
    const staleFailure = skeleton.querySelector("[data-pet-failure]");
    if (staleFailure) staleFailure.hidden = true;
    const subject = this.design ? "design" : this.pets > 1 ? "pets" : "pet";
    skeleton.querySelector("[data-pet-skeleton-title]").textContent = `Drawing your ${subject}…`;
    skeleton.hidden = false;
    this.startProgress(this.pendingJob?.started_at);
    this.syncEmailNote();
    const time = ["puzzle", "scene_print"].includes(this.outputKind)
      ? "about 3 minutes, sometimes 5 or more if we redraw it" : "about 30 seconds";
    const drawing = `Drawing your ${subject}. This takes ${time}. You can leave this page; your preview will be in your previews when you come back.`;
    this.status.textContent = drawing;
    this.showStage();
    // A finished preview joins the loaded history; without it, never start a paid preview.
    if (!this.historyFresh) {
      // A cold service answers the first request slowly; say so instead of showing a stalled bar.
      const warming = setTimeout(() => (this.status.textContent = `Drawing your ${subject}. Warming up the drawing engine first, which can add a minute or two.`), 2500);
      await this.loadHistory(null, true).catch(() => {}); // metafield rows: the service has not confirmed them yet
      clearTimeout(warming);
      if (this.historyFresh) {
        this.status.textContent = drawing;
        if (!this.pendingJob) {
          clearInterval(this.progressTimer); // the bar restarts when the drawing starts, not while the engine wakes
          this.startProgress();
        }
      }
    }
    if (!this.historyFresh) {
      this.status.textContent = "Your previews did not load, so a new one cannot start. Reload the page and try again.";
      await this.token().catch(() => {}); // only so the log below is sent if start() never got a token
      track("start_error", { error: "preview blocked: history not loaded" });
      this.busy = this.drawing = false;
      this.stopProgress();
      skeleton.hidden = true;
      this.showStage();
      this.updateSteps();
      this.setRemaining(this.remaining);
      return null;
    }
    if (this.previews) this.drawHistoryRow(); // the drawing tile leads "Your previews"
    try {
      let job = this.pendingJob;
      if (!job) {
        const body = new FormData();
        await fill(body);
        body.append("turnstile_token", this.querySelector('[name="cf-turnstile-response"]')?.value || "");
        track("preview_start", { path: path.split("/")[1] + (path.endsWith("redo") ? "_redo" : "") });
        try {
          job = await this.api(path, { method: "POST", body });
        } catch (err) {
          // A 5xx or gateway timeout did not queue a job. A Turnstile token is single use, so only retry without one.
          if (!(err.status >= 500) || body.get("turnstile_token")) throw err;
          track("preview_retry", { status: err.status });
          job = await this.api(path, { method: "POST", body });
        }
        this.pendingJob = { ...job, photoUrl, croppedUrl };
        localStorage.setItem(this.pendingKey(), JSON.stringify({ job_id: job.job_id, output_kind: this.outputKind, started_at: Date.now() }));
        onQueued();
        this.calendarQueued(job, photoUrl, croppedUrl);
      }
      this.lastJobId = job.job_id;
      const done = await this.poll(job.job_id);
      this.pendingJob = null;
      localStorage.removeItem(this.pendingKey());
      if (done.status !== "preview") throw new Error(done.message);
      this.previewFailed = false;
      this.jobFailed = false;
      this.photos[done.job_id] = photoUrl;
      this.cropped[done.job_id] = croppedUrl;
      this.previews = [
        done,
        ...this.previews.filter((p) => p.job_id !== done.job_id),
      ];
      this.renderInput.value = "";
      this.drawing = false; // the finished preview replaces the drawing tile
      this.drawHistory();
      this.status.textContent = "Here is your preview. Order it, or make another.";
      track("preview_done", { render: done.job_id });
      return done;
    } catch (err) {
      if (err.field === "instruction" && !this.pendingJob) {
        this.sceneError(err.message);
        this.status.textContent = "Change the scene description, then make your preview.";
        track("scene_rejected", { error: err.message.slice(0, 80) });
        this.instruction?.scrollIntoView({ block: "center", behavior: "smooth" });
        this.instruction?.focus({ preventScroll: true });
        return null;
      }
      this.previewFailed = true;
      if (err.jobGone) {
        // The service has no such job for this customer, so it will never finish: show the failure, not Resume.
        this.lastJobId = this.pendingJob?.job_id || this.lastJobId;
        this.pendingJob = null;
        localStorage.removeItem(this.pendingKey());
      }
      this.jobFailed = !this.pendingJob; // a lost-touch poll keeps its job, so Resume stays in charge
      this.failureReport = {
        id: this.pendingJob?.job_id || this.lastJobId || "",
        kind: this.dataset.kind || "",
        style: this.querySelector('input[name="pet-style"]:checked')?.value || "",
        reason: err.message || "preview failed",
      };
      track("preview_error", { error: err.message.slice(0, 80) });
      this.status.textContent = err.message;
      return null;
    } finally {
      this.busy = this.drawing = false;
      this.calendarSettled(this.lastJobId);
      if (this.previews) this.drawHistoryRow();
      this.stopProgress();
      skeleton.hidden = true;
      if (this.jobFailed) this.failCard(skeleton); // a failed job keeps its card; the example is not the result
      this.showStage();
      this.updateSteps();
      this.setRemaining(this.remaining);
      window.turnstile?.reset();
    }
  }

  // A failed poll (dropped connection, gateway timeout, cold start) never ends the job: retry with backoff and
  // give up only after sustained failure. The job stays pending, so "Resume my preview" polls it again.
  // The failure card replaces the progress card: the status line says why, and Try again starts over.
  failCard(skeleton) {
    skeleton.querySelector("[data-pet-skeleton-title]").textContent = "Sorry, we hit an error and could not finish that preview.";
    const failure = skeleton.querySelector("[data-pet-failure]");
    if (failure) {
      const report = this.failureReport || {};
      failure.querySelector("[data-pet-failure-ref]").textContent = report.id ? `Reference: ${report.id}` : "";
      const button = failure.querySelector("[data-pet-failure-report]");
      button.hidden = button.disabled = false;
      button.textContent = "Oops, we hit a bug. Tell us and we'll fix it.";
      failure.querySelector("[data-pet-failure-thanks]").replaceChildren();
      failure.hidden = false;
    }
    skeleton.classList.add("is-failed");
    skeleton.hidden = false;
  }

  // Sends the failed preview's details to the team in place, then thanks the buyer; email is the fallback.
  async sendFailureReport() {
    const failure = this.querySelector("[data-pet-failure]");
    const button = failure.querySelector("[data-pet-failure-report]");
    const thanks = failure.querySelector("[data-pet-failure-thanks]");
    const report = this.failureReport || {};
    button.disabled = true;
    button.textContent = "Sending\u2026";
    const body = new FormData();
    body.append("render", report.id || "");
    body.append("kind", report.kind || "");
    body.append("style", report.style || "");
    body.append("reason", report.reason || "");
    try {
      await this.api("/pet-failure-report", { method: "POST", body });
      thanks.textContent = "Thanks for letting us know. The team has the details and will fix it as soon as possible.";
    } catch {
      const mail = document.createElement("a");
      const text = ["Render: " + (report.id || "unknown"), "Product: " + (report.kind || "unknown"),
        "Style: " + (report.style || "unknown"), "Error: " + (report.reason || "unknown")].join("\n");
      mail.href = `mailto:itsfutureshock@gmail.com?subject=${encodeURIComponent("Preview failed: " + (report.id || "unknown"))}&body=${encodeURIComponent(text)}`;
      mail.textContent = "itsfutureshock@gmail.com";
      thanks.replaceChildren("We could not send that. Email ", mail, " and we'll fix it.");
    }
    button.hidden = true;
  }

  // Calendar: 13 pages (cover + 12 months), each its own preview the buyer approves; the cart line lists all 13
  // (pet_portrait.CALENDAR_PAGES). The design token and page picks persist per customer in localStorage.
  get calendar() {
    const box = this.dataset.kind === "calendar" && this.querySelector("[data-pet-calendar]");
    if (!box) return null;
    const key = `osf-pet-calendar:${this.tokenValue?.split(".")[0] || ""}`;
    if (this.calendarState?.key !== key) {
      let saved = {};
      try { saved = JSON.parse(localStorage.getItem(key) || "{}"); } catch { /* a bad entry starts a new design */ }
      this.calendarState = { key, token: saved.token || "", pages: saved.pages || {}, styles: saved.styles || {},
                             photos: saved.photos || {}, left: null, active: "front_cover", drawing: {} };
    }
    return Object.assign(this.calendarState, { box });
  }

  saveCalendar() {
    if (!this.tokenValue) return; // signed out the key has no customer part; the sign-in draft carries the picks
    const { key, token, pages, styles, photos } = this.calendar;
    localStorage.setItem(key, JSON.stringify({ token, pages, styles, photos }));
  }

  calendarPages() {
    return [...this.calendar.box.querySelectorAll("[data-pet-calendar-page]")].map((tile) => tile.dataset.petCalendarPage);
  }

  calendarRow(page) {
    const id = this.calendar?.pages[page];
    return id && this.previews?.find((p) => p.job_id === id && p.status === "preview");
  }

  calendarStyle(page) {
    const tile = this.calendar.box.querySelector(`[data-pet-calendar-page="${page}"]`);
    return this.calendar.styles[page] || this.calendarRow(page)?.style || tile?.dataset.style
      || this.querySelector('input[name="pet-style"]:checked')?.value;
  }

  // The photo a page uses: a made page's own preview, else the saved photo picked for it (none: the step 2 photo).
  calendarPhoto(page) {
    return this.calendarRow(page)?.job_id || this.calendar.photos[page] || null;
  }

  // A photo picked in step 2 belongs to the open page; a made page keeps its own until it is redone.
  calendarPhotoChanged() {
    const cal = this.calendar;
    if (!cal || this.calendarRow(cal.active)) return;
    const photo = this.pickedJob();
    if (photo) cal.photos[cal.active] = photo;
    else delete cal.photos[cal.active];
    this.saveCalendar();
    this.drawCalendar();
  }

  styleImage(style) {
    return this.radio("pet-style", style)?.dataset.example || "";
  }

  async calendarToken() {
    const cal = this.calendar;
    if (!cal.token) {
      try {
        const made = await this.api("/pet-calendar", { method: "POST" });
        Object.assign(cal, { token: made.calendar, left: made.previews, pages: {} });
        this.saveCalendar();
      } catch (err) {
        this.status.textContent = err.message;
      }
    }
    return cal.token;
  }

  pickCalendarPage(page) {
    const cal = this.calendar;
    cal.active = page;
    const row = this.calendarRow(page);
    if (row) this.choose(row);
    else this.radio("pet-style", this.calendarStyle(page))?.click();
    const photo = this.calendarPhoto(page);
    if (photo && photo !== this.pickedJob()) this.pickSaved(photo);
    // Loading the page's photo shows it on the style example; a made page shows its own preview again.
    if (row) Promise.resolve(this.mainLoading).then(() => cal.active === page && this.calendarRow(page) === row && this.choose(row));
    this.status.textContent = `${row ? "Showing" : "Next"}: ${this.calendarLabel(page)}. ${row ? "Redo it, or pick another page." : "Choose its style, then make it."}`;
    this.drawCalendar();
  }

  calendarLabel(page) {
    return this.calendar.box.querySelector(`[data-pet-calendar-page="${page}"] span`)?.textContent || page;
  }

  calendarStyleChanged(style) {
    this.calendar.styles[this.calendar.active] = style;
    this.saveCalendar();
    this.drawCalendar();
  }

  // A page preview of this design, picked or newly made, becomes its page's pick.
  calendarPicked(preview) {
    const cal = this.calendar;
    if (!cal || !cal.token || preview.calendar !== cal.token || preview.status !== "preview") return;
    cal.pages[preview.calendar_page] = preview.job_id;
    cal.active = preview.calendar_page;
    this.saveCalendar();
    this.drawCalendar();
  }

  calendarQueued(job, photoUrl, croppedUrl) {
    const cal = this.calendar;
    if (!cal || !job.calendar_page) return;
    cal.left = job.calendar_left;
    // makePreview polls the request's own job.
    for (const j of job.jobs || [job]) if (j.job_id === job.job_id) cal.drawing[j.calendar_page] = j.job_id;
    else this.calendarPoll(j, photoUrl, croppedUrl);
    this.drawCalendar();
  }

  calendarPoll({ job_id, calendar_page }, photoUrl, croppedUrl) {
    const cal = this.calendar;
    cal.drawing[calendar_page] = job_id;
    this.poll(job_id).then((done) => {
      if (done.status !== "preview") return;
      if (photoUrl) this.photos[done.job_id] = photoUrl;
      if (croppedUrl) this.cropped[done.job_id] = croppedUrl;
      this.previews = [done, ...this.previews.filter((p) => p.job_id !== done.job_id)];
      cal.pages[done.calendar_page] = done.job_id;
      this.saveCalendar();
      this.drawHistory(false);
    }).catch(() => {}).finally(() => this.calendarSettled(job_id));
  }

  calendarSettled(jobId) {
    const cal = this.calendar;
    if (!cal) return;
    for (const [page, id] of Object.entries(cal.drawing)) if (id === jobId) delete cal.drawing[page];
    this.drawCalendar();
  }

  calendarMissing() {
    return this.calendarPages().filter((page) => !this.calendarRow(page) && !this.calendar.drawing[page]);
  }

  // Every page not made yet: one request per photo, the step 2 photo's pages first.
  async makeCalendarPages() {
    const cal = this.calendar, current = this.pickedJob() || "", groups = new Map();
    for (const page of this.calendarMissing()) {
      const photo = cal.photos[page] || current;
      groups.set(photo, [...(groups.get(photo) || []), page]);
    }
    const order = [...groups].sort(([a]) => (a === current ? -1 : 0));
    for (const [i, [photo, pages]] of order.entries()) {
      cal.active = pages[0];
      if (photo !== current) {
        this.pickSaved(photo);
        await this.mainLoading;
        if (this.sourceJob !== photo) return;
      }
      // A Turnstile token is single use; the widget issues the next one shortly after the last request.
      const bot = this.querySelector('[name="cf-turnstile-response"]');
      for (let n = 0; i && bot && !bot.value && n < 50; n++) await sleep(200);
      await this.generate(Object.fromEntries(pages.map((page) => [page, this.calendarStyle(page)])));
      if (!this.calendarRow(pages[0]) && !cal.drawing[pages[0]]) return; // that request failed: stop here
    }
  }

  drawCalendar() {
    const cal = this.calendar;
    if (!cal) return this.drawCalendarHeading(); // switched in place to another format: back to the default heading
    // Pages of this design made elsewhere (another tab, before a reload) fill the pages still empty.
    for (const p of cal.token ? this.previews || [] : []) {
      if (p.calendar !== cal.token) continue;
      if (p.status === "preview" && !this.calendarRow(p.calendar_page)) cal.pages[p.calendar_page] = p.job_id;
      else if (["queued", "running"].includes(p.status) && !Object.values(cal.drawing).includes(p.job_id)
               && p.job_id !== this.pendingJob?.job_id) this.calendarPoll(p);
    }
    let ready = 0;
    for (const tile of cal.box.querySelectorAll("[data-pet-calendar-page]")) {
      const page = tile.dataset.petCalendarPage, row = this.calendarRow(page);
      ready += row ? 1 : 0;
      tile.setAttribute("aria-pressed", String(page === cal.active));
      tile.toggleAttribute("data-ready", !!row);
      tile.querySelector("[data-pet-calendar-state]").textContent = cal.drawing[page] ? "Drawing\u2026" : row ? "Ready" : "Not made";
      const src = row ? this.thumbSrc(row) : this.styleImage(this.calendarStyle(page));
      const img = tile.querySelector("img");
      if (src && img.getAttribute("src") !== src) img.src = src;
      const photo = this.calendarPhoto(page), badge = tile.querySelector("[data-pet-calendar-photo]");
      if (photo && this.photos && !this.photos[photo] && !(cal.noPhoto ||= new Set()).has(photo))
        this.photoFor(photo).then(() => this.drawCalendar(), () => cal.noPhoto.add(photo));
      const photoSrc = photo ? this.photos?.[photo] : this.photoUrl;
      if (photoSrc && badge.getAttribute("src") !== photoSrc) badge.src = photoSrc;
      badge.hidden = !photoSrc;
    }
    this.drawCalendarHeading();
    const pages = this.calendarPages();
    const input = this.querySelector("[data-pet-calendar-pages]");
    input.value = ready === pages.length ? pages.map((page) => cal.pages[page]).join(",") : "";
    input.disabled = !input.value;
    const all = cal.box.querySelector("[data-pet-calendar-all]");
    all.disabled = this.busy || !this.calendarMissing().length;
    all.hidden = !("signedIn" in this.dataset); // signed out, the sign-in link is the next step
    all.textContent = `Make the remaining pages (${this.calendarMissing().length})`;
    cal.box.querySelector("[data-pet-calendar-left]").textContent = `${ready} of ${pages.length} pages ready.`
      + (cal.left == null ? "" : ` ${cal.left} previews left for this calendar, redos included.`);
  }

  // Step 3 names the page its style applies to: "Pick a style for the cover" or "Pick a style for January".
  drawCalendarHeading() {
    const headings = this.querySelectorAll("[data-pet-style-heading]");
    if (!headings.length) return; // design mode keeps its own heading
    const active = this.calendar?.active;
    const text = !active ? "Pick a style and make your preview"
      : `Pick a style for ${active === "front_cover" ? "the cover" : this.calendarLabel(active)}`;
    for (const heading of headings) heading.textContent = text;
    if (this.stepNames) this.stepNames[2] = text;
  }

  // Add to cart only with all 13 pages; the cover is the line's preview.
  calendarReady(event) {
    const pages = this.calendarPages(), missing = pages.filter((page) => !this.calendarRow(page));
    if (!missing.length) {
      const cover = this.calendarRow("front_cover");
      if (this.renderInput.value !== cover.job_id) this.choose(cover);
      return true;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    const message = `Make all ${pages.length} calendar pages first: ${missing.length} to go (${missing.map((page) => this.calendarLabel(page)).join(", ")}).`;
    this.status.textContent = message;
    const note = this.cards[3].querySelector("[data-pet-cart-note]");
    note.textContent = message;
    note.hidden = false;
    track("cart_blocked_calendar", { missing: missing.length });
    return false;
  }

  async poll(jobId) {
    const started = Date.now();
    let failures = 0, failingSince = 0;
    for (;;) {
      await pollPause(failures ? POLL_RETRY_MS[failures - 1] : POLL_MS);
      let job;
      try {
        job = await this.api(`/pet-preview/${jobId}`, { signal: AbortSignal.timeout(POLL_TIMEOUT_MS) });
      } catch (err) {
        const transient = !err.status || err.status >= 500 || [408, 429].includes(err.status);
        failingSince ||= Date.now();
        if (!transient) throw Object.assign(err, { jobGone: err.status === 404 });
        track("poll_retry", { status: err.status || 0, failures: failures + 1 });
        if (++failures >= POLL_RETRY_MS.length || Date.now() - failingSince > POLL_GIVE_UP_MS)
          throw new Error("We lost touch with the preview service, but your preview is still being made. Press Resume my preview to check again.");
        this.status.textContent = "Reconnecting. Your preview is still being made.";
        continue;
      }
      failures = failingSince = 0;
      if (!["queued", "running"].includes(job.status)) return job;
      if (Date.now() - started > SLOW_MS)
        this.status.textContent = "Still working. You can leave and come back; it will be in your previews.";
    }
  }
}

customElements.define("osf-pet-portrait", OsfPetPortrait);

// Jigsaw cut outline for the preview: one tab per inner edge, direction and shape seeded so a size always looks the same.
function drawPuzzleCuts(host, cols, rows, aspect) {
  if (!host || host.dataset.cuts === `${cols}x${rows}`) return;
  host.dataset.cuts = `${cols}x${rows}`;
  let seed = cols * 7919 + rows;
  const rand = () => ((seed = (seed * 16807) % 2147483647) / 2147483647);
  const w = 1000 / cols, h = 1000 / aspect / rows;
  // Tab profile in edge units: u along the edge, v toward the tab.
  const tab = [[0.3, 0, 0.4, 0, 0.38, 0.08], [0.24, 0.27, 0.76, 0.27, 0.62, 0.08], [0.6, 0, 0.7, 0, 1, 0]];
  const edge = (x, y, dx, dy, len) => {
    const flip = rand() < 0.5 ? -1 : 1, shift = (rand() - 0.5) * 0.08, size = 0.9 + rand() * 0.2;
    const pt = (u, v) => { u = u === 0 || u === 1 ? u : u + shift; v *= flip * size; return `${(x + (u * dx - v * dy) * len).toFixed(1)} ${(y + (u * dy + v * dx) * len).toFixed(1)}`; };
    return tab.map(([a, b, c, d, e, f]) => `C${pt(a, b)} ${pt(c, d)} ${pt(e, f)}`).join("");
  };
  let d = "";
  for (let r = 1; r < rows; r++) { d += `M0 ${(r * h).toFixed(1)}`; for (let c = 0; c < cols; c++) d += edge(c * w, r * h, 1, 0, w); }
  for (let c = 1; c < cols; c++) { d += `M${(c * w).toFixed(1)} 0`; for (let r = 0; r < rows; r++) d += edge(c * w, r * h, 0, 1, h); }
  host.innerHTML = `<svg viewBox="0 0 1000 ${(1000 / aspect).toFixed(1)}" preserveAspectRatio="none" aria-hidden="true"><path d="${d}" fill="none" stroke="rgb(0 0 0 / .28)" stroke-width="1.6" vector-effect="non-scaling-stroke"/><path d="${d}" fill="none" stroke="rgb(255 255 255 / .6)" stroke-width=".8" vector-effect="non-scaling-stroke"/></svg>`;
}

// Lifestyle views: thumbnails under the stage swap a product scene over the preview.
function lifestyleReset(root) {
  root.removeAttribute("data-lifestyle-open");
  root.querySelector("[data-pet-lifestyle-view]").hidden = true;
  const art = root.querySelector("[data-pet-lifestyle-art]");
  if (art) art.hidden = true;
  const note = root.querySelector("[data-pet-lifestyle-note]");
  if (note) note.hidden = true;
  for (const b of root.querySelectorAll("[data-pet-lifestyle-views] button"))
    b.setAttribute("aria-pressed", String(b.hasAttribute("data-pet-lifestyle-preview")));
}

function syncLifestyle(root, widget) {
  const views = root.querySelector("[data-pet-lifestyle-views]");
  if (!views) return;
  views.innerHTML = widget.querySelector("[data-pet-lifestyle-views]")?.innerHTML || "";
  const note = root.querySelector("[data-pet-lifestyle-note]");
  const next = widget.querySelector("[data-pet-lifestyle-note]");
  if (note && next) note.textContent = next.textContent;
  lifestyleReset(root);
  filterLifestyle(root);
}

// Knit photos show one print layout; keep only the ones that match the picked layout.
function filterLifestyle(root) {
  const views = root.querySelector("[data-pet-lifestyle-views]");
  if (!views) return;
  const layout = root.knitLayout?.() ?? "portrait";
  const { material, shape } = root.ornament || {};
  // Sock photos show the red Christmas knit, so any sock Design or Palette pick hides them all.
  const knitPick = root.dataset.kind === "socks" && root.knitTileKey(layout) !== layout;
  const design = root.dataset.kind === "socks" ? undefined : root.querySelector('input[name="properties[Design]"]:checked')?.value.toLowerCase();
  const palette = root.querySelector('input[name="properties[Palette]"]:checked')?.value;
  const puzzleSize = { 87920: "252", 87921: "520" }[root.puzzleSize?.value];
  let shown = 0;
  for (const b of views.querySelectorAll("[data-pet-lifestyle-src]")) {
    const { petLifestyleLayout: photoLayout, petLifestyleMaterial: photoMaterial, petLifestyleShape: photoShape } = b.dataset;
    // Ornament photos carry their material and shape in the alt text; unlabeled photos always show.
    b.hidden = knitPick || (photoLayout !== undefined && photoLayout !== layout) ||
      (material !== undefined && photoMaterial !== undefined && photoMaterial !== material) ||
      (shape !== undefined && photoShape !== undefined && photoShape !== shape) ||
      // Pattern products only show photos whose alt names the picked design.
      (design !== undefined && !b.dataset.petLifestyleAlt.toLowerCase().includes(design)) ||
      (b.dataset.petLifestylePalette !== undefined && b.dataset.petLifestylePalette !== palette) ||
      (b.dataset.petLifestyleSize !== undefined && b.dataset.petLifestyleSize !== puzzleSize);
    if (b.hidden && b.getAttribute("aria-pressed") === "true") lifestyleReset(root);
    if (!b.hidden) shown++;
  }
  views.hidden = shown === 0;
}

// The scene photo carries the bare pattern; the stage's preview art is multiplied onto the scene's fabric
// shading at the same print placement, so the scene shows the customer's pet.
function drawSceneArt(root, scene) {
  const art = root.querySelector("[data-pet-lifestyle-art]");
  const mockup = root.querySelector("[data-pet-mockup]");
  const goods = GOODS[root.dataset.kind], matrix = SCENES[root.dataset.kind];
  if (!art || !goods || !matrix || mockup.hidden || !mockup.width) return;
  const shade = new Image();
  shade.crossOrigin = "anonymous";
  shade.onload = () => {
    if (!scene.isConnected || scene.getAttribute("aria-pressed") !== "true") return;
    const [left, top, width] = goods.rect, [aw, ah] = goods.area;
    art.width = shade.naturalWidth;
    art.height = shade.naturalHeight;
    const place = new DOMMatrix([art.width, 0, 0, art.height, 0, 0]).multiply(new DOMMatrix(matrix))
      .multiply(new DOMMatrix([width / mockup.width, 0, 0, width * ah / aw / mockup.height, left, top]));
    const ctx = art.getContext("2d");
    const pet = (op) => { ctx.globalCompositeOperation = op; ctx.setTransform(place); ctx.drawImage(mockup, 0, 0); ctx.resetTransform(); };
    pet("source-over");
    ctx.globalCompositeOperation = "multiply";
    ctx.drawImage(shade, 0, 0);
    ctx.globalCompositeOperation = "destination-in";
    ctx.drawImage(shade, 0, 0); // fabric only
    pet("destination-in"); // pet only
    ctx.globalCompositeOperation = "source-over";
    art.hidden = false;
  };
  shade.src = art.dataset.petLifestyleShade;
}

// Puzzle scenes: the stage art, cropped to the picked size's shape, multiplied onto the blank puzzle's shading.
function drawPuzzleScene(root, scene) {
  const canvas = root.querySelector("[data-pet-lifestyle-art]");
  const art = root.querySelector("[data-pet-stage-art]");
  const { petLifestyleSize: size, petLifestyleScene: view } = scene.dataset;
  const spec = PUZZLE_SCENES[size]?.[view];
  if (!canvas || !spec || !art.complete || !art.naturalWidth) return;
  const shade = new Image();
  shade.crossOrigin = "anonymous";
  shade.onload = () => {
    if (!scene.isConnected || scene.getAttribute("aria-pressed") !== "true") return;
    const W = (canvas.width = shade.naturalWidth), H = (canvas.height = shade.naturalHeight);
    const [pw, ph] = PUZZLE_AREAS[size], iw = art.naturalWidth, ih = art.naturalHeight;
    const sw = Math.min(iw, ih * pw / ph), sh = sw * ph / pw; // center crop at the print's shape
    const ctx = canvas.getContext("2d");
    const draw = (t) => {
      ctx.setTransform(new DOMMatrix([W, 0, 0, H, 0, 0]).multiply(new DOMMatrix(t)));
      ctx.drawImage(art, (iw - sw) / 2, (ih - sh) / 2, sw, sh, 0, 0, 1, 1);
      ctx.resetTransform();
    };
    ctx.imageSmoothingQuality = "high";
    draw(spec.pieces);
    ctx.save();
    ctx.beginPath();
    spec.clip.forEach(([x, y], i) => (i ? ctx.lineTo : ctx.moveTo).call(ctx, x * W, y * H));
    ctx.clip();
    draw(spec.face);
    ctx.restore();
    ctx.globalCompositeOperation = "multiply";
    ctx.drawImage(shade, 0, 0);
    ctx.globalCompositeOperation = "destination-in";
    ctx.drawImage(shade, 0, 0); // printed surfaces only
    ctx.globalCompositeOperation = "source-over";
    canvas.hidden = false;
  };
  shade.src = scene.dataset.petLifestyleShade;
}

// Warm the scenes on show once the stage is drawn so the first click paints the finished photo; fetched
// alongside the stage's tiles and maps, the 1600 px photos starved them on slow networks.
function preloadLifestyle(root) {
  const views = root.querySelector("[data-pet-lifestyle-views]");
  if (!views || root.querySelector("[data-pet-stage]").dataset.renderState !== "ready") return;
  for (const b of views.querySelectorAll("[data-pet-lifestyle-src]:not([hidden]):not([data-warm])")) {
    b.dataset.warm = "";
    new Image().src = b.dataset.petLifestyleSrc;
  }
}

function initLifestyle() {
  for (const root of document.querySelectorAll("osf-pet-portrait")) {
    const views = root.querySelector("[data-pet-lifestyle-views]");
    if (!views) continue;
    filterLifestyle(root);
  }
}

document.addEventListener("click", (event) => {
  const button = event.target.closest("[data-pet-lifestyle-views] button");
  if (!button) return;
  const root = button.closest("osf-pet-portrait");
  if (button.hasAttribute("data-pet-lifestyle-preview")) return lifestyleReset(root);
  lifestyleReset(root);
  const img = root.querySelector("[data-pet-lifestyle-img]");
  img.src = button.dataset.petLifestyleBlank || button.dataset.petLifestyleSrc;
  img.alt = button.dataset.petLifestyleAlt;
  root.querySelector("[data-pet-lifestyle-view]").hidden = false;
  root.querySelector("[data-pet-lifestyle-note]").hidden = false;
  root.setAttribute("data-lifestyle-open", "");
  button.setAttribute("aria-pressed", "true");
  root.querySelector("[data-pet-lifestyle-views] [data-pet-lifestyle-preview]").setAttribute("aria-pressed", "false");
  // Printify gallery photos already show the example pet; puzzle scenes and bare-pattern scenes get the preview drawn on.
  if (button.dataset.petLifestyleScene) drawPuzzleScene(root, button);
  else if (!button.hasAttribute("data-pet-lifestyle-gallery")) drawSceneArt(root, button);
});

// A freshly drawn preview replaces the scene so the customer sees their result.
document.addEventListener("DOMContentLoaded", () => {
  initLifestyle();
  for (const stage of document.querySelectorAll("[data-pet-stage]")) {
    let had = stage.classList.contains("has-mockup");
    new MutationObserver(() => {
      const has = stage.classList.contains("has-mockup");
      if (has && !had) lifestyleReset(stage.closest("osf-pet-portrait"));
      had = has;
    }).observe(stage, { attributes: true, attributeFilter: ["class"] });
  }
});
if (document.readyState !== "loading") initLifestyle();
